/**
 * CharacterSheetEditor - Authentic D&D Character Sheet Editor
 *
 * Designed to look and feel like a real D&D character sheet with:
 * - Parchment texture and fantasy theming
 * - Traditional character sheet layout
 * - Proper ability score boxes
 * - D&D-themed styling throughout
 *
 * @date_of_change 2025-09-18 - Created authentic character sheet editor
 */

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import SmartPasteInterface from './SmartPasteInterface/SmartPasteInterface';
import ImageBrowser from './ImageBrowser/ImageBrowser';
import ImageUploadService from '../services/ImageUploadService';
import { getAssetUsageSnapshot } from '../services/EntitlementService';
import { resolveCharacterArtUrl, resolvePlaceholderUrl } from '../services/ArtworkResolver';
import { characterTemplates } from '../services/DataService';
import { CharacterDataService } from '../services/CharacterDataService';
import UnifiedDataManager from '../services/UnifiedDataManager';
import {
  PREMIUM_PLAN_NAME,
  UPGRADE_ROUTE,
  getTierConfig,
  isPremiumTier
} from '../config/entitlements';
import IntelligentContentParser from '../utils/IntelligentContentParser';
import { buildSmartPasteCharacterImport } from '../utils/SmartPasteCharacterImport';
import { resolveRuntimeTier } from '../utils/runtimeTier';
import { buildAuthRoute, buildLocationRedirectTarget } from '../utils/authRedirect';
import {
  assessCharacterCombatReadiness,
  buildCombatReadinessMeta,
  hydrateCharacterCombatState,
  summarizeCombatReadinessIssues
} from '../utils/CharacterCombatReadiness';
import {
  mergeSmartPasteEntryList,
  mergeSmartPasteEquipment,
  stampSmartPasteEquipment
} from '../utils/SmartPasteEntryMerge';
import {
  buildCharacterWeaponTableEntries
} from '../utils/CharacterSheetWeaponPresentation';
import { isAmmunitionEntry, isExplicitlyEquippedWeapon } from '../utils/EquipmentState';
import {
  buildCharacterProficiencySummary,
  buildCharacterWealthSummary,
  hasDistinctStructuredNotes
} from '../utils/CharacterSheetReferencePresentation';
import {
  inferCharacterCategory,
  resolveCharacterCategory,
  writeCharacterCategory
} from '../utils/dashboardVaultState';
import CharacterActionEconomyPanel from './CharacterActionEconomyPanel';
import CharacterActionEconomyEditor from './CharacterActionEconomyEditor';
import CharacterSpellbookPanel from './CharacterSpellbookPanel';
import CharacterSpellbookEditor from './CharacterSpellbookEditor';
import CharacterClassProgressionEditor, {
  normalizeCharacterClassProgression
} from './CharacterClassProgressionEditor';
import CharacterInventorySection from './CharacterInventorySection';
import { buildCharacterActionEconomyViewModel } from '../utils/CharacterActionEconomyPresentation';
import { buildCharacterPrintModel } from '../utils/CharacterPrintPresentation';
import {
  buildCharacterReferencePresentation,
  resolveCharacterArmorClass,
  resolveCharacterChoiceSummary,
  resolveCharacterDefensesAndSenses,
  resolveCharacterHitDice
} from '../utils/CharacterRulesPresentation';
import { enrichSpellActionData } from '../utils/combat/SpellActionReference';
import {
  CharacterPrintPortal,
  waitForCharacterPrintAssets,
  waitForCharacterPrintPaint
} from './character-print/CharacterPrintDocument';
import { generateStarterCharacter } from '../generation/character-generator/CharacterGenerator';
import { SKILL_ABILITY_MAP, STARTER_CLASS_OPTIONS } from '../generation/character-generator/generatorData';
import './CharacterSheetStyles.css';

const SPELLCASTING_CLASS_NAMES = new Set([
  'artificer',
  'bard',
  'cleric',
  'druid',
  'paladin',
  'ranger',
  'sorcerer',
  'warlock',
  'wizard'
]);

const EditableTextList = ({ label, items, placeholder, onChange }) => {
  const values = Array.isArray(items) ? items : [];
  const visibleValues = values.length > 0 ? values : [''];
  const updateValue = (index, value) => {
    const next = [...visibleValues];
    next[index] = value;
    onChange(next);
  };
  const removeValue = (index) => onChange(visibleValues.filter((_, itemIndex) => itemIndex !== index));
  const addValue = () => onChange([...values, '']);

  return (
    <div className="character-editable-text-list" aria-label={label}>
      {visibleValues.map((value, index) => (
        <div className="character-editable-text-list__row" key={`${label}-${index}`}>
          <input
            type="text"
            value={value}
            placeholder={index === 0 ? placeholder : `${label.replace(/s$/, '')} ${index + 1}`}
            aria-label={`${label.replace(/s$/, '')} ${index + 1}`}
            onChange={(event) => updateValue(index, event.target.value)}
          />
          <button type="button" aria-label={`Remove ${label.replace(/s$/, '')} ${index + 1}`} onClick={() => removeValue(index)}>
            Remove
          </button>
        </div>
      ))}
      <button type="button" className="character-editable-text-list__add" onClick={addValue}>Add {label.replace(/s$/, '')}</button>
    </div>
  );
};

export const characterHasSpellcastingIdentity = (character) => {
  const normalizeLabel = (value) => String(value || '').trim().toLowerCase();
  const classNames = [
    character?.class,
    character?.subclass,
    character?.subclassName,
    character?.archetype,
    ...(Array.isArray(character?.classes)
      ? character.classes.flatMap((entry) => [entry?.name, entry?.className, entry?.class])
      : [])
  ]
    .map(normalizeLabel)
    .filter(Boolean);

  const hasSpellcastingClass = classNames.some((className) => SPELLCASTING_CLASS_NAMES.has(className));
  const hasSpellcastingSubclass = classNames.some((className) => ['arcane trickster', 'eldritch knight'].includes(className));
  if (hasSpellcastingClass || hasSpellcastingSubclass) {
    return true;
  }

  // @date_of_change: 2026-03-18
  // rule_reference: D&D 5e spell pages should stay available for canonical spellcasters even when a saved sheet has sparse spell lists; explicit spellcasting metadata also qualifies.
  return Boolean(
    character?.spellcasting
    || character?.spells
    || character?.preparedSpells
    || character?.data?.spellcasting
    || character?.data?.spells
    || character?.schemaData?.spellcasting
    || character?.schemaData?.spells
    || character?.customData?.spellcasting
    || character?.customData?.spells
    || character?.canonicalData?.spellcasting
    || character?.canonicalData?.spells
  );
};

const LEGACY_SPELL_LEVEL_KEYS = Object.freeze({
  cantrip: 0,
  cantrips: 0,
  level0: 0,
  level1: 1,
  level2: 2,
  level3: 3,
  level4: 4,
  level5: 5,
  level6: 6,
  level7: 7,
  level8: 8,
  level9: 9,
});

const isSpellRecord = (value) => Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const cloneSpellEntries = (list) => (
  Array.isArray(list)
    ? list.map((entry) => (isSpellRecord(entry) ? { ...entry } : entry))
    : []
);

const toLegacySpellReference = (entry) => {
  if (typeof entry === 'string') {
    return entry.trim();
  }

  if (!isSpellRecord(entry)) {
    return '';
  }

  return String(entry.name || entry.id || entry.title || entry.label || '').trim();
};

const toFiniteSpellNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

const normalizeSpellAbility = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  if (!normalized) return undefined;
  if (normalized === 'int') return 'intelligence';
  if (normalized === 'wis') return 'wisdom';
  if (normalized === 'cha') return 'charisma';
  return ['intelligence', 'wisdom', 'charisma'].includes(normalized) ? normalized : undefined;
};

const inferSpellcastingAbilityFromCharacter = (character) => {
  const labels = [];
  const collect = (value) => {
    if (!value) return;
    if (typeof value === 'string') {
      labels.push(value);
    } else if (Array.isArray(value)) {
      value.forEach(collect);
    } else if (typeof value === 'object') {
      labels.push(value.name, value.className, value.class, value.subclass, value.subclassName, value.archetype);
    }
  };

  [
    character,
    character?.data,
    character?.schemaData,
    character?.canonicalData,
    character?.customData,
    character?.characterData,
    character?.characterData?.data,
    character?.compat?.originalData
  ].forEach((source) => {
    collect(source?.class);
    collect(source?.classes);
    collect(source?.subclass);
    collect(source?.archetype);
  });

  const text = labels.filter(Boolean).join(' ').toLowerCase();
  if (/(arcane trickster|eldritch knight|wizard|artificer)/.test(text)) return 'intelligence';
  if (/(cleric|druid|ranger)/.test(text)) return 'wisdom';
  if (/(bard|paladin|sorcerer|warlock)/.test(text)) return 'charisma';
  return undefined;
};

const resolveCharacterAbilityScores = (character) => {
  const merged = {};
  [
    character?.abilities,
    character?.data?.abilities,
    character?.schemaData?.abilities,
    character?.canonicalData?.abilities,
    character?.customData?.abilities,
    character?.characterData?.abilities,
    character?.characterData?.data?.abilities,
    character?.compat?.originalData?.abilities
  ].forEach((source) => {
    if (source && typeof source === 'object' && !Array.isArray(source)) {
      Object.entries(source).forEach(([key, value]) => {
        const normalizedKey = normalizeSpellAbility(key) || key;
        const numeric = Number(value);
        if (Number.isFinite(numeric) && merged[normalizedKey] == null) {
          merged[normalizedKey] = numeric;
        }
      });
    }
  });
  return merged;
};

const resolveCharacterProficiencyBonus = (character) => {
  const candidates = [
    character?.proficiencyBonus,
    character?.data?.combatStats?.proficiencyBonus,
    character?.data?.proficiencyBonus,
    character?.schemaData?.proficiencyBonus,
    character?.canonicalData?.proficiencyBonus,
    character?.customData?.proficiencyBonus,
    character?.characterData?.proficiencyBonus,
    character?.characterData?.combatStats?.proficiencyBonus,
    character?.characterData?.data?.combatStats?.proficiencyBonus,
    character?.compat?.originalData?.proficiencyBonus
  ];
  for (const value of candidates) {
    const numeric = Number(value);
    if (Number.isFinite(numeric)) return numeric;
  }
  return null;
};

const parseSpellLevel = (value) => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value !== 'string') {
    return null;
  }

  const normalized = value.trim().toLowerCase();
  if (!normalized) {
    return null;
  }

  if (Object.prototype.hasOwnProperty.call(LEGACY_SPELL_LEVEL_KEYS, normalized)) {
    return LEGACY_SPELL_LEVEL_KEYS[normalized];
  }

  if (/^[0-9]+$/.test(normalized)) {
    return parseInt(normalized, 10);
  }

  const levelMatch = normalized.match(/^level\s*([0-9]+)$/);
  if (levelMatch) {
    return parseInt(levelMatch[1], 10);
  }

  return null;
};

const normalizeSpellSlots = (slotSource) => {
  if (!isSpellRecord(slotSource)) {
    return {};
  }

  return Object.entries(slotSource).reduce((acc, [levelKey, slotValue]) => {
    const level = parseSpellLevel(levelKey);
    if (level == null || level < 1 || level > 9) {
      return acc;
    }

    if (typeof slotValue === 'number' && Number.isFinite(slotValue) && slotValue >= 0) {
      acc[level] = { total: slotValue, used: 0 };
      return acc;
    }

    if (!isSpellRecord(slotValue)) {
      return acc;
    }

    const total = toFiniteSpellNumber(slotValue.total ?? slotValue.max);
    if (total == null || total < 0) {
      return acc;
    }

    const used = toFiniteSpellNumber(slotValue.used ?? slotValue.expended) ?? 0;
    acc[level] = {
      total,
      used: Math.max(0, Math.min(total, used))
    };
    return acc;
  }, {});
};

const hasCanonicalSpellEntries = (value) => {
  if (Array.isArray(value)) return value.some((entry) => String(toLegacySpellReference(entry)).trim());
  if (!isSpellRecord(value)) return false;
  if (Object.prototype.hasOwnProperty.call(value, 'entries') && Array.isArray(value.entries)) return true;
  return ['known', 'prepared', 'atWill']
    .some((key) => Array.isArray(value[key]) && value[key].some((entry) => String(toLegacySpellReference(entry)).trim()))
    || (isSpellRecord(value.byLevel)
      && Object.values(value.byLevel).some((entries) => Array.isArray(entries) && entries.length > 0));
};

const spellcastingMetadataOnly = (value) => {
  if (!isSpellRecord(value)) return null;
  return {
    ability: value.ability ?? value.spellcastingAbility ?? value.spellAbility,
    attackBonus: value.attackBonus ?? value.spellAttackBonus,
    saveDC: value.saveDC ?? value.saveDc ?? value.spellSaveDC,
    slots: value.slots ?? value.spellSlots,
    spellSlots: value.spellSlots ?? value.slots
  };
};

const getCharacterSpellContainers = (character) => {
  const containers = [];
  const seenBranches = new Set();

  const pushContainer = (value) => {
    if (Array.isArray(value) || isSpellRecord(value)) {
      containers.push(value);
    }
  };

  const branches = [
    character,
    character?.data,
    character?.schemaData,
    character?.canonicalData,
    character?.customData,
    character?.characterData,
    character?.characterData?.data,
    character?.characterData?.schemaData,
    character?.characterData?.canonicalData,
    character?.originalUnifiedEntity?.data,
    character?.compat?.originalData
  ];

  branches.forEach((branch) => {
    if (!branch || seenBranches.has(branch)) {
      return;
    }

    seenBranches.add(branch);
    const canonicalSpells = branch.spells;
    const canonicalHasEntries = hasCanonicalSpellEntries(canonicalSpells);
    pushContainer(canonicalSpells);
    pushContainer(canonicalHasEntries ? spellcastingMetadataOnly(branch.spellcasting) : branch.spellcasting);

    if (isSpellRecord(branch.spellcasting) && !canonicalHasEntries) {
      pushContainer(branch.spellcasting.spells);
    }

    if (Array.isArray(branch.preparedSpells)) {
      pushContainer({ prepared: branch.preparedSpells });
    }

    if (isSpellRecord(branch.spellsByLevel)) {
      pushContainer({ byLevel: branch.spellsByLevel });
    }
  });

  if (Array.isArray(character?.spells?.entries)) {
    pushContainer({ entries: character.spells.entries });
  }

  return containers;
};

const sanitizeSpellEntry = (spell) => {
  if (!isSpellRecord(spell)) {
    return spell;
  }

  const sanitized = { ...spell };
  delete sanitized.__known;
  delete sanitized.__atWill;
  return sanitized;
};

export const resolveCharacterSpellData = (character) => {
  const containers = getCharacterSpellContainers(character);
  const entriesByKey = new Map();
  const seenContainers = new Set();
  const mergedSlots = {};
  const spellcastingMeta = {
    ability: undefined,
    attackBonus: null,
    saveDC: null
  };

  const mergeSlots = (slotSource) => {
    const normalized = normalizeSpellSlots(slotSource);
    Object.entries(normalized).forEach(([level, slotData]) => {
      if (!mergedSlots[level]) {
        mergedSlots[level] = slotData;
      }
    });
  };

  const mergeSpellEntry = (spell, options = {}) => {
    if (!spell) {
      return;
    }

    const {
      fallbackLevel = null,
      prepared = false,
      known = false,
      atWill = false,
      sourceTag = 'misc'
    } = options;

    const base = isSpellRecord(spell) ? { ...spell } : { name: spell };
    if (!base.name) {
      base.name = base.title || base.id || base.label || (typeof spell === 'string' ? spell : '');
    }

    if (!String(base.name || '').trim()) {
      return;
    }

    const normalizedName = String(base.name).trim();
    const normalizedLevel = parseSpellLevel(base.level ?? base.spellLevel ?? base.circle ?? fallbackLevel);
    const entryKey = `${normalizedName.toLowerCase().replace(/\s+/g, ' ')}|${normalizedLevel ?? 'na'}`;
    const existing = entriesByKey.get(entryKey);

    if (!base.castingTime && typeof base.castTime === 'string') {
      base.castingTime = base.castTime;
    }

    if (!base.range && typeof base.spellRange === 'string') {
      base.range = base.spellRange;
    }

    if (typeof base.components === 'string' && !base.material) {
      const materialMatch = base.components.match(/M\s*\(([^)]+)\)/i);
      if (materialMatch) {
        base.material = materialMatch[1];
      }
    }

    const preparedSource = base.prepared ?? base.isPrepared ?? base.preparedSpell ?? base.isPreparedSpell;
    const ritualFromComponents = typeof base.components === 'string' && base.components.toUpperCase().includes('RITUAL');
    const concentrationFromDuration = typeof base.duration === 'string' && base.duration.toLowerCase().includes('concentration');

    entriesByKey.set(entryKey, {
      ...(existing || {}),
      ...base,
      name: normalizedName,
      ...(normalizedLevel !== null ? { level: normalizedLevel } : {}),
      prepared: Boolean((existing && existing.prepared) || prepared || preparedSource === true),
      __known: Boolean(
        (existing && existing.__known)
        || known
        || prepared
        || atWill
        || sourceTag.includes('known')
        || sourceTag.includes('spelllist')
        || sourceTag.includes('bylevel')
        || sourceTag.includes('spells')
        || sourceTag.includes('resolved')
      ),
      __atWill: Boolean((existing && existing.__atWill) || atWill),
      ritual: typeof base.ritual === 'boolean'
        ? base.ritual
        : Boolean((existing && existing.ritual) || ritualFromComponents || (Array.isArray(base.tags) && base.tags.includes('ritual'))),
      concentration: typeof base.concentration === 'boolean'
        ? base.concentration
        : Boolean((existing && existing.concentration) || concentrationFromDuration)
    });
  };

  const mergeSpellArray = (list, options = {}) => {
    if (!Array.isArray(list)) {
      return;
    }
    list.forEach((entry) => mergeSpellEntry(entry, options));
  };

  const mergeByLevelContainer = (container, sourceTag = 'byLevel') => {
    if (!isSpellRecord(container)) {
      return;
    }

    Object.entries(container).forEach(([levelKey, spellList]) => {
      const level = parseSpellLevel(levelKey);
      mergeSpellArray(spellList, {
        fallbackLevel: level,
        known: true,
        atWill: level === 0,
        sourceTag: `${sourceTag}-${String(levelKey).toLowerCase()}`
      });
    });
  };

  const mergeLegacyLevelArrays = (container, sourceTag = 'legacy') => {
    if (!isSpellRecord(container)) {
      return;
    }

    Object.entries(LEGACY_SPELL_LEVEL_KEYS).forEach(([legacyKey, level]) => {
      if (Array.isArray(container[legacyKey])) {
        mergeSpellArray(container[legacyKey], {
          fallbackLevel: level,
          known: true,
          atWill: level === 0,
          sourceTag: `${sourceTag}-${legacyKey}`
        });
      }
    });
  };

  const ingestContainer = (container, sourceTag = 'source') => {
    if (!container) {
      return;
    }

    if (Array.isArray(container)) {
      mergeSpellArray(container, { known: true, sourceTag });
      return;
    }

    if (!isSpellRecord(container) || seenContainers.has(container)) {
      return;
    }

    seenContainers.add(container);

    if (!spellcastingMeta.ability) {
      spellcastingMeta.ability = normalizeSpellAbility(
        container.ability
        ?? container.spellcastingAbility
        ?? container.spellAbility
      );
    }

    if (spellcastingMeta.attackBonus == null) {
      spellcastingMeta.attackBonus = toFiniteSpellNumber(
        container.attackBonus
        ?? container.spellAttackBonus
      );
    }

    if (spellcastingMeta.saveDC == null) {
      spellcastingMeta.saveDC = toFiniteSpellNumber(
        container.saveDC
        ?? container.saveDc
        ?? container.spellSaveDC
      );
    }

    mergeSlots(container.slots ?? container.spellSlots);
    mergeSpellArray(container.prepared, { prepared: true, known: true, sourceTag: `${sourceTag}-prepared` });
    mergeSpellArray(container.known, { known: true, sourceTag: `${sourceTag}-known` });
    mergeSpellArray(container.spellsKnown, { known: true, sourceTag: `${sourceTag}-spellsknown` });
    mergeSpellArray(container.atWill, { known: true, atWill: true, sourceTag: `${sourceTag}-atwill` });
    mergeSpellArray(container.spellList, { known: true, sourceTag: `${sourceTag}-spelllist` });
    mergeSpellArray(container.resolved, { known: true, sourceTag: `${sourceTag}-resolved` });

    if (Array.isArray(container.spells)) {
      mergeSpellArray(container.spells, { known: true, sourceTag: `${sourceTag}-spells` });
    }

    mergeByLevelContainer(container.byLevel, `${sourceTag}-bylevel`);
    mergeByLevelContainer(container.spellsByLevel, `${sourceTag}-spellsbylevel`);
    mergeLegacyLevelArrays(container, sourceTag);
    // Editor entries are applied last so explicit sheet edits win over compatibility copies.
    mergeSpellArray(container.entries, { known: true, sourceTag: `${sourceTag}-entries` });

    if (isSpellRecord(container.spells) && container.spells !== container) {
      ingestContainer(container.spells, `${sourceTag}-nested-spells`);
    }
  };

  containers.forEach((container, index) => {
    ingestContainer(container, `source-${index}`);
  });

  if (!spellcastingMeta.ability) {
    spellcastingMeta.ability = inferSpellcastingAbilityFromCharacter(character);
  }
  if (spellcastingMeta.ability) {
    const scores = resolveCharacterAbilityScores(character);
    const score = toFiniteSpellNumber(scores?.[spellcastingMeta.ability]);
    const proficiency = resolveCharacterProficiencyBonus(character);
    if (score != null && proficiency != null) {
      const modifier = Math.floor((score - 10) / 2);
      if (spellcastingMeta.attackBonus == null) spellcastingMeta.attackBonus = modifier + proficiency;
      if (spellcastingMeta.saveDC == null) spellcastingMeta.saveDC = 8 + modifier + proficiency;
    }
  }

  const orderedEntries = Array.from(entriesByKey.values()).sort((left, right) => {
    const leftLevel = parseSpellLevel(left.level);
    const rightLevel = parseSpellLevel(right.level);

    if (leftLevel !== rightLevel) {
      if (leftLevel == null) return 1;
      if (rightLevel == null) return -1;
      return leftLevel - rightLevel;
    }

    return String(left.name || '').localeCompare(String(right.name || ''));
  });

  const enrichedEntries = orderedEntries.map((entry) => enrichSpellActionData(entry, { subject: character }));
  const entries = enrichedEntries.map(sanitizeSpellEntry);
  const prepared = enrichedEntries.filter((entry) => entry.prepared).map(sanitizeSpellEntry);
  const known = enrichedEntries
    .filter((entry) => entry.__known || entry.prepared || entry.__atWill)
    .map(sanitizeSpellEntry);
  const atWill = enrichedEntries.filter((entry) => entry.__atWill).map(sanitizeSpellEntry);
  const byLevel = {};

  enrichedEntries.forEach((entry) => {
    const level = parseSpellLevel(entry.level);
    if (level == null) {
      return;
    }

    if (!byLevel[level]) {
      byLevel[level] = [];
    }

    byLevel[level].push(sanitizeSpellEntry(entry));
  });

  return {
    entries,
    prepared,
    known: known.length > 0 ? known : entries,
    atWill,
    byLevel,
    slots: mergedSlots,
    ability: spellcastingMeta.ability,
    attackBonus: spellcastingMeta.attackBonus,
    saveDC: spellcastingMeta.saveDC
  };
};

export const buildCanonicalCharacterSpells = (character) => {
  const resolved = resolveCharacterSpellData(character);
  const spells = {};

  if (resolved.entries.length > 0) {
    spells.entries = cloneSpellEntries(resolved.entries);
  }

  if (resolved.ability) {
    spells.ability = resolved.ability;
  }
  if (resolved.attackBonus != null) {
    spells.attackBonus = resolved.attackBonus;
  }
  if (resolved.saveDC != null) {
    spells.saveDC = resolved.saveDC;
  }
  if (resolved.known.length > 0) {
    spells.known = cloneSpellEntries(resolved.known);
  }
  if (resolved.prepared.length > 0) {
    spells.prepared = cloneSpellEntries(resolved.prepared);
  }
  if (resolved.atWill.length > 0) {
    spells.atWill = cloneSpellEntries(resolved.atWill);
  }
  if (Object.keys(resolved.byLevel).length > 0) {
    spells.byLevel = Object.fromEntries(
      Object.entries(resolved.byLevel).map(([level, entries]) => [level, cloneSpellEntries(entries)])
    );
  }
  if (Object.keys(resolved.slots).length > 0) {
    spells.slots = { ...resolved.slots };
  }

  return spells;
};

export const buildEditedCharacterSpells = (previousSpells, sourceEntries) => {
  const baseline = isSpellRecord(previousSpells) ? previousSpells : {};
  const entries = (Array.isArray(sourceEntries) ? sourceEntries : [])
    .map((entry) => (isSpellRecord(entry) ? { ...entry } : { name: String(entry || '') }))
    .map((entry) => ({
      ...entry,
      name: String(entry.name || '').trim(),
      level: Math.max(0, parseSpellLevel(entry.level) ?? 0),
      prepared: Boolean(entry.prepared),
      ritual: Boolean(entry.ritual),
      concentration: Boolean(entry.concentration)
    }));
  const byLevel = {};
  entries.forEach((entry) => {
    byLevel[entry.level] = byLevel[entry.level] || [];
    byLevel[entry.level].push({ ...entry });
  });
  const next = {
    entries: cloneSpellEntries(entries),
    known: cloneSpellEntries(entries),
    prepared: cloneSpellEntries(entries.filter((entry) => entry.prepared)),
    atWill: cloneSpellEntries(entries.filter((entry) => entry.level === 0)),
    byLevel,
    slots: { ...normalizeSpellSlots(baseline.slots ?? baseline.spellSlots) }
  };
  const ability = normalizeSpellAbility(baseline.ability ?? baseline.spellcastingAbility);
  const attackBonus = toFiniteSpellNumber(baseline.attackBonus ?? baseline.spellAttackBonus);
  const saveDC = toFiniteSpellNumber(baseline.saveDC ?? baseline.saveDc ?? baseline.spellSaveDC);
  if (ability) next.ability = ability;
  if (attackBonus != null) next.attackBonus = attackBonus;
  if (saveDC != null) next.saveDC = saveDC;
  return next;
};

const parseSpeedNumber = (value) => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string') {
    const match = value.match(/-?\d+/);
    if (match) {
      const parsed = Number.parseInt(match[0], 10);
      return Number.isFinite(parsed) ? parsed : null;
    }
  }

  return null;
};

export const buildSchemaCompatibleCharacterSpeed = (speed, fallback = 30) => {
  if (speed && typeof speed === 'object' && !Array.isArray(speed)) {
    const normalizedModes = {};
    Object.entries(speed).forEach(([mode, value]) => {
      const numeric = parseSpeedNumber(value);
      if (numeric !== null) {
        normalizedModes[mode] = numeric;
      }
    });

    if (Object.keys(normalizedModes).length > 0) {
      if (normalizedModes.walk == null) {
        const firstNumeric = Object.values(normalizedModes).find((value) => Number.isFinite(value));
        normalizedModes.walk = Number.isFinite(firstNumeric) ? firstNumeric : fallback;
      }
      return normalizedModes;
    }
  }

  if (typeof speed === 'string' && speed.trim()) {
    const normalizedModes = {};
    const lower = speed.toLowerCase();
    ['walk', 'fly', 'swim', 'climb', 'burrow'].forEach((mode) => {
      const modeMatch = lower.match(new RegExp(`${mode}\\s*(?:speed\\s*)?(\\d+)`, 'i'));
      if (modeMatch) {
        normalizedModes[mode] = Number.parseInt(modeMatch[1], 10);
      }
    });

    const defaultNumeric = parseSpeedNumber(speed);
    if (defaultNumeric !== null) {
      normalizedModes.walk = normalizedModes.walk ?? defaultNumeric;
    }

    if (Object.keys(normalizedModes).length > 0) {
      return normalizedModes;
    }
  }

  const numeric = parseSpeedNumber(speed);
  return { walk: numeric !== null ? numeric : fallback };
};

export const sanitizeSchemaSpellcastingPayload = (spellcasting) => {
  if (!isSpellRecord(spellcasting)) {
    return undefined;
  }

  const sanitized = { ...spellcasting };
  const normalizedAbility = normalizeSpellAbility(sanitized.ability || sanitized.spellcastingAbility);
  if (normalizedAbility) {
    sanitized.ability = normalizedAbility;
    sanitized.spellcastingAbility = normalizedAbility;
  }

  if (typeof sanitized.concentration === 'string' && sanitized.concentration.trim()) {
    sanitized.concentration = sanitized.concentration.trim();
  } else {
    delete sanitized.concentration;
  }

  return sanitized;
};

const sanitizeSchemaFeatureEntries = (entries) => (Array.isArray(entries) ? entries : []).map((entry) => {
  if (typeof entry === 'string') {
    return { name: entry, description: '' };
  }

  const sanitized = { ...(entry || {}) };
  if (sanitized.source && typeof sanitized.source !== 'string') {
    sanitized.source = String(sanitized.source.name || sanitized.source.title || '').trim() || undefined;
  }
  if (sanitized.uses && typeof sanitized.uses === 'object') {
    sanitized.uses = { ...sanitized.uses };
    if (!['short rest', 'long rest'].includes(sanitized.uses.resetOn)) {
      delete sanitized.uses.resetOn;
    }
  }
  return sanitized;
});

const sanitizeSchemaEquipmentPayload = (equipment) => {
  const source = (equipment && typeof equipment === 'object' && !Array.isArray(equipment)) ? equipment : {};
  const sanitizeEntry = (entry, kind) => {
    const sanitized = typeof entry === 'string' ? { name: entry } : { ...(entry || {}) };
    if (kind === 'weapon') {
      const attackBonus = Number(sanitized.attackBonus);
      if (Number.isInteger(attackBonus)) sanitized.attackBonus = attackBonus;
      else delete sanitized.attackBonus;
    }
    if (kind === 'armor') {
      const armorClass = Number(sanitized.armorClass ?? sanitized.ac?.value ?? sanitized.ac);
      if (Number.isInteger(armorClass)) sanitized.armorClass = armorClass;
      else delete sanitized.armorClass;
    }
    if (kind === 'item') {
      const quantity = Number(sanitized.quantity ?? sanitized.qty);
      if (Number.isInteger(quantity) && quantity >= 0) sanitized.quantity = quantity;
      else delete sanitized.quantity;
    }
    if (sanitized.properties != null && !Array.isArray(sanitized.properties)) {
      delete sanitized.properties;
    }
    return sanitized;
  };
  const currency = (source.currency && typeof source.currency === 'object' && !Array.isArray(source.currency))
    ? Object.fromEntries(Object.entries(source.currency)
      .map(([coin, value]) => [coin, Number(value)])
      .filter(([, value]) => Number.isInteger(value) && value >= 0))
    : undefined;

  return {
    ...source,
    weapons: (Array.isArray(source.weapons) ? source.weapons : []).map((entry) => sanitizeEntry(entry, 'weapon')),
    armor: (Array.isArray(source.armor) ? source.armor : []).map((entry) => sanitizeEntry(entry, 'armor')),
    items: (Array.isArray(source.items) ? source.items : []).map((entry) => sanitizeEntry(entry, 'item')),
    ...(currency ? { currency } : {})
  };
};

const sanitizeSchemaProficiencies = (proficiencies, character) => {
  if (!proficiencies || typeof proficiencies !== 'object' || Array.isArray(proficiencies)) {
    return undefined;
  }
  const toList = (value) => {
    if (Array.isArray(value)) return value.map((entry) => String(entry)).filter(Boolean);
    if (value && typeof value === 'object') {
      return Object.entries(value).filter(([, enabled]) => Boolean(enabled)).map(([name]) => name);
    }
    return [];
  };
  return {
    armor: toList(proficiencies.armor),
    weapons: toList(proficiencies.weapons),
    tools: toList(proficiencies.tools ?? character?.toolProficiencies),
    languages: toList(proficiencies.languages ?? character?.languages)
  };
};

export const buildCharacterSpellcastingCompat = (character) => {
  const resolved = resolveCharacterSpellData(character);
  const directSpellcasting = isSpellRecord(character?.spellcasting)
    ? character.spellcasting
    : (isSpellRecord(character?.data?.spellcasting) ? character.data.spellcasting : {});
  const directSpells = isSpellRecord(character?.spells) ? character.spells : {};
  const flatSpellArray = Array.isArray(character?.spells) ? character.spells : null;
  const compat = { ...directSpellcasting };
  const canonicalListDeclared = Object.prototype.hasOwnProperty.call(directSpells, 'entries') && Array.isArray(directSpells.entries);

  // date_of_change: 2026-04-07
  // rule_reference: Character editor spell-slot compatibility must preserve and merge canonical slots from direct spellcasting payloads and resolved nested spell data.
  compat.slots = { ...normalizeSpellSlots(compat.slots ?? compat.spellSlots) };
  compat.spellSlots = { ...normalizeSpellSlots(compat.spellSlots ?? compat.slots) };

  const pickSpellArray = (...candidates) => {
    let firstNormalized = null;
    for (const candidate of candidates) {
      if (Array.isArray(candidate) && candidate.length > 0) {
        const normalized = candidate.map(toLegacySpellReference).filter(Boolean);
        if (!firstNormalized) {
          firstNormalized = normalized;
        }
        if (normalized.length > 0) {
          return normalized;
        }
      }
    }
    return firstNormalized || [];
  };

  const mergeSlots = (slotSource) => {
    const normalized = normalizeSpellSlots(slotSource);
    Object.entries(normalized).forEach(([level, slotData]) => {
      if (!compat.slots[level]) {
        compat.slots[level] = slotData;
      }
      if (!compat.spellSlots[level]) {
        compat.spellSlots[level] = slotData;
      }
    });
  };

  mergeSlots(directSpellcasting.slots ?? directSpellcasting.spellSlots);
  mergeSlots(directSpells.slots ?? directSpells.spellSlots);
  mergeSlots(resolved.slots);

  let prepared = pickSpellArray(
    directSpells.prepared,
    resolved.prepared,
    directSpellcasting.prepared
  );
  let spellsKnown = pickSpellArray(
    directSpells.known,
    resolved.known,
    directSpellcasting.spellsKnown,
    directSpells.spellList,
    directSpells.spells,
    flatSpellArray
  );
  let spellList = pickSpellArray(
    directSpells.entries,
    resolved.entries,
    directSpells.spellList,
    directSpellcasting.spellList,
    directSpells.spells,
    flatSpellArray
  );
  if (canonicalListDeclared) {
    prepared = (Array.isArray(directSpells.prepared) ? directSpells.prepared : []).map(toLegacySpellReference).filter(Boolean);
    spellsKnown = (Array.isArray(directSpells.known) ? directSpells.known : []).map(toLegacySpellReference).filter(Boolean);
    spellList = directSpells.entries.map(toLegacySpellReference).filter(Boolean);
  }

  if (resolved.ability) {
    compat.ability = compat.ability || resolved.ability;
    compat.spellcastingAbility = compat.spellcastingAbility || resolved.ability;
  }

  if (resolved.attackBonus != null) {
    compat.attackBonus = compat.attackBonus ?? resolved.attackBonus;
    compat.spellAttackBonus = compat.spellAttackBonus ?? resolved.attackBonus;
  }

  if (resolved.saveDC != null) {
    compat.saveDC = compat.saveDC ?? resolved.saveDC;
    compat.spellSaveDC = compat.spellSaveDC ?? resolved.saveDC;
  }

  if (prepared.length > 0 || canonicalListDeclared) {
    compat.prepared = prepared;
  }
  if (spellsKnown.length > 0 || canonicalListDeclared) {
    compat.spellsKnown = spellsKnown;
  }
  if (spellList.length > 0 || canonicalListDeclared) {
    compat.spellList = spellList;
  }

  if (Object.keys(compat.slots).length === 0) {
    delete compat.slots;
  }
  if (Object.keys(compat.spellSlots).length === 0) {
    delete compat.spellSlots;
  }

  return sanitizeSchemaSpellcastingPayload(compat) || {};
};

const resolveTemplateKey = (templateId) => {
  const normalizedTemplateId = String(templateId || '').trim().toLowerCase();
  if (!normalizedTemplateId || !characterTemplates || typeof characterTemplates !== 'object') {
    return null;
  }

  const templateKeys = Object.keys(characterTemplates);
  return templateKeys.find((key) => {
    const normalizedKey = String(key || '').trim().toLowerCase();
    const registeredId = String(characterTemplates[key]?.id || '').trim().toLowerCase();
    return normalizedTemplateId === normalizedKey
      || normalizedTemplateId === registeredId
      || normalizedTemplateId.startsWith(`${normalizedKey}-`);
  }) || null;
};

const createBlankCharacter = () => hydrateCharacterCombatState({
  id: 'new_character_draft',
  type: 'character',
  name: '',
  class: '',
  race: '',
  species: '',
  subrace: '',
  background: '',
  alignment: '',
  level: 1,
  hp: 10,
  maxHp: 10,
  tempHp: 0,
  ac: 10,
  initiative: 0,
  speed: 30,
  proficiencyBonus: 2,
  abilities: {
    strength: 10,
    dexterity: 10,
    constitution: 10,
    intelligence: 10,
    wisdom: 10,
    charisma: 10
  },
  biography: {
    description: '',
    backstory: '',
    personalityTraits: '',
    ideals: '',
    bonds: '',
    flaws: ''
  },
  features: [],
  feats: [],
  traits: [],
  actions: [],
  skills: {},
  savingThrows: {},
  languages: [],
  tags: [],
  spells: {},
  equipment: {
    weapons: [],
    armor: [],
    other: [],
    items: []
  }
});

const normalizeSavingThrowProficiencies = (savingThrows, abilities) => {
  const source = savingThrows && typeof savingThrows === 'object' && !Array.isArray(savingThrows)
    ? savingThrows
    : {};
  return Object.fromEntries([
    'strength', 'dexterity', 'constitution', 'intelligence', 'wisdom', 'charisma'
  ].map((ability) => {
    const value = source[ability];
    if (typeof value === 'boolean') return [ability, value];
    const numeric = Number(value);
    const score = Number(abilities?.[ability] ?? 10);
    const baseModifier = Math.floor((score - 10) / 2);
    return [ability, Number.isFinite(numeric) && numeric !== baseModifier];
  }));
};

const CharacterSheetEditor = () => {
  const { templateId: routeTemplateId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const normalizedPathname = String(location.pathname || '').replace(/\/+$/, '');
  const templateId = routeTemplateId || (normalizedPathname === '/character/edit/new' ? 'new' : undefined);
  // date_of_change: 2026-04-22
  // rule_reference: Marketing-funnel parity — /character/edit/new is a public route so anonymous
  // marketing visitors can try the editor. Save Character, Upload Image, Choose Existing, and the
  // Dashboard button soft-gate on isSignedIn and route anonymous clickers through Clerk's
  // /sign-in?redirect_url=... flow so they return to the editor after signing in.
  const { isLoaded: isAuthLoaded, isSignedIn } = useAuth();
  const isAnonymous = isAuthLoaded && !isSignedIn;
  const promptSignIn = useCallback(() => {
    const redirectTarget = buildLocationRedirectTarget(location);
    navigate(buildAuthRoute('/sign-in', redirectTarget));
  }, [location, navigate]);
  // date_of_change: 2025-10-18 - View mode handled by query param; same sheet UI, read-only via fieldset
  const [searchParams] = useSearchParams();
  const viewOnly = (searchParams.get('mode') || '').toLowerCase() === 'view';
  const requestedCategory = resolveCharacterCategory(searchParams.get('category'));

  // date_of_change: 2025-09-29 - Removed unused convertStatsToAbilities to resolve lint warning

  // Helper to check what character templates are actually available
  const getAvailableTemplates = () => {
    if (characterTemplates) {
      return Object.keys(characterTemplates);
    }
    return [];
  };

  // State management
  const [character, setCharacter] = useState(null);
  const [customName, setCustomName] = useState('');
  const [characterCategory, setCharacterCategory] = useState(requestedCategory);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [showSmartPasteModal, setShowSmartPasteModal] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [showImageBrowser, setShowImageBrowser] = useState(false);
  const [assetUsage, setAssetUsage] = useState({ totalCount: 0, totalBytes: 0 });
  const [printSnapshot, setPrintSnapshot] = useState(null);
  const fileInputRef = useRef(null);
  const smartPasteOverlayRef = useRef(null);
  const smartPasteDialogRef = useRef(null);
  const smartPasteCloseButtonRef = useRef(null);
  const smartPasteTriggerRef = useRef(null);
  const tier = resolveRuntimeTier();
  const tierConfig = getTierConfig(tier);
  const isPremium = isPremiumTier(tier);
  const assetCap = tierConfig.limits.assetCount;
  const atAssetCap = !isPremium && assetCap !== 'unlimited' && assetUsage.totalCount >= assetCap;
  // date_of_change: 2026-02-26
  // rule_reference: CharacterSheetEditor UX — allow editing of structured features/feats/speciesTraits without losing object structure.
  // Only one editor is mounted at a time to avoid performance issues from many textareas.
  const [structuredEntryEditor, setStructuredEntryEditor] = useState(null);
  const structuredEntryNameRef = useRef(null);
  const structuredEntryDescriptionRef = useRef(null);
  const structuredEntrySourceRef = useRef(null);
  const structuredEntryMaxUsesRef = useRef(null);
  const structuredEntryCurrentUsesRef = useRef(null);
  const structuredEntryResetRef = useRef(null);
  const [expandedSections, setExpandedSections] = useState({
    actionEconomy: true,
    features: false,
    equipment: false,
    spells: false
  });
  const canGenerateStarterCharacter = templateId === 'new' && !viewOnly;

  useEffect(() => {
    if (!statusMessage) return;
    const t = setTimeout(() => setStatusMessage(''), 3000);
    return () => clearTimeout(t);
  }, [statusMessage]);

  useEffect(() => {
    if (!showSmartPasteModal) return undefined;

    const previouslyFocusedElement = document.activeElement;
    const fallbackFocusTarget = smartPasteTriggerRef.current;
    const overlay = smartPasteOverlayRef.current;
    const dialog = smartPasteDialogRef.current;
    const editorRoot = overlay?.parentElement;
    const backgroundSiblings = editorRoot
      ? Array.from(editorRoot.children).filter((element) => element !== overlay)
      : [];
    const backgroundState = backgroundSiblings.map((element) => ({
      element,
      hadInert: element.hasAttribute('inert'),
      ariaHidden: element.getAttribute('aria-hidden')
    }));
    const previousBodyOverflow = document.body.style.overflow;

    backgroundSiblings.forEach((element) => {
      element.setAttribute('inert', '');
      element.setAttribute('aria-hidden', 'true');
    });
    document.body.style.overflow = 'hidden';

    const getFocusableElements = () => Array.from(dialog?.querySelectorAll(
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    ) || []).filter((element) => !element.hasAttribute('hidden'));

    const handleModalKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setShowSmartPasteModal(false);
        return;
      }
      if (event.key !== 'Tab') return;

      const focusableElements = getFocusableElements();
      if (focusableElements.length === 0) {
        event.preventDefault();
        dialog?.focus();
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    document.addEventListener('keydown', handleModalKeyDown);
    const focusFrame = window.requestAnimationFrame
      ? window.requestAnimationFrame(() => (smartPasteCloseButtonRef.current || dialog)?.focus())
      : window.setTimeout(() => (smartPasteCloseButtonRef.current || dialog)?.focus(), 0);

    return () => {
      if (window.cancelAnimationFrame) {
        window.cancelAnimationFrame(focusFrame);
      } else {
        window.clearTimeout(focusFrame);
      }
      document.removeEventListener('keydown', handleModalKeyDown);
      document.body.style.overflow = previousBodyOverflow;
      backgroundState.forEach(({ element, hadInert, ariaHidden }) => {
        if (!hadInert) element.removeAttribute('inert');
        if (ariaHidden === null) {
          element.removeAttribute('aria-hidden');
        } else {
          element.setAttribute('aria-hidden', ariaHidden);
        }
      });
      const focusReturnTarget = previouslyFocusedElement instanceof HTMLElement && previouslyFocusedElement !== document.body
        ? previouslyFocusedElement
        : fallbackFocusTarget;
      if (focusReturnTarget?.isConnected) {
        focusReturnTarget.focus();
      }
    };
  }, [showSmartPasteModal]);

  useEffect(() => {
    if (templateId === 'new') {
      setCharacterCategory(requestedCategory);
    }
  }, [requestedCategory, templateId]);

  useEffect(() => {
    // date_of_change: 2026-04-22 - Skip the backend asset-usage lookup for anonymous visitors
    // trying the editor at /character/edit/new. The default { totalCount: 0, totalBytes: 0 } is
    // safe for display, and Upload Image / Choose Existing soft-gate on isSignedIn before they
    // would ever need the real snapshot.
    if (!isSignedIn) {
      return undefined;
    }
    let cancelled = false;

    const loadAssetUsage = async () => {
      const usage = await getAssetUsageSnapshot();
      if (!cancelled) {
        setAssetUsage(usage);
      }
    };

    loadAssetUsage();
    return () => {
      cancelled = true;
    };
  }, [isSignedIn]);

  // Load character data
  useEffect(() => {
    const loadCharacter = async () => {
      try {
        setLoading(true);

        console.log(`[CharacterSheetEditor] Attempting to load character: ${templateId}`);
        console.log(`[CharacterSheetEditor] Available templates:`, getAvailableTemplates());

        // date_of_change: 2025-11-13
        // cause: Default templates sometimes had sparse/incomplete UnifiedDataManager records
        // fix: For known template keys (wizard, cleric, fighter, etc.), prefer CharacterDataService
        //      so the editor/viewer display the full JSON (weapons, equipment, spells, features, proficiencies).
        const preferKey = resolveTemplateKey(templateId);
        if (preferKey) {
          try {
            const characterFromTemplate = CharacterDataService.createCharacterFromTemplate(preferKey);
            if (characterFromTemplate) {
              // Fallback: map proficiencies/languages from compat if top-level missing
              const compatProfs = characterFromTemplate.compat?.originalData?.proficiencies;
              const merged = {
                ...characterFromTemplate,
                proficiencies: characterFromTemplate.proficiencies || compatProfs || null,
                languages: characterFromTemplate.languages || compatProfs?.languages || characterFromTemplate.biography?.languages || null
              };
              const enrichedTemplate = IntelligentContentParser.deriveCharacterImportData(merged);
              const hydratedTemplate = hydrateCharacterCombatState(enrichedTemplate);
              console.log(`[CharacterSheetEditor] ✅ Loaded template (preferred over UDM for defaults):`, characterFromTemplate);
              setCharacter(hydratedTemplate);
              setCharacterCategory(inferCharacterCategory({ id: hydratedTemplate.id || templateId, tags: hydratedTemplate.tags }, hydratedTemplate.name));
              setCustomName(hydratedTemplate.name + ' (Custom)');
              setStatusMessage(`✅ Loaded character template: ${hydratedTemplate.name}`);
              setLoading(false);
              return;
            }
          } catch (e) {
            console.warn('[CharacterSheetEditor] Preferred template load failed, falling back to UDM/template flow:', e);
          }
        }

        if (templateId === 'new') {
          // @date_of_change: 2025-10-12 - Provide blank D&D 2024 character sheet scaffold when creating from dashboard
          // rule_reference: D&D 2024 Player's Handbook "Create a Character" requires empty fields before player input
          const blankCharacter = createBlankCharacter();
          setCharacter(blankCharacter);
          setCharacterCategory(requestedCategory);
          setCustomName('');
          setStatusMessage('🆕 Blank character sheet loaded. Fill fields manually, generate a starter, or use Smart Paste.');
          setLoading(false);
          return;
        }

        // Prefer UnifiedDataManager canonical entity first
        let entity = null;
        try {
          entity = await UnifiedDataManager.loadEntity(templateId, 'character');
        } catch (e) {
          console.warn('[CharacterSheetEditor] UDM.loadEntity failed, falling back:', e);
        }

        if (entity && entity.data) {
          const canonicalNode = entity.data || {};
          const identityNode = canonicalNode.identity || {};
          const disp = UnifiedDataManager.getDisplayData({ type: 'character', id: entity.id, data: canonicalNode });
          const classesNode = Array.isArray(canonicalNode.classes) ? canonicalNode.classes : [];
          const primaryClass = classesNode[0] || {};

          const resolvedName = identityNode.name ?? canonicalNode.name ?? entity.id ?? 'Unnamed Character';
          const resolvedClass = primaryClass.name ?? null;
          const resolvedLevel = canonicalNode.level ?? primaryClass.level ?? null;
          const isCanonicalSource = true;

          const mergedCharacter = {
            ...canonicalNode,
            id: entity.id,
            name: disp.name || resolvedName,
            class: disp.class ?? resolvedClass,
            level: disp.level ?? resolvedLevel,
            // date_of_change: 2025-12-12
            // rule_reference: master_character_schema.json canonical branch stores identity fields at top-level.
            // Prefer those, then fall back to identity.* for older/alternate sources.
            race: canonicalNode.race ?? canonicalNode.identity?.race ?? null,
            background: canonicalNode.background ?? canonicalNode.identity?.background ?? null,
            alignment: canonicalNode.alignment ?? canonicalNode.identity?.alignment ?? null,
            subclass: primaryClass.subclass ?? null,
            hitDice: primaryClass.hitDice ?? canonicalNode.hitDice ?? null,
            experience: canonicalNode.experience ?? canonicalNode.experiencePoints ?? null,
            inspiration: canonicalNode.inspiration ?? null,
            hitDiceSpent: canonicalNode.hitDiceSpent ?? null,
            deathSaves: canonicalNode.deathSaves ?? null,
            hasShield: canonicalNode.hasShield ?? null,
            size: canonicalNode.size ?? null,
            // date_of_change: 2025-12-12
            // rule_reference: master_character_schema.json canonical branch stores hitPoints/combatStats; preserve on load.
            hp: disp.hp ?? canonicalNode.hp?.current ?? canonicalNode.hitPoints?.current ?? null,
            maxHp: disp.maxHp ?? canonicalNode.hp?.maximum ?? canonicalNode.hitPoints?.maximum ?? null,
            tempHp: canonicalNode.hp?.temporary ?? canonicalNode.hitPoints?.temporary ?? null,
            ac: disp.ac ?? canonicalNode.ac?.value ?? canonicalNode.combatStats?.armorClass ?? null,
            initiative: canonicalNode.initiative ?? canonicalNode.ac?.initiative ?? canonicalNode.combatStats?.initiative ?? null,
            speed: canonicalNode.speed?.walk ?? canonicalNode.combatStats?.speed ?? null,
            abilities: disp.abilities ?? canonicalNode.abilities ?? null,
            skills: canonicalNode.skills ?? null,
            skillsDetailed: canonicalNode.skillsDetailed ?? null,
            savingThrows: normalizeSavingThrowProficiencies(
              canonicalNode.saves ?? canonicalNode.savingThrows,
              disp.abilities ?? canonicalNode.abilities
            ),
            features: canonicalNode.featuresDetailed ?? canonicalNode.features ?? null,
            classFeatures: canonicalNode.classFeatures ?? null,
            feats: canonicalNode.feats ?? null,
            speciesTraits: canonicalNode.speciesTraits ?? null,
            classFeaturesText: canonicalNode.classFeaturesText ?? '',
            speciesTraitsText: canonicalNode.speciesTraitsText ?? '',
            featsText: canonicalNode.featsText ?? '',
            equipment: canonicalNode.equipmentDetailed ?? canonicalNode.equipment ?? canonicalNode.inventory ?? null,
            inventory: canonicalNode.inventory ?? null,
            items: canonicalNode.items ?? null,
            // date_of_change: 2025-12-12
            // rule_reference: Handle both flat array and nested object (master_character_schema.json) for actions
            actions: (() => {
              if (Array.isArray(canonicalNode.actions)) return canonicalNode.actions;
              if (canonicalNode.actions && Array.isArray(canonicalNode.actions.actions)) return canonicalNode.actions.actions;
              return null;
            })(),
            bonusActions: (() => {
              if (Array.isArray(canonicalNode.bonusActions)) return canonicalNode.bonusActions;
              if (canonicalNode.actions && Array.isArray(canonicalNode.actions.bonusActions)) return canonicalNode.actions.bonusActions;
              return null;
            })(),
            reactions: (() => {
              if (Array.isArray(canonicalNode.reactions)) return canonicalNode.reactions;
              if (canonicalNode.actions && Array.isArray(canonicalNode.actions.reactions)) return canonicalNode.actions.reactions;
              return null;
            })(),
            spellcasting: canonicalNode.spellcasting ?? null,
            spells: canonicalNode.spells ?? null,
            senses: canonicalNode.senses ?? null,
            passivePerception: canonicalNode.passivePerception ?? null,
            proficiencies: canonicalNode.proficiencies ?? null,
            toolProficiencies: canonicalNode.toolProficiencies ?? null,
            languages: canonicalNode.languages ?? null,
            appearance: canonicalNode.appearance?.description ?? canonicalNode.appearance ?? null,
            biography: canonicalNode.biography ?? null,
            personalityTraits: canonicalNode.personalityTraits ?? canonicalNode.biography?.personalityTraits ?? null,
            motivations: canonicalNode.motivations ?? canonicalNode.biography?.motivations ?? null,
            ideals: canonicalNode.ideals ?? canonicalNode.biography?.ideals ?? null,
            bonds: canonicalNode.bonds ?? canonicalNode.biography?.bonds ?? null,
            flaws: canonicalNode.flaws ?? canonicalNode.biography?.flaws ?? null,
            backstory: canonicalNode.backstory ?? canonicalNode.biography?.backstory ?? null,
            identifyingFeatures: canonicalNode.identifyingFeatures ?? canonicalNode.biography?.identifyingFeatures ?? null,
            notes: canonicalNode.notes ?? null,
            artwork: entity._meta?.artwork ?? null,
            tokenAssetKey: entity._meta?.artwork ?? null,
            dataQualityWarnings: []
          };
          const enrichedCharacter = IntelligentContentParser.deriveCharacterImportData(mergedCharacter);
          const hydratedCharacter = hydrateCharacterCombatState(enrichedCharacter);
          hydratedCharacter.dataQualityWarnings = Array.from(new Set([
            ...(hydratedCharacter.dataQualityWarnings || []),
            ...getStoredCombatReadinessIssues(entity?._meta?.combatReadiness)
          ]));

          console.log(`[CharacterSheetEditor] ✅ Loaded character data:`, {
            id: hydratedCharacter.id,
            name: hydratedCharacter.name,
            level: hydratedCharacter.level,
            class: hydratedCharacter.class,
            isCanonicalSource,
            dataQualityWarnings: hydratedCharacter.dataQualityWarnings
          });

          const warningBanner = hydratedCharacter.dataQualityWarnings && hydratedCharacter.dataQualityWarnings.length > 0
            ? `⚠️ Issues detected: ${hydratedCharacter.dataQualityWarnings.join(' ')}`
            : null;

          setCharacter(hydratedCharacter);
          setCharacterCategory(inferCharacterCategory(entity, hydratedCharacter.name));
          setCustomName(isCanonicalSource ? hydratedCharacter.name : `${hydratedCharacter.name} (Custom)`);
          setStatusMessage(warningBanner
            ? warningBanner
            : isCanonicalSource
              ? '✅ Loaded canonical character data from UnifiedDataManager'
              : `✅ Loaded character data: ${mergedCharacter.name}`);
        } else {
          // Try to load character data directly from CharacterDataService
          try {
            console.log(`[CharacterSheetEditor] Trying CharacterDataService.createCharacterFromTemplate for: ${templateId}`);
            const characterFromTemplate = CharacterDataService.createCharacterFromTemplate(templateId);
            if (characterFromTemplate) {
              const enrichedTemplate = IntelligentContentParser.deriveCharacterImportData(characterFromTemplate);
              const hydratedTemplate = hydrateCharacterCombatState(enrichedTemplate);
              console.log(`[CharacterSheetEditor] ✅ Loaded from template:`, characterFromTemplate);
              setCharacter(hydratedTemplate);
              setCharacterCategory(inferCharacterCategory({ id: hydratedTemplate.id || templateId, tags: hydratedTemplate.tags }, hydratedTemplate.name));
              setCustomName(hydratedTemplate.name + ' (Custom)');
              setStatusMessage(`✅ Loaded character template: ${hydratedTemplate.name}`);
            } else {
              throw new Error(`Template "${templateId}" not found in CharacterDataService`);
            }
          } catch (templateError) {
            // No data found - be truthful about it
            const availableTemplates = getAvailableTemplates();
            console.error(`[CharacterSheetEditor] ❌ No character data found for "${templateId}"`);
            console.error(`[CharacterSheetEditor] Template error:`, templateError);
            console.log(`[CharacterSheetEditor] Available templates: [${availableTemplates.join(', ')}]`);

            setStatusMessage(
              `❌ Character template "${templateId}" not found. ` +
              (availableTemplates.length > 0
                ? `Available templates: ${availableTemplates.join(', ')}`
                : 'No character templates are available.')
            );
            setCharacter(null);
          }
        }
      } catch (error) {
        console.error('[CharacterSheetEditor] Failed to load character:', error);
        setStatusMessage(`❌ Error loading character: ${error.message}`);
        setCharacter(null);
      } finally {
        setLoading(false);
      }
    };

    if (templateId) {
      loadCharacter();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId]);

  // Helper functions
  const updateCharacter = (field, value) => {
    setCharacter(prev => ({ ...prev, [field]: value }));
  };

  const updateClassProgression = (nextClasses) => {
    setCharacter((prev) => {
      if (!prev) return prev;
      const classes = (Array.isArray(nextClasses) ? nextClasses : [])
        .map((entry) => ({
          ...entry,
          name: String(entry?.name || ''),
          subclass: String(entry?.subclass || ''),
          level: Math.max(1, Math.min(20, parseInt(entry?.level, 10) || 1)),
          hitDice: String(entry?.hitDice || entry?.hitDie || '')
        }));
      if (classes.length === 0) return prev;

      const primary = classes[0];
      const totalLevel = classes.reduce((sum, entry) => sum + entry.level, 0);
      const next = {
        ...prev,
        classes,
        class: primary.name,
        subclass: primary.subclass,
        level: totalLevel,
        proficiencyBonus: Math.floor((Math.max(1, totalLevel) - 1) / 4) + 2
      };

      // Character level and proficiency are derived from class progression. Preserve
      // legitimate initiative, passive Perception, and spell adjustments while updating
      // their bases. date_of_change: 2026-08-08.
      return synchronizeDerivedCharacterStats(prev, next);
    });
  };

  const updateCharacterArrayEntry = (sourceCollection, sourceIndex, patch) => {
    setCharacter((prev) => {
      if (!prev) return prev;
      const path = String(sourceCollection || '').split('.').filter(Boolean);
      if (path.length === 0) return prev;

      const next = { ...prev };
      let sourceNode = prev;
      let targetNode = next;
      path.slice(0, -1).forEach((segment) => {
        const sourceValue = sourceNode?.[segment];
        const normalizedSource = segment === 'equipment' && Array.isArray(sourceValue)
          ? { items: sourceValue }
          : sourceValue;
        targetNode[segment] = normalizedSource && typeof normalizedSource === 'object' && !Array.isArray(normalizedSource)
          ? { ...normalizedSource }
          : {};
        sourceNode = normalizedSource;
        targetNode = targetNode[segment];
      });

      const collectionKey = path[path.length - 1];
      const sourceArray = Array.isArray(sourceNode?.[collectionKey]) ? sourceNode[collectionKey] : [];
      const nextArray = [...sourceArray];
      const existing = nextArray[sourceIndex];
      const existingObject = typeof existing === 'string' ? { name: existing } : { ...(existing || {}) };
      nextArray[sourceIndex] = typeof patch === 'function'
        ? patch(existingObject)
        : { ...existingObject, ...patch };
      targetNode[collectionKey] = nextArray;
      return next;
    });
  };

  const updateWeaponField = (row, field, rawValue) => {
    const sourceCollection = row?.sourceCollection || 'equipment.weapons';
    const sourceIndex = Number.isInteger(row?.sourceIndex)
      ? row.sourceIndex
      : Number(row?.draftIndex || 0);
    updateCharacterArrayEntry(sourceCollection, sourceIndex, (existing) => {
      const next = { ...existing, type: existing.type || 'weapon' };
      if (field === 'attackBonus') {
        const match = String(rawValue || '').match(/[+-]?\d+/);
        if (match) next.attackBonus = Number(match[0]);
        else delete next.attackBonus;
      } else if (field === 'damageText') {
        const text = String(rawValue || '').trim();
        const match = text.match(/^(\d+d\d+(?:\s*[+-]\s*\d+)?)\s*(.*)$/i);
        next.damage = match ? match[1].replace(/\s+/g, '') : text;
        if (match?.[2]) next.damageType = match[2].trim();
        else delete next.damageType;
      } else if (field === 'range') {
        next.range = String(rawValue || '').trim();
      } else if (field === 'mastery') {
        next.mastery = String(rawValue || '').trim();
      } else {
        next[field] = String(rawValue || '');
      }
      return next;
    });
  };

  const updateCurrencyValue = (coinKey, value) => {
    const schemaCoinKeys = { cp: 'copper', sp: 'silver', ep: 'electrum', gp: 'gold', pp: 'platinum' };
    setCharacter((prev) => {
      if (!prev) return prev;
      const equipment = prev.equipment && typeof prev.equipment === 'object' && !Array.isArray(prev.equipment)
        ? prev.equipment
        : { items: Array.isArray(prev.equipment) ? prev.equipment : [] };
      return {
        ...prev,
        equipment: {
          ...equipment,
          currency: {
            ...(equipment.currency || {}),
            [schemaCoinKeys[coinKey] || coinKey]: Math.max(0, parseInt(value, 10) || 0)
          }
        }
      };
    });
  };

  const updateSpellEntry = (index, patch) => {
    setCharacter((prev) => {
      if (!prev) return prev;
      const directEntries = isSpellRecord(prev.spells) && Array.isArray(prev.spells.entries)
        ? prev.spells.entries
        : resolveCharacterSpellData(prev).entries;
      const currentEntries = directEntries.map((entry) => (isSpellRecord(entry) ? { ...entry } : { name: String(entry || '') }));
      const existing = currentEntries[index] || { name: '', level: 0 };
      currentEntries[index] = { ...existing, ...patch };
      const spells = buildEditedCharacterSpells(prev.spells, currentEntries);
      return {
        ...prev,
        spells,
        spellcasting: buildCharacterSpellcastingCompat({ ...prev, spells })
      };
    });
  };

  const updateSpellSlot = (level, field, rawValue) => {
    setCharacter((prev) => {
      if (!prev) return prev;
      const spells = (prev.spells && typeof prev.spells === 'object' && !Array.isArray(prev.spells)) ? prev.spells : {};
      const existingSlots = normalizeSpellSlots(spells.slots ?? spells.spellSlots);
      const existing = existingSlots[level] || { used: 0, total: 0 };
      const numericValue = Math.max(0, parseInt(rawValue, 10) || 0);
      const nextSlot = { ...existing, [field]: numericValue };
      if (field === 'total' && nextSlot.used > numericValue) nextSlot.used = numericValue;
      if (field === 'used' && numericValue > nextSlot.total) nextSlot.total = numericValue;
      return { ...prev, spells: { ...spells, slots: { ...existingSlots, [level]: nextSlot } } };
    });
  };

  const updateSpellcastingAbility = (ability) => {
    setCharacter((prev) => {
      if (!prev) return prev;
      const spells = (prev.spells && typeof prev.spells === 'object' && !Array.isArray(prev.spells)) ? prev.spells : {};
      const nextSpells = { ...spells };
      const previousData = resolveCharacterSpellData(prev);
      const previousAbility = previousData.ability;
      const scores = resolveCharacterAbilityScores(prev);
      const proficiency = getProficiencyBonus(prev);
      const previousModifier = previousAbility ? abilityModNumber(Number(scores?.[previousAbility] ?? 10)) : 0;
      const nextModifier = ability ? abilityModNumber(Number(scores?.[ability] ?? 10)) : 0;
      const attackAdjustment = Number.isFinite(Number(previousData.attackBonus))
        ? Number(previousData.attackBonus) - (previousModifier + proficiency)
        : 0;
      const saveAdjustment = Number.isFinite(Number(previousData.saveDC))
        ? Number(previousData.saveDC) - (8 + previousModifier + proficiency)
        : 0;

      if (ability) {
        nextSpells.ability = ability;
        nextSpells.attackBonus = nextModifier + proficiency + attackAdjustment;
        nextSpells.saveDC = 8 + nextModifier + proficiency + saveAdjustment;
      } else {
        delete nextSpells.ability;
        delete nextSpells.attackBonus;
        delete nextSpells.saveDC;
      }
      return {
        ...prev,
        spells: nextSpells,
        spellcasting: buildCharacterSpellcastingCompat({ ...prev, spells: nextSpells })
      };
    });
  };

  const updateStringList = (field, values) => {
    setCharacter((prev) => {
      if (!prev) return prev;
      const nextList = (Array.isArray(values) ? values : []).map((value) => String(value || ''));
      const proficiencyKey = field === 'toolProficiencies' ? 'tools' : (field === 'languages' ? 'languages' : null);
      const proficiencies = prev.proficiencies && typeof prev.proficiencies === 'object' && !Array.isArray(prev.proficiencies)
        ? prev.proficiencies
        : {};
      return {
        ...prev,
        [field]: nextList,
        ...(proficiencyKey ? { proficiencies: { ...proficiencies, [proficiencyKey]: nextList } } : {})
      };
    });
  };

  const updateProficiencyLabels = (group, currentLabels, matcher, canonicalLabel, checked) => {
    setCharacter((prev) => {
      if (!prev) return prev;
      const proficiencies = prev.proficiencies && typeof prev.proficiencies === 'object' && !Array.isArray(prev.proficiencies)
        ? prev.proficiencies
        : {};
      const materialized = Array.from(new Set((Array.isArray(currentLabels) ? currentLabels : []).map(String).filter(Boolean)));
      const nextLabels = checked
        ? Array.from(new Set([...materialized, ...(canonicalLabel ? [canonicalLabel] : [])]))
        : materialized.filter((label) => !matcher(String(label).toLowerCase()));
      return { ...prev, proficiencies: { ...proficiencies, [group]: nextLabels } };
    });
  };

  const updateSpellcastingAdjustment = (field, adjustment) => {
    setCharacter((prev) => {
      if (!prev) return prev;
      const resolved = resolveCharacterSpellData(prev);
      const ability = resolved.ability;
      if (!ability) return prev;
      const scores = resolveCharacterAbilityScores(prev);
      const modifier = abilityModNumber(Number(scores?.[ability] ?? 10));
      const proficiency = getProficiencyBonus(prev);
      const base = field === 'saveDC' ? 8 + modifier + proficiency : modifier + proficiency;
      const spells = (prev.spells && typeof prev.spells === 'object' && !Array.isArray(prev.spells)) ? prev.spells : {};
      const nextSpells = { ...spells, [field]: base + Number(adjustment || 0) };
      return {
        ...prev,
        spells: nextSpells,
        spellcasting: buildCharacterSpellcastingCompat({ ...prev, spells: nextSpells })
      };
    });
  };

  const addSpellEntry = () => {
    const nextIndex = isSpellRecord(character?.spells) && Array.isArray(character.spells.entries)
      ? character.spells.entries.length
      : resolvedSpellData.entries.length;
    updateSpellEntry(nextIndex, {
      name: 'New Spell',
      level: 0,
      prepared: false,
      ritual: false,
      concentration: false,
      castingTime: '',
      range: '',
      components: '',
      duration: '',
      description: ''
    });
  };

  const removeSpellEntry = (index) => {
    setCharacter((prev) => {
      if (!prev) return prev;
      const directEntries = isSpellRecord(prev.spells) && Array.isArray(prev.spells.entries)
        ? prev.spells.entries
        : resolveCharacterSpellData(prev).entries;
      const currentEntries = directEntries
        .map((entry) => (isSpellRecord(entry) ? { ...entry } : { name: String(entry || '') }));
      const nextEntries = currentEntries.filter((_, entryIndex) => entryIndex !== index);
      const spells = buildEditedCharacterSpells(prev.spells, nextEntries);
      return {
        ...prev,
        spells,
        spellcasting: buildCharacterSpellcastingCompat({ ...prev, spells })
      };
    });
  };

  const getStoredCombatReadinessIssues = (combatReadinessMeta) => {
    if (!combatReadinessMeta || typeof combatReadinessMeta !== 'object') return [];
    return Array.from(new Set([
      ...(Array.isArray(combatReadinessMeta.errors) ? combatReadinessMeta.errors : []),
      ...(Array.isArray(combatReadinessMeta.warnings) ? combatReadinessMeta.warnings : [])
    ].filter(Boolean)));
  };

  // date_of_change: 2026-02-26
  // rule_reference: AuditTrail (CanonicalSchemas.ts) — record editor decisions for GM transparency.
  const appendAuditDecision = (audit, header, category) => {
    const base = (audit && typeof audit === 'object') ? audit : {
      normalizer: 'CharacterSheetEditor',
      seed: String(character?.id || templateId || 'unsaved-character'),
      decisions: []
    };
    const decisions = Array.isArray(base.decisions) ? base.decisions : [];
    return {
      ...base,
      decisions: [
        ...decisions,
        {
          header,
          decision: { category, confidence: 1, reason: ['CharacterSheetEditor'] }
        }
      ]
    };
  };

  const getProficiencyBonus = (sourceCharacter = character) => {
    const explicitBonus = Number(sourceCharacter?.proficiencyBonus);
    if (Number.isFinite(explicitBonus)) return explicitBonus;
    const levelValue = Number(sourceCharacter?.level);
    const level = Number.isFinite(levelValue) && levelValue > 0 ? levelValue : 1;
    return Math.floor((level - 1) / 4) + 2;
  };

  // date_of_change: 2026-06-27
  // rule_reference: D&D 5e/2024 skills add the linked ability modifier plus proficiency once, or twice when expertise applies.
  const resolveSkillState = (sourceCharacter, skillName, abilityName) => {
    const skillsMap = (sourceCharacter?.skills && typeof sourceCharacter.skills === 'object' && !Array.isArray(sourceCharacter.skills))
      ? sourceCharacter.skills
      : null;
    const mapEntry = skillsMap ? skillsMap[skillName] : null;
    const detailed = Array.isArray(sourceCharacter?.skillsDetailed)
      ? sourceCharacter.skillsDetailed.find(s => String(s?.name || '').toLowerCase() === String(skillName).toLowerCase())
      : null;
    const baseModifier = abilityModNumber(Number(sourceCharacter?.abilities?.[abilityName] ?? 10));
    const proficiencyBonus = getProficiencyBonus(sourceCharacter);
    const storedBonus = (() => {
      const candidate = detailed?.bonus ?? mapEntry?.value ?? mapEntry?.bonus;
      return typeof candidate === 'number' && Number.isFinite(candidate)
        ? candidate
        : (typeof candidate === 'string' && candidate.trim() !== '' && Number.isFinite(Number(candidate))
          ? Number(candidate)
          : null);
    })();
    const legacyProficient = Array.isArray(sourceCharacter?.skills)
      && sourceCharacter.skills.some(s => String(s).toLowerCase() === String(skillName).toLowerCase());
    const schemaTraining = typeof mapEntry === 'string' ? mapEntry.toLowerCase() : '';
    const proficient = Boolean(
      mapEntry?.proficient === true
      || detailed?.proficient === true
      || legacyProficient
      || schemaTraining === 'proficient'
      || schemaTraining === 'expert'
      || schemaTraining === 'half-proficient'
      || (storedBonus !== null && storedBonus !== baseModifier)
    );
    const expertise = Boolean(
      mapEntry?.expertise === true
      || detailed?.expertise === true
      || schemaTraining === 'expert'
    );
    const value = baseModifier + (proficient ? proficiencyBonus : 0) + (expertise ? proficiencyBonus : 0);
    return { proficient, expertise, value };
  };

  const buildSkillTrainingSnapshot = (sourceCharacter = character) => {
    const skillsMap = (sourceCharacter?.skills && typeof sourceCharacter.skills === 'object' && !Array.isArray(sourceCharacter.skills))
      ? sourceCharacter.skills
      : {};
    const detailedEntries = Array.isArray(sourceCharacter?.skillsDetailed)
      ? sourceCharacter.skillsDetailed
      : [];
    const legacyEntries = Array.isArray(sourceCharacter?.skills)
      ? sourceCharacter.skills
      : [];
    const trackedSkillNames = Array.from(new Set([
      ...Object.keys(skillsMap || {}),
      ...detailedEntries.map((entry) => entry?.name),
      ...legacyEntries
    ].map((name) => String(name || '').trim()).filter(Boolean)));

    const nextSkills = {};
    const nextDetailed = [];

    trackedSkillNames.forEach((skillName) => {
      const abilityKey = SKILL_ABILITY_MAP[skillName];
      const mapEntry = skillsMap?.[skillName];
      const detailedEntry = detailedEntries.find((entry) => String(entry?.name || '').toLowerCase() === String(skillName).toLowerCase());
      const state = abilityKey
        ? resolveSkillState(sourceCharacter, skillName, abilityKey)
        : {
            proficient: Boolean(mapEntry?.proficient === true || detailedEntry?.proficient === true),
            expertise: Boolean(mapEntry?.expertise === true || detailedEntry?.expertise === true),
            value: (() => {
              const candidate = detailedEntry?.bonus ?? mapEntry?.value ?? mapEntry?.bonus;
              return typeof candidate === 'number' && Number.isFinite(candidate)
                ? candidate
                : (typeof candidate === 'string' && candidate.trim() !== '' && Number.isFinite(Number(candidate))
                  ? Number(candidate)
                  : 0);
            })()
          };

      if (!state.proficient && !state.expertise) {
        return;
      }

      nextSkills[skillName] = {
        ...(mapEntry && typeof mapEntry === 'object' ? mapEntry : {}),
        proficient: state.proficient,
        expertise: state.expertise,
        value: state.value
      };

      nextDetailed.push({
        ...(detailedEntry && typeof detailedEntry === 'object' ? detailedEntry : {}),
        name: skillName,
        proficient: state.proficient,
        expertise: state.expertise,
        bonus: state.value
      });
    });

    return { skills: nextSkills, skillsDetailed: nextDetailed };
  };

  const getPassivePerceptionBase = (sourceCharacter) => (
    10 + resolveSkillState(sourceCharacter, 'Perception', 'wisdom').value
  );

  const synchronizeDerivedCharacterStats = (previousCharacter, nextCharacter, { initiative = false } = {}) => {
    const previousPassiveBase = getPassivePerceptionBase(previousCharacter);
    const previousPassive = Number(previousCharacter?.passivePerception);
    const passiveAdjustment = Number.isFinite(previousPassive)
      ? previousPassive - previousPassiveBase
      : 0;
    const synchronized = {
      ...nextCharacter,
      passivePerception: getPassivePerceptionBase(nextCharacter) + passiveAdjustment
    };

    if (initiative) {
      const previousDexModifier = abilityModNumber(Number(previousCharacter?.abilities?.dexterity ?? 10));
      const previousInitiative = Number(previousCharacter?.initiative);
      const initiativeAdjustment = Number.isFinite(previousInitiative)
        ? previousInitiative - previousDexModifier
        : 0;
      synchronized.initiative = abilityModNumber(Number(nextCharacter?.abilities?.dexterity ?? 10)) + initiativeAdjustment;
    }

    const previousSpellData = resolveCharacterSpellData(previousCharacter);
    const nextSpellData = resolveCharacterSpellData(nextCharacter);
    const spellAbility = nextSpellData.ability || previousSpellData.ability;
    if (spellAbility) {
      const previousSpellModifier = abilityModNumber(Number(previousCharacter?.abilities?.[spellAbility] ?? 10));
      const nextSpellModifier = abilityModNumber(Number(nextCharacter?.abilities?.[spellAbility] ?? 10));
      const previousProficiency = getProficiencyBonus(previousCharacter);
      const nextProficiency = getProficiencyBonus(nextCharacter);
      const spells = (synchronized.spells && typeof synchronized.spells === 'object' && !Array.isArray(synchronized.spells))
        ? { ...synchronized.spells }
        : { entries: Array.isArray(synchronized.spells) ? synchronized.spells : [] };

      if (Number.isFinite(Number(previousSpellData.attackBonus))) {
        const adjustment = Number(previousSpellData.attackBonus) - (previousSpellModifier + previousProficiency);
        spells.attackBonus = nextSpellModifier + nextProficiency + adjustment;
      }
      if (Number.isFinite(Number(previousSpellData.saveDC))) {
        const adjustment = Number(previousSpellData.saveDC) - (8 + previousSpellModifier + previousProficiency);
        spells.saveDC = 8 + nextSpellModifier + nextProficiency + adjustment;
      }
      synchronized.spells = spells;
    }

    return synchronized;
  };

  const updateAbilityScore = (abilityName, score) => {
    setCharacter((prev) => {
      if (!prev) return prev;
      const next = {
        ...prev,
        abilities: { ...(prev.abilities || {}), [abilityName]: score },
        savingThrows: normalizeSavingThrowProficiencies(prev.savingThrows, prev.abilities)
      };
      return synchronizeDerivedCharacterStats(prev, next, { initiative: abilityName === 'dexterity' });
    });
  };

  const updateShieldState = (hasShield) => {
    setCharacter((prev) => {
      if (!prev) return prev;
      const currentAc = Number(prev.ac);
      const shieldAdjustment = hasShield === Boolean(prev.hasShield) ? 0 : (hasShield ? 2 : -2);
      return {
        ...prev,
        hasShield,
        ac: Number.isFinite(currentAc) ? currentAc + shieldAdjustment : (hasShield ? 12 : 10)
      };
    });
  };

  const updateSkillTraining = (skillName, abilityName, nextFlags) => {
    setCharacter((prev) => {
      if (!prev) return prev;
      const currentState = resolveSkillState(prev, skillName, abilityName);
      const proficient = Boolean(nextFlags.proficient);
      const expertise = proficient && Boolean(nextFlags.expertise);
      const baseModifier = abilityModNumber(Number(prev?.abilities?.[abilityName] ?? 10));
      const proficiencyBonus = getProficiencyBonus(prev);
      const value = baseModifier + (proficient ? proficiencyBonus : 0) + (expertise ? proficiencyBonus : 0);
      const existingSkills = (prev.skills && typeof prev.skills === 'object' && !Array.isArray(prev.skills)) ? prev.skills : {};
      const nextSkills = { ...existingSkills, [skillName]: { proficient, expertise, value } };
      const nextDetailed = Array.isArray(prev.skillsDetailed)
        ? prev.skillsDetailed.map((entry) => String(entry?.name || '').toLowerCase() === String(skillName).toLowerCase()
          ? { ...entry, proficient, expertise, bonus: value }
          : entry)
        : [];
      if (!nextDetailed.some((entry) => String(entry?.name || '').toLowerCase() === String(skillName).toLowerCase())) {
        nextDetailed.push({ name: skillName, proficient, expertise, bonus: value });
      }
      const trainingSnapshot = buildSkillTrainingSnapshot({
        ...prev,
        skills: nextSkills,
        skillsDetailed: nextDetailed
      });
      const nextCharacter = hydrateCharacterCombatState({
        ...prev,
        ...trainingSnapshot,
        _audit: appendAuditDecision(
          prev?._audit,
          `CharacterSheetEditor.skillTraining(${skillName}): ${currentState.proficient}/${currentState.expertise} -> ${proficient}/${expertise}`,
          'skill_training_update'
        )
      });
      return synchronizeDerivedCharacterStats(prev, nextCharacter);
    });
  };

  const normalizeClassKey = (value) => String(value || '').trim().toLowerCase().replace(/[^a-z]/g, '');

  // date_of_change: 2026-06-27
  // rule_reference: Starter class feature suggestions reuse the app's generator summaries instead of embedding copied rules text; GMs can edit or replace entries.
  const getSuggestedClassFeatures = (sourceCharacter = character) => {
    const classKey = normalizeClassKey(sourceCharacter?.class);
    const option = Object.values(STARTER_CLASS_OPTIONS).find((entry) => normalizeClassKey(entry.label) === classKey || normalizeClassKey(entry.key) === classKey);
    if (!option || !Array.isArray(option.levelOneFeatures)) return [];
    const existingNames = new Set((Array.isArray(sourceCharacter?.classFeatures) ? sourceCharacter.classFeatures : [])
      .map((entry) => String(entry?.name || entry || '').trim().toLowerCase())
      .filter(Boolean));
    return option.levelOneFeatures
      .filter((entry) => entry?.name && !existingNames.has(String(entry.name).trim().toLowerCase()))
      .map((entry) => ({
        name: entry.name,
        description: entry.description || '',
        source: { name: 'Starter Character Generator', license: 'Custom summary' }
      }));
  };

  const addStructuredEntry = (field, fallbackName) => {
    if (viewOnly || previewMode) return;
    const nextIndex = Array.isArray(character?.[field]) ? character[field].length : 0;
    setCharacter((prev) => {
      if (!prev) return prev;
      const list = Array.isArray(prev?.[field]) ? [...prev[field]] : [];
      const nextEntry = { name: fallbackName, description: '' };
      return hydrateCharacterCombatState({
        ...prev,
        [field]: [...list, nextEntry],
        _audit: appendAuditDecision(prev?._audit, `CharacterSheetEditor.addStructuredEntry(${field}): ${fallbackName}`, 'structured_entry_add')
      });
    });
    setStructuredEntryEditor({ field, index: nextIndex, openedAt: Date.now() });
  };

  const applySuggestedClassFeatures = () => {
    if (viewOnly || previewMode) return;
    setCharacter((prev) => {
      if (!prev) return prev;
      const additions = getSuggestedClassFeatures(prev);
      if (additions.length === 0) return prev;
      return hydrateCharacterCombatState({
        ...prev,
        classFeatures: [...(Array.isArray(prev.classFeatures) ? prev.classFeatures : []), ...additions],
        _audit: appendAuditDecision(prev?._audit, `CharacterSheetEditor.applySuggestedClassFeatures(${prev.class || 'unknown'}): ${additions.length}`, 'class_feature_suggestion_apply')
      });
    });
    setStatusMessage('✅ Added suggested class features');
  };

  const handleCharacterClassChange = (value) => {
    setCharacter((prev) => {
      if (!prev) return prev;
      const classes = normalizeCharacterClassProgression(prev);
      classes[0] = { ...classes[0], name: value };
      const nextBase = { ...prev, class: value, classes };
      const additions = getSuggestedClassFeatures(nextBase);
      if ((Array.isArray(prev.classFeatures) && prev.classFeatures.length > 0) || additions.length === 0) {
        return nextBase;
      }
      return hydrateCharacterCombatState({
        ...nextBase,
        classFeatures: additions,
        _audit: appendAuditDecision(prev?._audit, `CharacterSheetEditor.autoClassFeatures(${value}): ${additions.length}`, 'class_feature_suggestion_apply')
      });
    });
  };

  const handleCharacterSubclassChange = (value) => {
    setCharacter((prev) => {
      if (!prev) return prev;
      const classes = normalizeCharacterClassProgression(prev);
      classes[0] = { ...classes[0], subclass: value };
      return { ...prev, subclass: value, classes };
    });
  };

  // date_of_change: 2026-02-26
  // rule_reference: Preserve structured objects (source/usage/etc.) by patching only name/description.
  const startStructuredEntryEdit = (field, index) => {
    if (viewOnly || previewMode) return;
    if (!Array.isArray(character?.[field])) return;
    if (!character[field][index]) return;
    setStructuredEntryEditor({ field, index, openedAt: Date.now() });
  };

  const cancelStructuredEntryEdit = () => {
    setStructuredEntryEditor(null);
  };

  const saveStructuredEntryEdit = () => {
    if (!structuredEntryEditor) return;
    const { field, index } = structuredEntryEditor;
    const nextName = String(structuredEntryNameRef.current?.value || '').trim();
    const nextDescription = String(structuredEntryDescriptionRef.current?.value || '').trim();
    const nextSource = String(structuredEntrySourceRef.current?.value || '').trim();
    const nextMaxUses = Math.max(0, parseInt(structuredEntryMaxUsesRef.current?.value, 10) || 0);
    const nextCurrentUses = Math.max(0, parseInt(structuredEntryCurrentUsesRef.current?.value, 10) || 0);
    const nextResetOn = String(structuredEntryResetRef.current?.value || '');

    const patchEntry = (oldEntry) => {
      const existingSource = oldEntry?.source && typeof oldEntry.source === 'object'
        ? oldEntry.source
        : null;
      const shouldKeepUses = nextMaxUses > 0 || nextCurrentUses > 0 || Boolean(nextResetOn);
      const nextEntry = {
        ...oldEntry,
        name: nextName || oldEntry?.name || '',
        description: nextDescription,
        source: nextSource
          ? (existingSource ? { ...existingSource, name: nextSource } : nextSource)
          : undefined,
        uses: shouldKeepUses
          ? {
              ...(oldEntry?.uses && typeof oldEntry.uses === 'object' ? oldEntry.uses : {}),
              max: nextMaxUses,
              current: Math.min(nextMaxUses || nextCurrentUses, nextCurrentUses),
              ...(nextResetOn ? { resetOn: nextResetOn } : {})
            }
          : undefined
      };

      if (!nextEntry.source) delete nextEntry.source;
      if (!nextEntry.uses) delete nextEntry.uses;
      return nextEntry;
    };

    setCharacter((prev) => {
      if (!prev) return prev;

      // date_of_change: 2026-03-06 - Handle action economy arrays which may be nested
      let list, oldEntry, updatedCharacter;
      
      if (field === 'actions') {
        list = Array.isArray(prev?.actions?.actions)
          ? [...prev.actions.actions]
          : (Array.isArray(prev?.actions) ? [...prev.actions] : []);
        oldEntry = list[index];
        if (!oldEntry) return prev;
        list[index] = patchEntry(oldEntry);
        // Preserve nested structure if it exists
        if (Array.isArray(prev?.actions?.actions)) {
          updatedCharacter = {
            ...prev,
            actions: { ...prev.actions, actions: list }
          };
        } else {
          updatedCharacter = { ...prev, actions: list };
        }
      } else if (field === 'bonusActions') {
        list = Array.isArray(prev?.bonusActions)
          ? [...prev.bonusActions]
          : (Array.isArray(prev?.actions?.bonusActions) ? [...prev.actions.bonusActions] : []);
        oldEntry = list[index];
        if (!oldEntry) return prev;
        list[index] = patchEntry(oldEntry);
        updatedCharacter = Array.isArray(prev?.bonusActions)
          ? { ...prev, bonusActions: list }
          : { ...prev, actions: { ...prev.actions, bonusActions: list } };
      } else if (field === 'reactions') {
        list = Array.isArray(prev?.reactions)
          ? [...prev.reactions]
          : (Array.isArray(prev?.actions?.reactions) ? [...prev.actions.reactions] : []);
        oldEntry = list[index];
        if (!oldEntry) return prev;
        list[index] = patchEntry(oldEntry);
        updatedCharacter = Array.isArray(prev?.reactions)
          ? { ...prev, reactions: list }
          : { ...prev, actions: { ...prev.actions, reactions: list } };
      } else {
        // Standard structured arrays (features, speciesTraits, feats)
        list = Array.isArray(prev?.[field]) ? [...prev[field]] : [];
        oldEntry = list[index];
        if (!oldEntry) return prev;
        list[index] = patchEntry(oldEntry);
        updatedCharacter = { ...prev, [field]: list };
      }

      const entryName = nextName || oldEntry?.name || `Index ${index}`;
      const nextAudit = appendAuditDecision(
        prev?._audit,
        `CharacterSheetEditor.structuredEntryEdit(${field}): ${entryName}`,
        'structured_entry_update'
      );

      return hydrateCharacterCombatState({
        ...updatedCharacter,
        _audit: nextAudit
      });
    });

    setStatusMessage('✅ Updated entry');
    setStructuredEntryEditor(null);
  };

  const removeStructuredEntry = () => {
    if (!structuredEntryEditor) return;
    const { field, index } = structuredEntryEditor;
    setCharacter((prev) => {
      if (!prev) return prev;
      const removeAt = (list) => (Array.isArray(list)
        ? list.filter((_, entryIndex) => entryIndex !== index)
        : null);
      let updatedCharacter = null;
      if (field === 'actions' && Array.isArray(prev?.actions?.actions)) {
        updatedCharacter = { ...prev, actions: { ...prev.actions, actions: removeAt(prev.actions.actions) } };
      } else if (field === 'bonusActions' && !Array.isArray(prev?.bonusActions) && Array.isArray(prev?.actions?.bonusActions)) {
        updatedCharacter = { ...prev, actions: { ...prev.actions, bonusActions: removeAt(prev.actions.bonusActions) } };
      } else if (field === 'reactions' && !Array.isArray(prev?.reactions) && Array.isArray(prev?.actions?.reactions)) {
        updatedCharacter = { ...prev, actions: { ...prev.actions, reactions: removeAt(prev.actions.reactions) } };
      } else if (Array.isArray(prev?.[field])) {
        updatedCharacter = { ...prev, [field]: removeAt(prev[field]) };
      }
      if (!updatedCharacter) return prev;
      return hydrateCharacterCombatState({
        ...updatedCharacter,
        _audit: appendAuditDecision(prev?._audit, `CharacterSheetEditor.removeStructuredEntry(${field}:${index})`, 'structured_entry_remove')
      });
    });
    setStructuredEntryEditor(null);
  };

  // @date_of_change: 2025-11-14 - Artwork handlers (SSOT tokenAssetKey + artwork)
  const handleImageUploaded = (canonicalKey) => {
    updateCharacter('tokenAssetKey', canonicalKey);
    updateCharacter('artwork', canonicalKey);
    setStatusMessage('✅ Artwork updated');
  };

  const handleImageSelected = (imageKey) => {
    updateCharacter('tokenAssetKey', imageKey);
    updateCharacter('artwork', imageKey);
    setShowImageBrowser(false);
    setStatusMessage('✅ Artwork selected');
  };

  const handleUploadButton = () => {
    // date_of_change: 2026-04-22 - Anonymous visitors on /character/edit/new can edit locally,
    // but uploading a portrait hits authenticated S3. Route them through Clerk sign-in.
    if (!isSignedIn) {
      promptSignIn();
      return;
    }
    if (atAssetCap) {
      setStatusMessage(`⚠️ Free vault full. Clear a slot or upgrade to ${PREMIUM_PLAN_NAME}.`);
      return;
    }
    if (fileInputRef.current) fileInputRef.current.click();
  };

  // date_of_change: 2026-04-22 - Choose Existing opens the ImageBrowser, which lists the
  // signed-in user's asset vault. Soft-gate for anonymous visitors.
  const handleChooseExistingButton = () => {
    if (!isSignedIn) {
      promptSignIn();
      return;
    }
    setShowImageBrowser(true);
  };

  // date_of_change: 2026-04-22 - Dashboard is behind RequireAuth; anonymous visitors get routed
  // through Clerk sign-in with a return URL so they come back to the editor after signing in.
  const handleDashboardButton = () => {
    if (!isSignedIn) {
      promptSignIn();
      return;
    }
    navigate('/');
  };

  const requireSignedInHeaderAction = (action) => {
    // date_of_change: 2026-04-23 - Public editor visitors may view the sheet,
    // but every top-menu action should require sign-in before it can run.
    if (!isSignedIn) {
      promptSignIn();
      return;
    }
    action();
  };

  const handlePrintButton = async () => {
    // date_of_change: 2026-08-02
    // rule_reference: Printing must project the current canonical editor state into a static
    // document so form controls, open drawers, and responsive UI state cannot alter the record.
    const snapshot = buildCharacterPrintModel(
      { ...character, name: customName || character?.name },
      {
        name: customName || character?.name,
        portraitUrl: characterArtUrl,
        spellData: resolvedSpellData,
        actionEconomyViewModel
      }
    );
    setPrintSnapshot(snapshot);

    try {
      await waitForCharacterPrintPaint();
      await waitForCharacterPrintAssets(document.getElementById('character-print-root'));
      await waitForCharacterPrintPaint();
      window.print();
    } finally {
      setPrintSnapshot(null);
    }
  };

  const handleSmartPasteButton = () => {
    requireSignedInHeaderAction(() => setShowSmartPasteModal(true));
  };

  const handlePreviewButton = () => {
    // date_of_change: 2026-04-25 - Preview is now available to anonymous users
    setPreviewMode(prev => !prev);
  };

  const handleGenerateCharacterButton = () => {
    try {
      const generatedCharacter = generateStarterCharacter();
      setCharacter(generatedCharacter);
      setCustomName(generatedCharacter?.name || '');
      setCharacterCategory('hero');
      setPreviewMode(false);
      setStatusMessage(`🎲 Generated ${generatedCharacter?.name || 'a starter character'} for quick editing.`);
    } catch (error) {
      console.error('[CharacterSheetEditor] Starter character generation failed:', error);
      setStatusMessage(`❌ Could not generate a starter character: ${error.message}`);
    }
  };

  const handleEditCharacterButton = () => {
    requireSignedInHeaderAction(() => navigate(`/character/edit/${character?.id || templateId}`));
  };

  const onFileChosen = async (e) => {
    try {
      const file = e.target.files?.[0];
      if (!file) return;
      const key = await ImageUploadService.uploadImage(file, (customName || character?.name || 'character'), 'character');
      handleImageUploaded(key);
      setAssetUsage(await getAssetUsageSnapshot());
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err) {
      setStatusMessage(`❌ Upload failed: ${err.message}`);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const abilityModNumber = (score) => {
    const s = typeof score === 'number' ? score : 10;
    return Math.floor((s - 10) / 2);
  };

  const toNumber = (value) => {
    if (value === null || value === undefined) return null;
    if (typeof value === 'number') return Number.isNaN(value) ? null : value;
    const parsed = parseInt(value, 10);
    return Number.isNaN(parsed) ? null : parsed;
  };

  const normalizeEquipment = (incoming, existing) => {
    if (!incoming) {
      if (!existing) {
        return { weapons: [], armor: [], items: [], valuables: [] };
      }
      return {
        weapons: [...(existing.weapons || [])],
        armor: [...(existing.armor || [])],
        items: [...(existing.items || [])],
        valuables: [...(existing.valuables || [])]
      };
    }
    if (Array.isArray(incoming)) {
      const buckets = { weapons: [], armor: [], items: [], valuables: [] };
      incoming.forEach((entry) => {
        if (!entry) return;
        if (!isAmmunitionEntry(entry) && isExplicitlyEquippedWeapon(entry, 'equipment')) {
          buckets.weapons.push(entry);
        } else if (entry.type === 'armor' || entry.type === 'shield') {
          buckets.armor.push(entry);
        } else if (entry.type === 'valuable') {
          buckets.valuables.push(entry);
        } else {
          buckets.items.push(entry);
        }
      });
      return buckets;
    }
    return {
      weapons: [...(incoming.weapons || existing?.weapons || [])],
      armor: [...(incoming.armor || existing?.armor || [])],
      items: [...(incoming.items || existing?.items || [])],
      valuables: [...(incoming.valuables || existing?.valuables || [])]
    };
  };

  const normalizeSpells = (incoming, existing) => {
    const baseline = existing || { spells: [], prepared: [], known: [], atWill: [], byLevel: {}, slots: {} };
    if (!incoming) {
      return {
        spells: [...(baseline.spells || [])],
        prepared: [...(baseline.prepared || [])],
        known: [...(baseline.known || [])],
        atWill: [...(baseline.atWill || [])],
        byLevel: { ...(baseline.byLevel || {}) },
        slots: { ...(baseline.slots || {}) }
      };
    }
    if (Array.isArray(incoming)) {
      return {
        spells: [...incoming],
        prepared: [...(baseline.prepared || [])],
        known: [...(baseline.known || [])],
        atWill: [...(baseline.atWill || [])],
        byLevel: { ...(baseline.byLevel || {}) },
        slots: { ...(baseline.slots || {}) }
      };
    }
    return {
      spells: [...(incoming.spells || baseline.spells || [])],
      prepared: [...(incoming.prepared || baseline.prepared || [])],
      known: [...(incoming.known || baseline.known || [])],
      atWill: [...(incoming.atWill || baseline.atWill || [])],
      byLevel: { ...(incoming.byLevel || baseline.byLevel || {}) },
      slots: { ...(incoming.slots || baseline.slots || {}) }
    };
  };

  const normalizeAbilities = (incoming, existing) => {
    if (!incoming) return existing || {};
    const result = { ...(existing || {}) };
    Object.entries(incoming).forEach(([key, value]) => {
      const normalized = toNumber(value);
      result[key] = normalized !== null ? normalized : value;
    });
    return result;
  };

  // GAP-007 bug #7: preserve full SpeedModes object so fly/swim/climb/burrow speeds are not
  // dropped when applying a SmartPaste suggestion or suggestion-review suggestion.
  // rule_reference: D&D 5e 2024 — creatures may have walk, fly, swim, climb, and burrow speeds.
  // date_of_change: 2026-05-20
  const normalizeSpeed = (incoming, existing) => {
    if (incoming === null || incoming === undefined) return existing ?? null;
    // Scalar number — treat as walk speed object for consistency with SpeedModes contract
    if (typeof incoming === 'number') {
      return Number.isFinite(incoming) ? { walk: incoming } : (existing ?? null);
    }
    if (typeof incoming === 'string') {
      const parsed = toNumber(incoming);
      return parsed !== null ? { walk: parsed } : (existing ?? null);
    }
    if (typeof incoming === 'object' && !Array.isArray(incoming)) {
      // Build a clean SpeedModes object, normalising all numeric entries
      const modes = {};
      for (const [mode, val] of Object.entries(incoming)) {
        const n = toNumber(val);
        if (n !== null) modes[mode.toLowerCase()] = n;
      }
      if (Object.keys(modes).length > 0) return modes;
    }
    return existing ?? null;
  };

  const normalizeAc = (incoming, existing) => {
    if (incoming === null || incoming === undefined) return existing ?? null;
    if (typeof incoming === 'number') return incoming;
    if (typeof incoming === 'object') {
      const value = toNumber(incoming.value);
      if (value !== null) return value;
    }
    const numeric = toNumber(incoming);
    return numeric !== null ? numeric : existing ?? null;
  };

  const normalizeHpValues = (incoming, existing) => {
    const seed = existing || {};
    let current = seed.hp ?? seed.current ?? null;
    let max = seed.maxHp ?? seed.maximum ?? null;
    const assign = (value) => {
      const parsed = toNumber(value);
      if (parsed !== null) {
        if (max === null) max = parsed;
        if (current === null) current = parsed;
      }
    };
    if (incoming !== null && incoming !== undefined) {
      if (typeof incoming === 'number' || typeof incoming === 'string') {
        const parsed = toNumber(incoming);
        if (parsed !== null) {
          current = parsed;
          if (max === null) max = parsed;
        }
      } else if (typeof incoming === 'object') {
        assign(incoming.current);
        assign(incoming.maximum);
        assign(incoming.average);
        assign(incoming.value);
      }
    }
    return { current, max };
  };

  const calculateModifier = (score) => {
    const mod = Math.floor((score - 10) / 2);
    return mod >= 0 ? `+${mod}` : `${mod}`;
  };

  // date_of_change: 2026-02-26
  // rule_reference: D&D 5e — UI must display signed bonuses (e.g., +5) for saves/skills parsed from stat blocks.
  const formatSignedNumber = (n) => {
    if (typeof n !== 'number' || !Number.isFinite(n)) return '';
    return n >= 0 ? `+${n}` : `${n}`;
  };

  const closeSmartPasteModal = () => {
    setShowSmartPasteModal(false);
  };

  const handleSmartPasteContent = (parsedContent, suggestions) => {
    console.log('[CharacterSheetEditor] Smart paste content parsed:', parsedContent);
  };

  const applyFullCharacterImport = (data, baseCharacterOverride = null) => {
    // @date_of_change: 2025-10-12 - Map Smart Paste payload into canonical character node (rule_reference: PHB 2024 Character Creation overview)
    if (!data) {
      return {
        message: '⚠️ SmartPaste could not find any character details to add.',
        nextCharacter: baseCharacterOverride || character || createBlankCharacter()
      };
    }
    const baseCharacter = baseCharacterOverride || character || createBlankCharacter();
    const mergedCharacter = buildSmartPasteCharacterImport(baseCharacter, data);
    const readiness = assessCharacterCombatReadiness(mergedCharacter);
    const readinessSummary = summarizeCombatReadinessIssues(readiness, 2);

    setCharacter(mergedCharacter);
    if (mergedCharacter.name) {
      setCustomName(mergedCharacter.name);
    }

    return {
      message: readinessSummary
        ? `✅ Added character details — ⚠️ Check before combat: ${readinessSummary}`
        : '✅ Added character details',
      nextCharacter: mergedCharacter
    };
  };

  // date_of_change: 2025-10-19
  // rule_reference: AGENTS.md — Discrete handlers must validate before state mutation; zero transformation beyond ingest
  const applyAttributesFromRecord = async (changeRecord, baseCharacterOverride = null) => {
    const { field, action, data } = changeRecord;

    const workingBase = baseCharacterOverride || character || createBlankCharacter();
    let nextCharacter = workingBase;
    const setOrUpdate = (key, value, mergedCharacterOverride = null) => {
      const merged = mergedCharacterOverride || { ...nextCharacter, [key]: value };
      const hydrated = hydrateCharacterCombatState(merged);
      nextCharacter = hydrated;
      setCharacter(hydrated);
      return hydrated;
    };

    switch (field) {
      case 'spells': {
        if (action === 'append') {
          const existing = normalizeSpells(null, workingBase?.spells);
          const appended = Array.isArray(data) ? data : [data];
          const next = { ...existing };
          next.known = Array.isArray(next.known) ? [...next.known] : [];
          next.prepared = Array.isArray(next.prepared) ? [...next.prepared] : [];
          next.byLevel = { ...(next.byLevel || {}) };

          appended.forEach((spell) => {
            const sLevel = typeof spell?.level === 'number' ? spell.level : null;
            if (sLevel !== null && sLevel >= 0) {
              const arr = Array.isArray(next.byLevel[sLevel]) ? [...next.byLevel[sLevel]] : [];
              arr.push(spell);
              next.byLevel[sLevel] = arr;
            } else {
              next.known.push(spell);
            }
          });

          // Validate with discrete handler prior to mutation
          try {
            const { SpellHandler } = await import('../handlers/SpellHandler');
            const levelForValidation = Number.isFinite(workingBase?.level) ? Number(workingBase.level) : 1;
            const validation = await SpellHandler.validate(next, levelForValidation);
            if (!validation.valid) {
              return { message: `❌ This spell could not be added: ${validation.errors[0] || 'the spell details need another look'}`, nextCharacter };
            }
            if (validation.normalizedSpells) {
              setOrUpdate('spells', validation.normalizedSpells);
            } else {
              setOrUpdate('spells', next);
            }
            const warn = (validation.warnings || []).join(' ');
            return {
              message: warn
                ? `✅ Added spell: ${appended.map((e) => e?.name || 'Spell').join(', ')} — ⚠️ Check this: ${warn}`
                : `✅ Added spell: ${appended.map((e) => e?.name || 'Spell').join(', ')}`,
              nextCharacter
            };
          } catch (e) {
            console.warn('[CharacterSheetEditor] SpellHandler validation failed at apply-time:', e);
            setOrUpdate('spells', next);
            return {
              message: `✅ Added spell without an automatic rules check: ${appended.map((entry) => entry?.name || 'Spell').join(', ')}`,
              nextCharacter
            };
          }
        }
        if (action === 'replace') {
          const normalized = normalizeSpells(data, workingBase?.spells);
          try {
            const { SpellHandler } = await import('../handlers/SpellHandler');
            const levelForValidation = Number.isFinite(workingBase?.level) ? Number(workingBase.level) : 1;
            const validation = await SpellHandler.validate(normalized, levelForValidation);
            if (!validation.valid) {
              return { message: `❌ These spells could not be added: ${validation.errors[0] || 'the spell details need another look'}`, nextCharacter };
            }
            setOrUpdate('spells', validation.normalizedSpells || normalized);
            const warn = (validation.warnings || []).join(' ');
            return { message: warn ? `✅ Updated spells — ⚠️ Check this: ${warn}` : '✅ Updated spells', nextCharacter };
          } catch (e) {
            console.warn('[CharacterSheetEditor] SpellHandler validation failed at replace:', e);
            setOrUpdate('spells', normalized);
            return { message: '✅ Updated spells without an automatic rules check', nextCharacter };
          }
        }
        break;
      }
      case 'equipment': {
        if (action === 'append') {
          const incoming = Array.isArray(data) ? data : [data];
          const merged = mergeSmartPasteEquipment(workingBase?.equipment, incoming);
          try {
            const { EquipmentHandler } = await import('../handlers/EquipmentHandler');
            const validation = await EquipmentHandler.validate(merged);
            if (!validation.valid) {
              return { message: `❌ This equipment could not be added: ${validation.errors[0] || 'the equipment details need another look'}`, nextCharacter };
            }
            setOrUpdate('equipment', validation.normalizedEquipment || merged);
            const warn = (validation.warnings || []).join(' ');
            return {
              message: warn
                ? `✅ Added equipment: ${incoming.map((e) => e?.name || 'Item').join(', ')} — ⚠️ Check this: ${warn}`
                : `✅ Added equipment: ${incoming.map((e) => e?.name || 'Item').join(', ')}`,
              nextCharacter
            };
          } catch (e) {
            console.warn('[CharacterSheetEditor] EquipmentHandler validation failed at apply-time:', e);
            setOrUpdate('equipment', merged);
            return {
              message: `✅ Added equipment without an automatic rules check: ${incoming.map((entry) => entry?.name || 'Item').join(', ')}`,
              nextCharacter
            };
          }
        }
        if (action === 'replace') {
          const normalized = stampSmartPasteEquipment(normalizeEquipment(data, workingBase?.equipment));
          try {
            const { EquipmentHandler } = await import('../handlers/EquipmentHandler');
            const validation = await EquipmentHandler.validate(normalized);
            if (!validation.valid) {
            return { message: `❌ This equipment could not be added: ${validation.errors[0] || 'the equipment details need another look'}`, nextCharacter };
            }
            setOrUpdate('equipment', validation.normalizedEquipment || normalized);
            const warn = (validation.warnings || []).join(' ');
            return { message: warn ? `✅ Updated equipment — ⚠️ Check this: ${warn}` : '✅ Updated equipment', nextCharacter };
          } catch (e) {
            console.warn('[CharacterSheetEditor] EquipmentHandler validation failed at replace:', e);
            setOrUpdate('equipment', normalized);
            return { message: '✅ Updated equipment without an automatic rules check', nextCharacter };
          }
        }
        break;
      }
      case 'name': {
        const value = typeof data === 'string' ? data : String(data || '');
        setOrUpdate('name', value);
        setCustomName(value);
        return { message: `✅ Set name: ${value}`, nextCharacter };
      }
      case 'class': {
        const value = typeof data === 'string' ? data : String(data || '');
        setOrUpdate('class', value);
        return { message: `✅ Set class: ${value}`, nextCharacter };
      }
      case 'classes': {
        // date_of_change: 2026-08-24
        // rule_reference: D&D 5e multiclass progression must be applied atomically so the
        // primary class, subclass, and total level cannot drift from the classes array.
        const classes = (Array.isArray(data) ? data : [])
          .map((entry) => ({
            ...entry,
            name: String(entry?.name || entry?.className || entry?.class || '').trim(),
            level: toNumber(entry?.level) ?? 0,
            subclass: entry?.subclass || entry?.archetype || entry?.path || entry?.domain || ''
          }))
          .filter((entry) => entry.name && entry.level > 0);
        if (!classes.length) {
          return { message: '⚠️ No usable class progression was found.', nextCharacter };
        }
        const primary = classes[0];
        const totalLevel = classes.reduce((sum, entry) => sum + entry.level, 0);
        const merged = hydrateCharacterCombatState({
          ...workingBase,
          classes,
          class: primary.name,
          subclass: primary.subclass || '',
          level: totalLevel
        });
        setOrUpdate('classes', classes, merged);
        return { message: `✅ Set ${classes.length} classes (${totalLevel} total levels)`, nextCharacter };
      }
      case 'level': {
        const n = toNumber(data) ?? 1;
        const merged = hydrateCharacterCombatState({
          ...workingBase,
          level: n,
          proficiencyBonus: workingBase.proficiencyBonus ?? (Math.floor((n - 1) / 4) + 2)
        });
        nextCharacter = merged;
        setCharacter(merged);
        return { message: `✅ Set level: ${n}`, nextCharacter };
      }
      case 'background': {
        const value = typeof data === 'string' ? data : String(data || '');
        setOrUpdate('background', value);
        return { message: `✅ Set background: ${value}`, nextCharacter };
      }
      case 'race':
      case 'species': {
        const value = typeof data === 'string' ? data : String(data || '');
        // Keep race/species in sync
        const merged = hydrateCharacterCombatState({ ...workingBase, race: value, species: value });
        nextCharacter = merged;
        setCharacter(merged);
        return { message: `✅ Set species: ${value}`, nextCharacter };
      }
      case 'ac': {
        const acVal = normalizeAc(data, workingBase?.ac ?? null) ?? 10;
        setOrUpdate('ac', acVal);
        return { message: `✅ Set AC: ${acVal}`, nextCharacter };
      }
      case 'hp': {
        const numeric = toNumber(data);
        const hpEnv = numeric !== null
          ? { current: numeric, max: numeric }
          : normalizeHpValues(data, workingBase);
        const merged = hydrateCharacterCombatState({
          ...workingBase,
          hp: hpEnv.current ?? numeric ?? 1,
          maxHp: hpEnv.max ?? numeric ?? 1
        });
        nextCharacter = merged;
        setCharacter(merged);
        return { message: `✅ Set HP: ${hpEnv.current ?? numeric}`, nextCharacter };
      }
      case 'speed': {
        const spd = normalizeSpeed(data, workingBase?.speed ?? null) ?? 30;
        setOrUpdate('speed', spd);
        const speedSummary = typeof spd === 'object' ? (spd.walk ?? Object.values(spd)[0]) : spd;
        return { message: `✅ Set speed: ${speedSummary}`, nextCharacter };
      }
      case 'currency': {
        const equipment = workingBase?.equipment && typeof workingBase.equipment === 'object' && !Array.isArray(workingBase.equipment)
          ? workingBase.equipment
          : {};
        const incomingCurrency = data && typeof data === 'object' && !Array.isArray(data) ? data : {};
        setOrUpdate('equipment', {
          ...equipment,
          currency: {
            ...(action === 'replace' ? {} : (equipment.currency || {})),
            ...incomingCurrency
          }
        });
        return { message: '✅ Set coins', nextCharacter };
      }
      case 'proficiencyBonus': {
        const pb = toNumber(data) ?? workingBase?.proficiencyBonus ?? 2;
        setOrUpdate('proficiencyBonus', pb);
        return { message: `✅ Set proficiency bonus: +${pb}`, nextCharacter };
      }
      case 'initiative': {
        const n = toNumber(data) ?? 0;
        setOrUpdate('initiative', n);
        return { message: `✅ Set initiative: ${n}`, nextCharacter };
      }
      case 'passivePerception': {
        const n = toNumber(data) ?? 10;
        setOrUpdate('passivePerception', n);
        return { message: `✅ Set Passive Perception: ${n}`, nextCharacter };
      }
      case 'features': {
        const additions = Array.isArray(data) ? data : [data];
        const merged = mergeSmartPasteEntryList(workingBase?.features, additions, 'features');
        setOrUpdate('features', merged);
        return { message: `✅ Added feature: ${additions.map((entry) => entry?.name || 'Feature').join(', ')}`, nextCharacter };
      }
      case 'feats': {
        const additions = Array.isArray(data) ? data : [data];
        const merged = mergeSmartPasteEntryList(workingBase?.feats, additions, 'feats');
        setOrUpdate('feats', merged);
        return { message: `✅ Added feat: ${additions.map((entry) => entry?.name || 'Feat').join(', ')}`, nextCharacter };
      }
      // date_of_change: 2026-02-28
      // rule_reference: D&D 5e (2024) — apply structured class features array from SmartPaste
      case 'classFeatures': {
        const additions = Array.isArray(data) ? data : [data];
        const merged = mergeSmartPasteEntryList(workingBase?.classFeatures, additions, 'classFeatures');
        setOrUpdate('classFeatures', merged);
        return { message: `✅ Added class feature: ${additions.map((entry) => entry?.name || 'Class Feature').join(', ')}`, nextCharacter };
      }
      case 'classFeaturesText': {
        const value = typeof data === 'string' ? data : String(data || '');
        setOrUpdate('classFeaturesText', value);
        return { message: '✅ Set Class Features text', nextCharacter };
      }
      case 'speciesTraitsText': {
        const value = typeof data === 'string' ? data : String(data || '');
        setOrUpdate('speciesTraitsText', value);
        return { message: '✅ Set Species Traits text', nextCharacter };
      }
      // date_of_change: 2026-02-26
      // rule_reference: D&D 5e (2024) SRD — apply structured species traits array
      case 'speciesTraits': {
        const additions = Array.isArray(data) ? data : [data];
        const merged = mergeSmartPasteEntryList(workingBase?.speciesTraits, additions, 'speciesTraits');
        setOrUpdate('speciesTraits', merged);
        return { message: `✅ Added ${additions.length} species traits: ${additions.map(t => t?.name || 'Trait').join(', ')}`, nextCharacter };
      }
      // date_of_change: 2026-02-26
      // rule_reference: D&D 5e (2024) PHB — apply SRD class proficiencies (armor, weapons, tools)
      case 'proficiencies': {
        const baseProficiencies = workingBase?.proficiencies || {};
        const merged = {
          ...baseProficiencies,
          armor: data?.armor || baseProficiencies.armor || [],
          weapons: data?.weapons || baseProficiencies.weapons || [],
          tools: data?.tools || baseProficiencies.tools || [],
        };
        setOrUpdate('proficiencies', merged);
        return {
          message: `✅ Set proficiencies (armor: ${merged.armor.length}, weapons: ${merged.weapons.length}, tools: ${merged.tools.length})`,
          nextCharacter
        };
      }
      case 'featsText': {
        const value = typeof data === 'string' ? data : String(data || '');
        setOrUpdate('featsText', value);
        return { message: '✅ Set Feats text', nextCharacter };
      }
      case 'actions': {
        // date_of_change: 2026-09-06
        // rule_reference: Reviewed action imports still require a final boundary check so a
        // flattened character sheet cannot become an executable combat option.
        const additions = (Array.isArray(data) ? data : [data])
          .filter((entry) => !IntelligentContentParser.isCharacterSheetMetadataDump(entry));
        if (!additions.length) {
          return {
            message: 'That pasted character-data block was not added as a combat action',
            nextCharacter: null,
            rejected: true
          };
        }
        const merged = mergeSmartPasteEntryList(workingBase?.actions, additions, 'actions');
        setOrUpdate('actions', merged);
        return { message: `✅ Added action: ${additions.map((entry) => entry?.name || 'Action').join(', ')}`, nextCharacter };
      }
      case 'bonusActions': {
        const additions = (Array.isArray(data) ? data : [data])
          .filter((entry) => !IntelligentContentParser.isCharacterSheetMetadataDump(entry));
        if (!additions.length) {
          return {
            message: 'That pasted character-data block was not added as a bonus action',
            nextCharacter: null,
            rejected: true
          };
        }
        const merged = mergeSmartPasteEntryList(workingBase?.bonusActions, additions, 'bonusActions');
        setOrUpdate('bonusActions', merged);
        return { message: `✅ Added bonus action: ${additions.map((entry) => entry?.name || 'Bonus Action').join(', ')}`, nextCharacter };
      }
      case 'reactions': {
        const additions = (Array.isArray(data) ? data : [data])
          .filter((entry) => !IntelligentContentParser.isCharacterSheetMetadataDump(entry));
        if (!additions.length) {
          return {
            message: 'That pasted character-data block was not added as a reaction',
            nextCharacter: null,
            rejected: true
          };
        }
        const merged = mergeSmartPasteEntryList(workingBase?.reactions, additions, 'reactions');
        setOrUpdate('reactions', merged);
        return { message: `✅ Added reaction: ${additions.map((entry) => entry?.name || 'Reaction').join(', ')}`, nextCharacter };
      }
      case 'abilities': {
        const normalized = normalizeAbilities(data, workingBase?.abilities);
        setOrUpdate('abilities', normalized);
        return { message: '✅ Updated ability scores', nextCharacter };
      }
      case 'skills': {
        // @date_of_change: 2025-10-18 - Canonicalize skills to structured map with computed values
        // @date_of_change: 2025-12-05 - Enhanced to support skillsDetailed format with proficiency/expertise info
        // rule_reference: CanonicalSchemas.ts expects structured skills; Editor checkboxes are not yet persisted
        const incomingList = Array.isArray(data) ? data : [data];
        const current = (typeof workingBase?.skills === 'object' && !Array.isArray(workingBase.skills))
          ? { ...workingBase.skills }
          : {};

        const skillAbilityMap = {
          Athletics: 'strength',
          Acrobatics: 'dexterity',
          'Sleight of Hand': 'dexterity',
          Stealth: 'dexterity',
          Arcana: 'intelligence',
          History: 'intelligence',
          Investigation: 'intelligence',
          Nature: 'intelligence',
          Religion: 'intelligence',
          'Animal Handling': 'wisdom',
          Insight: 'wisdom',
          Medicine: 'wisdom',
          Perception: 'wisdom',
          Survival: 'wisdom',
          Deception: 'charisma',
          Intimidation: 'charisma',
          Performance: 'charisma',
          Persuasion: 'charisma'
        };

        const abilities = workingBase?.abilities || {};
        const level = workingBase?.level || 1;
        const pb = typeof workingBase?.proficiencyBonus === 'number' ? workingBase.proficiencyBonus : (Math.ceil(level / 4) + 1);

        incomingList.forEach((item) => {
          if (!item) return;
          // Support both string (skill name) and detailed object { name, proficient, expertise, bonus }
          const isDetailed = typeof item === 'object' && item.name;
          const name = isDetailed ? item.name : String(item);
          const abilKey = skillAbilityMap[name] || 'wisdom';
          const mod = abilityModNumber(abilities?.[abilKey] ?? 10);
          const hasExpertise = isDetailed && item.expertise === true;
          const hasProficiency = isDetailed ? (item.proficient !== false) : true;
          // Use provided bonus if available, otherwise calculate from ability + PB + expertise
          const expertiseBonus = hasExpertise ? pb : 0;
          const proficiencyBonus = hasProficiency ? pb : 0;
          const computedValue = isDetailed && typeof item.bonus === 'number' 
            ? item.bonus 
            : (mod + proficiencyBonus + expertiseBonus);
          current[name] = {
            proficient: hasProficiency,
            expertise: hasExpertise,
            value: computedValue
          };
        });

        setOrUpdate('skills', current);
        return { message: `✅ Updated skills (${incomingList.length})`, nextCharacter };
      }
      case 'senses': {
        const incoming = (typeof data === 'object' && data) ? data : {};
        const merged = { ...(workingBase?.senses || {}), ...incoming };
        setOrUpdate('senses', merged);
        return { message: '✅ Set senses', nextCharacter };
      }
      case 'resistances': {
        const base = Array.isArray(workingBase?.resistances) ? [...workingBase.resistances] : [];
        const additions = Array.isArray(data) ? data : [data];
        setOrUpdate('resistances', [...base, ...additions]);
        return { message: `✅ Added resistances: ${additions.join(', ')}`, nextCharacter };
      }
      case 'immunities': {
        const base = Array.isArray(workingBase?.immunities) ? [...workingBase.immunities] : [];
        const additions = Array.isArray(data) ? data : [data];
        setOrUpdate('immunities', [...base, ...additions]);
        return { message: `✅ Added immunities: ${additions.join(', ')}`, nextCharacter };
      }
      case 'vulnerabilities': {
        const base = Array.isArray(workingBase?.vulnerabilities) ? [...workingBase.vulnerabilities] : [];
        const additions = Array.isArray(data) ? data : [data];
        setOrUpdate('vulnerabilities', [...base, ...additions]);
        return { message: `✅ Added vulnerabilities: ${additions.join(', ')}`, nextCharacter };
      }
      case 'conditionImmunities': {
        const base = Array.isArray(workingBase?.conditionImmunities) ? [...workingBase.conditionImmunities] : [];
        const additions = Array.isArray(data) ? data : [data];
        setOrUpdate('conditionImmunities', [...base, ...additions]);
        return { message: `✅ Added condition immunities: ${additions.join(', ')}`, nextCharacter };
      }
      case 'alignment': {
        const value = typeof data === 'string' ? data : String(data || '');
        setOrUpdate('alignment', value);
        return { message: `✅ Set alignment: ${value}`, nextCharacter };
      }
      case 'languages': {
        const base = Array.isArray(workingBase?.languages) ? [...workingBase.languages] : [];
        const additions = Array.isArray(data) ? data : [data];
        setOrUpdate('languages', [...base, ...additions]);
        return { message: `✅ Added languages: ${additions.join(', ')}`, nextCharacter };
      }
      case 'toolProficiencies': {
        const base = Array.isArray(workingBase?.toolProficiencies) ? [...workingBase.toolProficiencies] : [];
        const additions = Array.isArray(data) ? data : [data];
        setOrUpdate('toolProficiencies', [...base, ...additions]);
        return { message: `✅ Added tool proficiencies: ${additions.join(', ')}`, nextCharacter };
      }
      case 'savingThrows': {
        const incoming = typeof data === 'object' && data ? data : {};
        setOrUpdate('savingThrows', incoming);
        return { message: '✅ Set saving throws', nextCharacter };
      }
      case 'spellcasting': {
        const merged = { ...(workingBase?.spellcasting || {}), ...(data || {}) };
        setOrUpdate('spellcasting', merged);
        return { message: '✅ Updated spellcasting stats', nextCharacter };
      }
      // @date_of_change 2025-12-05 - Add subclass support
      case 'subclass': {
        const value = typeof data === 'string' ? data : String(data || '');
        setOrUpdate('subclass', value);
        return { message: `✅ Set subclass: ${value}`, nextCharacter };
      }
      // date_of_change: 2026-05-02
      // rule_reference: Hit dice (e.g. "8d10+3d8") must be importable as a discrete field so the
      // character sheet header displays the correct formula rather than the blank default.
      case 'hitDice': {
        const value = typeof data === 'string' ? data : String(data || '');
        setOrUpdate('hitDice', value);
        return { message: `✅ Set Hit Dice: ${value}`, nextCharacter };
      }
      // @date_of_change 2025-12-05 - Add biography field handlers (personality, ideals, bonds, flaws, backstory, appearance, notes, physical attributes)
      case 'personality': {
        const bio = { ...(workingBase?.biography || {}) };
        bio.personality = typeof data === 'string' ? data : String(data || '');
        setOrUpdate('biography', bio);
        return { message: '✅ Set personality', nextCharacter };
      }
      case 'ideals': {
        const bio = { ...(workingBase?.biography || {}) };
        bio.ideals = typeof data === 'string' ? data : String(data || '');
        setOrUpdate('biography', bio);
        return { message: '✅ Set ideals', nextCharacter };
      }
      case 'bonds': {
        const bio = { ...(workingBase?.biography || {}) };
        bio.bonds = typeof data === 'string' ? data : String(data || '');
        setOrUpdate('biography', bio);
        return { message: '✅ Set bonds', nextCharacter };
      }
      case 'flaws': {
        const bio = { ...(workingBase?.biography || {}) };
        bio.flaws = typeof data === 'string' ? data : String(data || '');
        setOrUpdate('biography', bio);
        return { message: '✅ Set flaws', nextCharacter };
      }
      case 'backstory': {
        const bio = { ...(workingBase?.biography || {}) };
        bio.backstory = typeof data === 'string' ? data : String(data || '');
        setOrUpdate('biography', bio);
        return { message: '✅ Set backstory', nextCharacter };
      }
      case 'appearance': {
        const bio = { ...(workingBase?.biography || {}) };
        bio.appearance = typeof data === 'string' ? data : String(data || '');
        setOrUpdate('biography', bio);
        return { message: '✅ Set appearance', nextCharacter };
      }
      case 'notes': {
        const value = typeof data === 'string' ? data : String(data || '');
        setOrUpdate('notes', value);
        return { message: '✅ Set notes', nextCharacter };
      }
      case 'age': {
        const bio = { ...(workingBase?.biography || {}) };
        bio.age = typeof data === 'string' ? data : String(data || '');
        setOrUpdate('biography', bio);
        return { message: `✅ Set age: ${bio.age}`, nextCharacter };
      }
      case 'height': {
        const bio = { ...(workingBase?.biography || {}) };
        bio.height = typeof data === 'string' ? data : String(data || '');
        setOrUpdate('biography', bio);
        return { message: `✅ Set height: ${bio.height}`, nextCharacter };
      }
      case 'weight': {
        const bio = { ...(workingBase?.biography || {}) };
        bio.weight = typeof data === 'string' ? data : String(data || '');
        setOrUpdate('biography', bio);
        return { message: `✅ Set weight: ${bio.weight}`, nextCharacter };
      }
      case 'eyes': {
        const bio = { ...(workingBase?.biography || {}) };
        bio.eyes = typeof data === 'string' ? data : String(data || '');
        setOrUpdate('biography', bio);
        return { message: `✅ Set eyes: ${bio.eyes}`, nextCharacter };
      }
      case 'hair': {
        const bio = { ...(workingBase?.biography || {}) };
        bio.hair = typeof data === 'string' ? data : String(data || '');
        setOrUpdate('biography', bio);
        return { message: `✅ Set hair: ${bio.hair}`, nextCharacter };
      }
      case 'skin': {
        const bio = { ...(workingBase?.biography || {}) };
        bio.skin = typeof data === 'string' ? data : String(data || '');
        setOrUpdate('biography', bio);
        return { message: `✅ Set skin: ${bio.skin}`, nextCharacter };
      }
      // date_of_change: 2026-02-12
      // rule_reference: D&D 5e (2024) - New parsed fields for comprehensive NPC/monster data
      case 'legendaryActions': {
        setOrUpdate('legendaryActions', data);
        return { message: '✅ Set legendary actions', nextCharacter };
      }
      case 'mythicActions': {
        setOrUpdate('mythicActions', data);
        return { message: '✅ Set mythic actions', nextCharacter };
      }
      case 'lairActions': {
        setOrUpdate('lairActions', data);
        return { message: '✅ Set lair actions', nextCharacter };
      }
      case 'regionalEffects': {
        setOrUpdate('regionalEffects', data);
        return { message: '✅ Set regional effects', nextCharacter };
      }
      case 'minions': {
        setOrUpdate('minions', data);
        return { message: '✅ Set minions', nextCharacter };
      }
      case 'challengeRating': {
        setOrUpdate('challengeRating', data);
        return { message: '✅ Set challenge rating: ' + data, nextCharacter };
      }
      case 'experience': {
        setOrUpdate('experience', data);
        return { message: '✅ Set XP: ' + data, nextCharacter };
      }
      case 'xp': {
        setOrUpdate('xp', data);
        return { message: '✅ Set XP: ' + data, nextCharacter };
      }
      case 'title': {
        const value = typeof data === 'string' ? data : String(data || '');
        setOrUpdate('title', value);
        return { message: '✅ Set title: ' + value, nextCharacter };
      }
      case 'aliases': {
        setOrUpdate('aliases', Array.isArray(data) ? data : [data]);
        return { message: '✅ Set aliases', nextCharacter };
      }
      case 'multiclassFeatures': {
        setOrUpdate('multiclassFeatures', data);
        return { message: '✅ Set multiclass features', nextCharacter };
      }
      case 'all': {
        if (action === 'replace') {
          return applyFullCharacterImport(data, workingBase);
        }
        break;
      }
      default:
        console.warn('[CharacterSheetEditor] Unknown field in smart paste:', field);
        break;
    }

    return { message: null, nextCharacter };
  };

  const handleSmartPasteApply = async (changeRecordOrRecords, applyOptions = {}) => {
    const resolvedApplyOptions = changeRecordOrRecords?.[Symbol.for('vcs.smartPaste.applyOptions')] || applyOptions;
    // @date_of_change 2025-12-05 - Allow SmartPasteInterface to send multiple changeRecords in a single Apply
    const records = Array.isArray(changeRecordOrRecords)
      ? changeRecordOrRecords.filter(Boolean)
      : (changeRecordOrRecords ? [changeRecordOrRecords] : []);

    if (!records.length) {
      setStatusMessage('⚠️ No character details were added');
      setTimeout(() => setStatusMessage(''), 3000);
      return { ok: false, message: 'Choose at least one character detail to add.' };
    }

    // Guard: unifiedEntity imports must be applied by themselves (no mixed batch)
    const unifiedRecords = records.filter(r => r?.field === 'unifiedEntity');
    if (unifiedRecords.length > 0 && records.length > 1) {
      setStatusMessage('❌ Saving a new character cannot be combined with changes to this sheet. Choose one or the other.');
      setTimeout(() => setStatusMessage(''), 4000);
      return { ok: false, message: 'Save the new character separately, or add details to this sheet.' };
    }

    try {
      const udm = UnifiedDataManager;
      const messages = [];
      let workingCharacter = character || createBlankCharacter();
      let appliedLiveCharacter = false;
      let applyFailed = false;

      for (const changeRecord of records) {
        // UnifiedDataManager entity creation
        if (changeRecord?.field === 'unifiedEntity' && changeRecord?.data) {
          try {
            const readiness = assessCharacterCombatReadiness(changeRecord.data?.data || {});
            const readinessSummary = summarizeCombatReadinessIssues(readiness, 2);
            const unifiedEntity = {
              ...changeRecord.data,
              _meta: {
                ...(changeRecord.data._meta || {}),
                combatReadiness: buildCombatReadinessMeta(readiness, 'smart_paste_unified_import')
              }
            };
            await udm.saveEntity(unifiedEntity, true);
            messages.push(
              readinessSummary
                ? `✅ Saved as a new character — ⚠️ Check before combat: ${readinessSummary}`
                : '✅ Saved as a new character'
            );
          } catch (saveErr) {
            console.error('[CharacterSheetEditor] Unified save failed:', saveErr);
            messages.push(`❌ The new character could not be saved: ${saveErr.message}`);
            applyFailed = true;
          }
          continue;
        }

        const result = await applyAttributesFromRecord(changeRecord, workingCharacter);
        if (result?.rejected) {
          applyFailed = true;
        }
        if (result?.nextCharacter) {
          workingCharacter = result.nextCharacter;
          appliedLiveCharacter = true;
        }
        if (result?.message) {
          messages.push(result.message);
        }
      }

      if (appliedLiveCharacter && !records.some((record) => record?.field === 'all')) {
        const readiness = assessCharacterCombatReadiness(workingCharacter);

        const warningMessages = Array.from(new Set([
          ...(readiness.dataQualityWarnings || []),
        ]));
        if (warningMessages.length) {
          messages.push(`⚠️ Check before combat: ${warningMessages.join(' ')}`);
        }
      }

      if (messages.length) {
        setStatusMessage(messages.join(' | '));
      } else {
        setStatusMessage('⚠️ Smart Paste did not apply any changes');
      }
      setTimeout(() => setStatusMessage(''), 4000);
      if (applyFailed) {
      return { ok: false, message: messages.join(' | ') || 'No character details could be added.' };
      }
      if (resolvedApplyOptions.closeModal !== false) {
        closeSmartPasteModal();
      }
      return { ok: true, message: messages.join(' | ') };
    } catch (error) {
      console.error('[CharacterSheetEditor] Smart paste apply error:', error);
      setStatusMessage(`❌ Error applying changes: ${error.message}`);
      return { ok: false, message: `Error applying changes: ${error.message}` };
    }
  };

  // Save character via UnifiedDataManager (canonical persistence)
  // date_of_change: 2025-10-18 - Replace CustomAssetManager with UDM canonical entity save
  const handleSave = async () => {
    // date_of_change: 2026-04-22 - Anonymous visitors at /character/edit/new must sign in to
    // persist. Route them through Clerk with a redirect_url pointing back to the editor so they
    // return to the sheet after signing in.
    if (!isSignedIn) {
      promptSignIn();
      return;
    }
    if (!customName.trim()) {
      setStatusMessage('❌ Please enter a character name');
      return;
    }

    try {
      setSaving(true);
      const now = new Date().toISOString();
      const id = character?.id && character.id !== 'new_character_draft'
        ? character.id
        : `custom_character_${customName.trim().toLowerCase().replace(/\s+/g, '-')}`;
      console.info('[CharacterSheetEditor] Saving character:', { id, name: customName.trim() });

      // Editor state is already structured. Re-running ingest normalization here can let
      // stale compatibility data override fields the user just changed.
      const editorClasses = normalizeCharacterClassProgression(character);
      if (editorClasses.some((entry) => !String(entry.name || '').trim())) {
        setStatusMessage('❌ Enter a class name for every class progression row');
        setSaving(false);
        return;
      }
      const levelValue = editorClasses.reduce(
        (sum, entry) => sum + Math.max(1, Math.min(20, Number(entry.level) || 1)),
        0
      );
      if (levelValue > 20) {
        setStatusMessage('❌ Total character level cannot exceed 20');
        setSaving(false);
        return;
      }
      const proficiencyBonusValue = typeof character?.proficiencyBonus === 'number'
        ? character.proficiencyBonus
        : (Math.ceil(levelValue / 4) + 1);

      const abilityBlock = {
        strength: character?.abilities?.strength ?? character?.stats?.str ?? 10,
        dexterity: character?.abilities?.dexterity ?? character?.stats?.dex ?? 10,
        constitution: character?.abilities?.constitution ?? character?.stats?.con ?? 10,
        intelligence: character?.abilities?.intelligence ?? character?.stats?.int ?? 10,
        wisdom: character?.abilities?.wisdom ?? character?.stats?.wis ?? 10,
        charisma: character?.abilities?.charisma ?? character?.stats?.cha ?? 10
      };

      const deriveSkills = () => {
        const trainingSnapshot = buildSkillTrainingSnapshot({
          ...character,
          abilities: abilityBlock,
          proficiencyBonus: proficiencyBonusValue
        });
        return trainingSnapshot.skills;
      };

      const deriveSkillsDetailed = () => {
        const trainingSnapshot = buildSkillTrainingSnapshot({
          ...character,
          abilities: abilityBlock,
          proficiencyBonus: proficiencyBonusValue
        });
        return trainingSnapshot.skillsDetailed;
      };

      const deriveSavingThrows = () => {
        if (character?.savingThrows && typeof character.savingThrows === 'object') {
          return { ...character.savingThrows };
        }
        if (character?.saves && typeof character.saves === 'object') {
          return { ...character.saves };
        }
        return {};
      };

      const buildEquipmentInput = () => {
        const equip = character?.equipment || {};
        return {
          ...(equip && typeof equip === 'object' && !Array.isArray(equip) ? equip : {}),
          weapons: Array.isArray(equip.weapons) ? equip.weapons : [],
          armor: Array.isArray(equip.armor) ? equip.armor : [],
          items: Array.isArray(equip.items) ? equip.items : []
        };
      };

      const biographyInput = {
        description: character?.notes ?? character?.biography?.description,
        appearance: character?.appearance ?? character?.biography?.appearance,
        backstory: character?.backstory ?? character?.biography?.backstory,
        personalityTraits: character?.personalityTraits ?? character?.biography?.personalityTraits,
        motivations: character?.motivations ?? character?.biography?.motivations,
        ideals: character?.ideals ?? character?.biography?.ideals,
        bonds: character?.bonds ?? character?.biography?.bonds,
        flaws: character?.flaws ?? character?.biography?.flaws,
        identifyingFeatures: character?.identifyingFeatures ?? character?.biography?.identifyingFeatures
      };
      const normalizedSpellPayload = buildCanonicalCharacterSpells(character || {});
      const normalizedSpellcasting = buildCharacterSpellcastingCompat(character || {});

      const rawCharacterInput = {
        ...character,
        id,
        name: customName.trim(),
        level: levelValue,
        proficiencyBonus: proficiencyBonusValue,
        abilities: abilityBlock,
        hp: character?.hp ?? character?.maxHp ?? 1,
        maxHp: character?.maxHp ?? character?.hp ?? 1,
        ac: character?.ac ?? 10,
        initiative: character?.initiative ?? abilityModNumber(abilityBlock.dexterity),
        speed: character?.speed ?? 30,
        skills: deriveSkills(),
        skillsDetailed: deriveSkillsDetailed(),
        savingThrows: deriveSavingThrows(),
        equipment: buildEquipmentInput(),
        spells: normalizedSpellPayload,
        spellcasting: normalizedSpellcasting,
        biography: biographyInput,
        features: character?.features || [],
        classFeatures: character?.classFeatures || [],
        feats: character?.feats || [],
        speciesTraits: character?.speciesTraits || [],
        classFeaturesText: character?.classFeaturesText || '',
        speciesTraitsText: character?.speciesTraitsText || '',
        featsText: character?.featsText || ''
      };
      const schemaSkills = Object.fromEntries(
        Object.entries(rawCharacterInput.skills || {}).map(([skillName, training]) => {
          if (typeof training === 'string') {
            return [skillName, training];
          }
          if (training?.expertise) return [skillName, 'expert'];
          if (training?.proficient) return [skillName, 'proficient'];
          return [skillName, 'none'];
        })
      );

      const canonical = rawCharacterInput;
      const audit = appendAuditDecision(
        character?._audit,
        'CharacterSheetEditor.handleSave',
        'editor_save'
      );

      const canonicalCharacter = {
        ...canonical,
        id,
        name: customName.trim(),
        level: levelValue,
        class: character?.class ?? canonical?.class,
        race: character?.race ?? character?.species ?? canonical?.race,
        background: character?.background ?? canonical?.background,
        alignment: character?.alignment ?? canonical?.alignment,
        abilities: abilityBlock,
        hp: rawCharacterInput.hp,
        maxHp: rawCharacterInput.maxHp,
        ac: rawCharacterInput.ac,
        initiative: rawCharacterInput.initiative,
        speed: rawCharacterInput.speed,
        proficiencyBonus: proficiencyBonusValue,
        biography: { ...biographyInput },
        equipment: rawCharacterInput.equipment,
        spells: normalizedSpellPayload,
        spellcasting: buildCharacterSpellcastingCompat({
          ...(character || {}),
          spells: normalizedSpellPayload,
          spellcasting: normalizedSpellcasting
        }),
        savingThrows: rawCharacterInput.savingThrows,
        skills: rawCharacterInput.skills,
        features: rawCharacterInput.features,
        classFeatures: rawCharacterInput.classFeatures,
        feats: rawCharacterInput.feats,
        speciesTraits: rawCharacterInput.speciesTraits,
        classFeaturesText: rawCharacterInput.classFeaturesText,
        speciesTraitsText: rawCharacterInput.speciesTraitsText,
        featsText: rawCharacterInput.featsText
      };

      canonicalCharacter._audit = audit;

      let equipmentValidation;
      let spellValidation;
      try {
        const { EquipmentHandler } = await import('../handlers/EquipmentHandler');
        equipmentValidation = await EquipmentHandler.validate(canonicalCharacter.equipment);
        if (equipmentValidation?.normalizedEquipment) {
          canonicalCharacter.equipment = equipmentValidation.normalizedEquipment;
        }
      } catch (equipmentError) {
        console.warn('[CharacterSheetEditor] EquipmentHandler validation failed:', equipmentError);
      }

      try {
        const { SpellHandler } = await import('../handlers/SpellHandler');
        spellValidation = await SpellHandler.validate(canonicalCharacter.spells, canonicalCharacter.level || levelValue);
        if (spellValidation?.normalizedSpells) {
          canonicalCharacter.spells = spellValidation.normalizedSpells;
          canonicalCharacter.spellcasting = buildCharacterSpellcastingCompat({
            ...(character || {}),
            spells: canonicalCharacter.spells,
            spellcasting: canonicalCharacter.spellcasting
          });
        }
      } catch (spellError) {
        console.warn('[CharacterSheetEditor] SpellHandler validation failed:', spellError);
      }

      const validationErrors = [];
      if (equipmentValidation && !equipmentValidation.valid) {
        validationErrors.push(...equipmentValidation.errors);
      }
      if (spellValidation && !spellValidation.valid) {
        validationErrors.push(...spellValidation.errors);
      }

      if (validationErrors.length) {
        setStatusMessage(`❌ Validation failed: ${validationErrors[0]}`);
        setSaving(false);
        return;
      }

      const combatReadiness = assessCharacterCombatReadiness(canonicalCharacter);

      // @date_of_change: 2025-11-20 - Conform to master_character_schema.json (canonical branch)
      const className = (editorClasses[0]?.name || character?.class || canonicalCharacter.class || 'Adventurer');
      // date_of_change: 2025-12-12
      // rule_reference: D&D 5e RAW caps class level at 20; master_character_schema.json enforces that max.
      // Preserve the true (epic/NPC) level on data.level while capping the class level for schema validation.
      const levelActual = Number(levelValue ?? canonicalCharacter.level ?? 1) || 1;
      const schemaSpeed = buildSchemaCompatibleCharacterSpeed(
        canonicalCharacter.speed ?? character?.speed,
        30
      );
      const schemaHitPoints = {
        current: Number(character?.hp ?? canonicalCharacter.hp ?? 1),
        maximum: Number(character?.maxHp ?? canonicalCharacter.maxHp ?? character?.hp ?? 1),
        temporary: Number(character?.tempHp ?? 0)
      };
      const schemaCombatStats = {
        armorClass: Number(character?.ac ?? canonicalCharacter.ac ?? 10),
        initiative: Number(character?.initiative ?? canonicalCharacter.initiative ?? abilityModNumber(abilityBlock.dexterity)),
        speed: schemaSpeed,
        proficiencyBonus: Number(proficiencyBonusValue)
      };
      const editorEquipment = (character?.equipment && typeof character.equipment === 'object' && !Array.isArray(character.equipment))
        ? character.equipment
        : ((canonicalCharacter.equipment && typeof canonicalCharacter.equipment === 'object' && !Array.isArray(canonicalCharacter.equipment))
          ? canonicalCharacter.equipment
          : {});
      const hasEditorActions = Array.isArray(character?.actions?.actions) || Array.isArray(character?.actions);
      const hasEditorBonusActions = Array.isArray(character?.bonusActions) || Array.isArray(character?.actions?.bonusActions);
      const hasEditorReactions = Array.isArray(character?.reactions) || Array.isArray(character?.actions?.reactions);
      const editorActions = Array.isArray(character?.actions?.actions)
        ? character.actions.actions
        : (Array.isArray(character?.actions) ? character.actions : []);
      const editorBonusActions = Array.isArray(character?.bonusActions)
        ? character.bonusActions
        : (Array.isArray(character?.actions?.bonusActions) ? character.actions.bonusActions : []);
      const editorReactions = Array.isArray(character?.reactions)
        ? character.reactions
        : (Array.isArray(character?.actions?.reactions) ? character.actions.reactions : []);
      // date_of_change: 2026-09-06
      // rule_reference: Saving an affected character should permanently remove legacy flattened
      // sheet metadata that older SmartPaste builds stored in action-economy collections.
      const sanitizeActionEntries = (entries) => (
        (Array.isArray(entries) ? entries : [])
          .filter((entry) => !IntelligentContentParser.isCharacterSheetMetadataDump(entry))
      );
      const schemaData = {
        // Preserve the complete editable sheet. The canonical fields below deliberately
        // overwrite schema-sensitive runtime shapes before validation and persistence.
        ...(character || {}),
        name: canonicalCharacter.name || customName.trim() || 'Unknown Character',
        class: String(className),
        level: levelActual,
        background: character?.background || canonicalCharacter.background || undefined,
        race: character?.race || character?.species || canonicalCharacter.race || undefined,
        alignment: character?.alignment ?? canonicalCharacter.alignment ?? undefined,
        abilities: {
          strength: Number(abilityBlock.strength ?? 10),
          dexterity: Number(abilityBlock.dexterity ?? 10),
          constitution: Number(abilityBlock.constitution ?? 10),
          intelligence: Number(abilityBlock.intelligence ?? 10),
          wisdom: Number(abilityBlock.wisdom ?? 10),
          charisma: Number(abilityBlock.charisma ?? 10)
        },
        // Preserve the complete canonical multiclass progression. The former single-row
        // rebuild discarded secondary classes on every save. date_of_change: 2026-08-08.
        classes: editorClasses.map((entry) => ({
          ...entry,
          name: String(entry.name),
          level: Math.max(1, Math.min(20, Number(entry.level) || 1)),
          ...(entry.subclass ? { subclass: String(entry.subclass) } : {}),
          ...(entry.hitDice ? { hitDice: String(entry.hitDice) } : {})
        })),
        // date_of_change: 2025-12-12
        // rule_reference: master_character_schema.json canonical branch uses combatStats for AC/initiative/speed/PB.
        combatStats: schemaCombatStats,
        hitPoints: schemaHitPoints,
        hp: schemaHitPoints.current,
        maxHp: schemaHitPoints.maximum,
        tempHp: schemaHitPoints.temporary,
        ac: schemaCombatStats.armorClass,
        initiative: schemaCombatStats.initiative,
        speed: schemaSpeed,
        proficiencyBonus: schemaCombatStats.proficiencyBonus,
        experiencePoints: Number(character?.experience ?? character?.experiencePoints ?? 0),
        inspiration: Boolean(character?.inspiration),
        passivePerception: Number.isFinite(Number(character?.passivePerception))
          ? Number(character.passivePerception)
          : getPassivePerceptionBase(character),
        skills: schemaSkills,
        skillsDetailed: rawCharacterInput.skillsDetailed,
        proficiencies: sanitizeSchemaProficiencies(character?.proficiencies, character),
        savingThrows: (() => {
          const s = rawCharacterInput.savingThrows || {};
          const pickBool = (value, abilityKey) => {
            if (typeof value === 'boolean') {
              return value;
            }

            if (typeof value === 'number' && Number.isFinite(value)) {
              const baseScore = Number(canonicalCharacter?.abilities?.[abilityKey] ?? abilityBlock?.[abilityKey] ?? 10);
              return value !== abilityModNumber(baseScore);
            }

            return false;
          };
          return {
            strength: pickBool(s.strength, 'strength'),
            dexterity: pickBool(s.dexterity, 'dexterity'),
            constitution: pickBool(s.constitution, 'constitution'),
            intelligence: pickBool(s.intelligence, 'intelligence'),
            wisdom: pickBool(s.wisdom, 'wisdom'),
            charisma: pickBool(s.charisma, 'charisma')
          };
        })(),
        // Optional blocks preserved when present and schema-compatible
        equipment: sanitizeSchemaEquipmentPayload(editorEquipment),
        equipmentDetailed: character?.equipment,
        spellcasting: sanitizeSchemaSpellcastingPayload(canonicalCharacter.spellcasting),
        // date_of_change: 2025-12-12
        // rule_reference: SmartPaste + Editor round-trip — persist applied spells and action economy lists for display/export.
        spells: canonicalCharacter.spells,
        actions: {
          // date_of_change: 2025-12-12
          // rule_reference: master_character_schema.json requires actions.{actions,bonusActions,reactions} arrays.
          actions: sanitizeActionEntries(hasEditorActions
            ? editorActions
            : (Array.isArray(combatReadiness?.projectedCharacter?.actions) ? combatReadiness.projectedCharacter.actions : [])),
          bonusActions: sanitizeActionEntries(hasEditorBonusActions
            ? editorBonusActions
            : (Array.isArray(combatReadiness?.projectedCharacter?.bonusActions) ? combatReadiness.projectedCharacter.bonusActions : [])),
          reactions: sanitizeActionEntries(hasEditorReactions
            ? editorReactions
            : (Array.isArray(combatReadiness?.projectedCharacter?.reactions) ? combatReadiness.projectedCharacter.reactions : []))
        },
        features: sanitizeSchemaFeatureEntries(character?.features ?? canonicalCharacter.features),
        featuresDetailed: Array.isArray(character?.features) ? character.features : undefined,
        classFeatures: Array.isArray(character?.classFeatures) ? character.classFeatures : canonicalCharacter.classFeatures,
        feats: Array.isArray(character?.feats) ? character.feats : canonicalCharacter.feats,
        speciesTraits: Array.isArray(character?.speciesTraits) ? character.speciesTraits : canonicalCharacter.speciesTraits,
        classFeaturesText: character?.classFeaturesText || undefined,
        speciesTraitsText: character?.speciesTraitsText || undefined,
        featsText: character?.featsText || undefined,
        biography: { ...biographyInput },
        appearance: (() => {
          const canonicalAppearance = (canonicalCharacter.appearance && typeof canonicalCharacter.appearance === 'object')
            ? canonicalCharacter.appearance
            : {};
          const editorAppearance = character?.appearance;
          if (editorAppearance && typeof editorAppearance === 'object') {
            return { ...canonicalAppearance, ...editorAppearance };
          }
          return {
            ...canonicalAppearance,
            ...(typeof editorAppearance === 'string' && editorAppearance.trim()
              ? { description: editorAppearance }
              : {})
          };
        })(),
        backstory: canonicalCharacter.biography?.backstory || character?.backstory,
        personalTraits: character?.compat?.originalData?.personalTraits || undefined,
        imageUrl: character?.imageUrl || undefined,
        notes: typeof character?.notes === 'string' ? character.notes : undefined
      };

      // Loaded canonical records use null for many absent optional fields, while the
      // JSON schema expects those properties to be omitted unless they have a value.
      Object.keys(schemaData).forEach((key) => {
        if (schemaData[key] == null) {
          delete schemaData[key];
        }
      });

      const udm = UnifiedDataManager;
      // Normalize token key for meta so resolvers can pick it up regardless of reader path
      // date_of_change: 2025-12-04
      // rule_reference: ArtworkResolver + Dashboard — embed tokenAssetKey/imageUrl into data as well as _meta
      const rawTokenKey = character?.tokenAssetKey || character?.artwork || null;
      const normalizedTokenKey = (typeof rawTokenKey === 'string')
        ? rawTokenKey.replace(/^\/+/, '')
        : null;

      // If we have a canonical upload key, mirror it into the schema data so that
      // backend and Dashboard/ArtworkResolver can resolve artwork even if _meta is dropped.
      if (normalizedTokenKey && normalizedTokenKey.startsWith('uploads/')) {
        // Expose canonical token key on data.* (master_character_schema allows additionalProperties)
        schemaData.tokenAssetKey = normalizedTokenKey;
        // Use the same key as a hint for imageUrl when an explicit URL is not already present
        if (!schemaData.imageUrl) {
          schemaData.imageUrl = normalizedTokenKey;
        }
      }
      const entity = {
        schemaVersion: '1.0.0',
        type: 'character',
        id,
        source: (
          character?.source && typeof character.source === 'object'
            ? { ...character.source }
            : { name: 'Editor', edition: '5e 2024', license: 'Custom' }
        ),
        data: schemaData,
        _meta: {
          createdAt: character?.createdAt || now,
          lastModified: now,
          artwork: rawTokenKey || null,
          combatReadiness: buildCombatReadinessMeta(combatReadiness, 'editor_save', now),
          ...(character?._generationMeta ? { generation: { ...character._generationMeta } } : {}),
          ...(normalizedTokenKey && normalizedTokenKey.startsWith('uploads/') ? {
            tokenAssetKey: normalizedTokenKey,
            tokenAsset: { key: normalizedTokenKey }
          } : {}),
          audit
        }
      };

      await udm.saveEntity(entity, true);
      console.info('[CharacterSheetEditor] Character save completed:', { id, name: schemaData.name });

      writeCharacterCategory(id, characterCategory);

      const warningMessages = Array.from(new Set([
        ...(equipmentValidation?.warnings || []),
        ...(spellValidation?.warnings || []),
        ...combatReadiness.dataQualityWarnings
      ]));
      setStatusMessage(
        warningMessages.length
          ? `⚠️ Character saved with warnings: ${warningMessages.join(' ')}`
          : '✅ Character saved to UnifiedDataManager!'
      );

      setTimeout(() => {
        navigate('/');
      }, 1500);
    } catch (error) {
      console.error('Failed to save character:', error);
      setStatusMessage(`❌ Error saving character: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Keep hooks on the stable top-level render path; loading and empty-state
  // branches return later, but derived sheet view models still need to be
  // initialized unconditionally to preserve hook ordering.
  const actionEconomyViewModel = useMemo(
    () => buildCharacterActionEconomyViewModel(character),
    [character]
  );

  const smartPasteModal = showSmartPasteModal ? (
    <div
      ref={smartPasteOverlayRef}
      className="smart-paste-modal-overlay"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) closeSmartPasteModal();
      }}
    >
      <div
        ref={smartPasteDialogRef}
        className="smart-paste-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="smart-paste-modal-title"
        tabIndex="-1"
      >
        <div className="smart-paste-modal-header">
          <h3 id="smart-paste-modal-title">✨ Smart Paste</h3>
          <button
            ref={smartPasteCloseButtonRef}
            type="button"
            className="smart-paste-modal-close"
            onClick={closeSmartPasteModal}
            aria-label="Close Smart Paste"
          >
            ×
          </button>
        </div>
        <div className="smart-paste-modal-body">
          <SmartPasteInterface
            onContentParsed={handleSmartPasteContent}
            onApplyChanges={handleSmartPasteApply}
            context={{
              entityType: 'character',
              characterId: character?.id || 'unsaved-character',
              currentData: character
            }}
            placeholder="Paste spells, equipment, features, or full character blocks..."
          />
        </div>
        {/* Footer intentionally minimal; SmartPasteInterface provides Apply/Cancel controls */}
      </div>
    </div>
  ) : null;

  if (loading) {
    return (
      <div className="character-sheet-editor loading" role="status" aria-live="polite" aria-busy="true">
        <div className="character-sheet-loading-panel">
          <div className="character-sheet-loading-orb" aria-hidden="true"></div>
          <p>Loading character sheet...</p>
        </div>
      </div>
    );
  }

  if (!character) {
    return (
      <div className="character-sheet-editor character-sheet-editor--character">
        {smartPasteModal}
        {/* Anonymous-mode banner */}
        {isAnonymous && (
          <div className="anonymous-banner">
            Build your character free. Sign in when you want to save, upload image, or use advanced tools.
          </div>
        )}
        {/* Header with action buttons */}
        <div className="sheet-header">
          <h1 className="sheet-title">D&D Character Sheet Editor</h1>
          <p className="sheet-subtitle">Create and customize your adventurer</p>

          <div className="sheet-action-buttons">
            <button
              ref={smartPasteTriggerRef}
              onClick={handleSmartPasteButton}
              className={`sheet-btn ${showSmartPasteModal ? 'sheet-btn-primary' : 'sheet-btn-secondary'}`}
              title={isAnonymous ? 'Sign in to use Smart Paste.' : 'Use Smart Paste'}
            >
              ✨ Smart Paste
            </button>
            <button
              onClick={handleDashboardButton}
              className="sheet-btn sheet-btn-secondary"
            >
              ← Dashboard
            </button>
          </div>
        </div>

        {/* Status Message */}
        {statusMessage && (
          <div className="toast-container">
            <div className={`toast ${statusMessage.includes('✅') ? 'success' : 'error'}`}>
              {statusMessage.replace(/^✅\s*/, '')}
            </div>
          </div>
        )}

        {/* No Data State */}
        <div className="character-sheet-page no-data">
          <div className="no-data-content">
            <h2>No Character Data Available</h2>
            <p>
              The requested character template could not be loaded. You can either:
            </p>
            <ul>
              <li>Go back to the Dashboard and try a different character</li>
              <li>Use the Smart Paste feature to import character data</li>
              <li>Create a new character from scratch</li>
            </ul>

            {/* Smart Paste Section */}
            <div className="smart-paste-section">
              <h3 className="smart-paste-title">✨ Smart Content Paste</h3>
              <p className="smart-paste-description">
                Paste D&D character content and it will be automatically parsed and structured. Use the Smart Paste button to open the importer.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const characterDisplayName = customName || character?.name || 'Character';
  const characterSummaryLine = [
    character?.class,
    character?.subclass,
    character?.species || character?.race,
    character?.background
  ].filter(Boolean).join(' • ');
  const isPresentationMode = viewOnly || previewMode;
  const hitDiceState = resolveCharacterHitDice(character);
  const armorClassState = resolveCharacterArmorClass(character);
  const defenseAndSenseState = resolveCharacterDefensesAndSenses(character);
  const referencePresentation = isPresentationMode
    ? buildCharacterReferencePresentation(character)
    : {
        classFeatures: Array.isArray(character?.classFeatures) ? character.classFeatures : [],
        features: Array.isArray(character?.features) ? character.features : [],
        speciesTraits: Array.isArray(character?.speciesTraits) ? character.speciesTraits : [],
        feats: Array.isArray(character?.feats) ? character.feats : []
      };
  const characterChoiceState = resolveCharacterChoiceSummary(character);
  const canEditStructuredReference = !viewOnly && !previewMode;
  const suggestedClassFeatures = getSuggestedClassFeatures(character);
  const hasStructuredClassFeatures = referencePresentation.classFeatures.length > 0;
  const hasStructuredFeatures = referencePresentation.features.length > 0;
  const hasStructuredSpeciesTraits = referencePresentation.speciesTraits.length > 0;
  const hasStructuredFeats = referencePresentation.feats.length > 0;
  const referenceClassEntries = [
    ...(Array.isArray(character?.classFeatures) ? character.classFeatures : []),
    ...(Array.isArray(character?.features) ? character.features : [])
  ];
  const hasClassFeatureNotes = hasDistinctStructuredNotes(referenceClassEntries, character?.classFeaturesText || '');
  const hasSpeciesTraitNotes = hasDistinctStructuredNotes(
    Array.isArray(character?.speciesTraits) ? character.speciesTraits : [],
    character?.speciesTraitsText || character?.racialTraits || ''
  );
  const hasFeatNotes = hasDistinctStructuredNotes(
    Array.isArray(character?.feats) ? character.feats : [],
    character?.featsText || ''
  );
  const hasReferenceContent = hasStructuredClassFeatures
    || hasStructuredFeatures
    || hasStructuredSpeciesTraits
    || hasStructuredFeats
    || hasClassFeatureNotes
    || hasSpeciesTraitNotes
    || hasFeatNotes;
  const hasActionEconomyContent = Boolean(actionEconomyViewModel?.hasContent);
  const characterArtUrl = resolveCharacterArtUrl(character);
  const hasCharacterArt = Boolean(characterArtUrl);
  const resolvedSpellData = resolveCharacterSpellData(character);
  const declaredSpellRecord = isSpellRecord(character?.spells) ? character.spells : null;
  const declaredSpellSlots = declaredSpellRecord?.slots ?? declaredSpellRecord?.spellSlots;
  const hasDeclaredSpellSlots = Boolean(declaredSpellRecord) && (
    Object.prototype.hasOwnProperty.call(declaredSpellRecord, 'slots')
    || Object.prototype.hasOwnProperty.call(declaredSpellRecord, 'spellSlots')
  );
  const editableSpellData = declaredSpellRecord && Array.isArray(declaredSpellRecord.entries)
    ? {
        ...resolvedSpellData,
        entries: declaredSpellRecord.entries,
        slots: hasDeclaredSpellSlots
          ? normalizeSpellSlots(declaredSpellSlots)
          : resolvedSpellData.slots
      }
    : resolvedSpellData;
  const spellSlotSource = resolvedSpellData.slots;
  const hasSpellSlotsContent = Object.values(spellSlotSource).some((slot) => {
    if (!slot || typeof slot !== 'object') return false;
    return String(slot.total ?? '').trim() !== '' || String(slot.used ?? '').trim() !== '';
  });
  const hasSpellcastingStats = Boolean(
    resolvedSpellData.ability
    || resolvedSpellData.saveDC != null
    || resolvedSpellData.attackBonus != null
  );
  const hasSpellcastingIdentity = characterHasSpellcastingIdentity(character);
  const weaponRows = buildCharacterWeaponTableEntries(character);
  const proficiencySummary = buildCharacterProficiencySummary(character);
  const wealthSummary = buildCharacterWealthSummary(character);
  const hasWealthContent = Object.values(wealthSummary.coins || {}).some((value) => value > 0)
    || (wealthSummary.valuables || []).length > 0;
  const hasSpellbookContent = resolvedSpellData.entries.length > 0
    || hasSpellSlotsContent
    || hasSpellcastingStats
    || hasSpellcastingIdentity;
  // Presentation modes show the complete sheet; empty sections remain compact or collapsed.
  const shouldRenderReferencePage = isPresentationMode
    || hasReferenceContent
    || hasActionEconomyContent
    || canEditStructuredReference;
  const shouldRenderSpellbookPage = isPresentationMode || hasSpellbookContent;

  return (
    <div className={`character-sheet-editor character-sheet-editor--character${previewMode || viewOnly ? ' character-sheet-editor--presentation' : ''}`}>
      {smartPasteModal}
      {/* Anonymous-mode banner */}
      {isAnonymous && (
        <div className="anonymous-banner">
          Build your character free. Sign in when you want to save, upload image, or use advanced tools.
        </div>
      )}
      {/* Header with action buttons */}
      <div className="sheet-header">
        <h1 className="sheet-title">D&D Character Sheet Editor</h1>
        <p className="sheet-subtitle">Create and customize your adventurer</p>

        <div className="sheet-action-buttons">
          <button
            onClick={handlePreviewButton}
            className={`sheet-btn ${previewMode ? 'sheet-btn-primary' : 'sheet-btn-secondary'}`}
            title={previewMode ? 'Return to editing' : 'Preview this sheet'}
          >
            {previewMode ? '✏️ Edit' : '👁️ Preview'}
          </button>
          <button
            onClick={handlePrintButton}
            className="sheet-btn sheet-btn-secondary"
            title="Print this sheet"
          >
            🖨️ Print Sheet
          </button>
          {viewOnly ? (
            <button
              onClick={handleEditCharacterButton}
              className="sheet-btn sheet-btn-primary"
              title={isAnonymous ? 'Sign in to edit this character.' : 'Edit this character'}
            >
              ✏️ Edit Character
            </button>
          ) : (
            <>
              {canGenerateStarterCharacter && (
                <button
                  onClick={handleGenerateCharacterButton}
                  className="sheet-btn sheet-btn-primary"
                  title="Generate a playable starter character"
                >
                  🎲 Generate Character
                </button>
              )}
              <button
                onClick={handleSave}
                disabled={saving}
                className="sheet-btn sheet-btn-success"
                title={isAnonymous ? 'Sign in to save this character to your Virtual Combat Simulator library.' : 'Save this character'}
              >
                {saving ? '⏳ Saving...' : '💾 Save Character'}
              </button>
              <button
                ref={smartPasteTriggerRef}
                onClick={handleSmartPasteButton}
                className={`sheet-btn ${showSmartPasteModal ? 'sheet-btn-primary' : 'sheet-btn-secondary'}`}
                title={isAnonymous ? 'Sign in to use Smart Paste.' : 'Use Smart Paste'}
              >
                ✨ Smart Paste
              </button>
            </>
          )}
          <div className="sheet-menu-select" aria-label="Character roster category">
            <label className="sheet-menu-select-label" htmlFor="character-roster-select">Roster</label>
            <select
              id="character-roster-select"
              className="sheet-menu-select-input"
              value={characterCategory}
              onChange={(e) => setCharacterCategory(resolveCharacterCategory(e.target.value))}
              disabled={viewOnly || previewMode}
            >
              <option value="hero">Hero</option>
              <option value="npc">NPC</option>
            </select>
          </div>
          <button
            onClick={handleDashboardButton}
            className="sheet-btn sheet-btn-secondary"
          >
            ← Dashboard
          </button>
        </div>
      </div>

      {/* Status Message */}
      {statusMessage && (
        <div className="toast-container">
          <div className={`toast ${statusMessage.includes('✅') ? 'success' : 'error'}`}>
            {statusMessage.replace(/^✅\s*/, '')}
          </div>
        </div>
      )}

      {/* Character Sheet - single UI for both view and edit. Read-only via fieldset when mode=view or previewMode */}
      <div className={`character-sheet-document sheet-orientation-portrait${previewMode || viewOnly ? ' character-sheet-document--presentation' : ''}`}>
        <fieldset disabled={viewOnly || previewMode}>
          <>
            <div className="character-sheet-page character-sheet-page--summary sheet-orientation-portrait">
            {/* @date_of_change: 2025-10-08 - Compact D&D 2024 Header Layout */}
            {/* D&D 2024 Header - Compact single-row design */}
            <div className="dnd-2024-header">
              <div className="header-left-fields">
                <div className="header-field header-field-full">
                  <label>Character Name</label>
                  <input
                    type="text"
                    value={customName}
                    onChange={(e) => setCustomName(e.target.value)}
                    placeholder="Enter character name"
                  />
                </div>

                <div className="header-row-2">
                  <div className="header-field">
                    <label>Background</label>
                    <input
                      type="text"
                      value={character.background || ''}
                      onChange={(e) => updateCharacter('background', e.target.value)}
                      placeholder="Soldier"
                    />
                  </div>
                  <div className="header-field">
                    <label>Class</label>
                    <input
                      type="text"
                      value={character.class || ''}
                      onChange={(e) => handleCharacterClassChange(e.target.value)}
                      placeholder="Fighter"
                    />
                  </div>
                </div>
                <div className="header-row-3">
                  <div className="header-field">
                    <label>Species</label>
                    <input
                      type="text"
                      value={character.race || character.species || ''}
                      onChange={(e) => updateCharacter('race', e.target.value)}
                      placeholder="Human"
                    />
                  </div>
                  <div className="header-field">
                    <label>Subclass</label>
                    <input
                      type="text"
                      value={character.subclass || ''}
                      onChange={(e) => handleCharacterSubclassChange(e.target.value)}
                      placeholder="e.g. Rune Knight"
                    />
                  </div>
                </div>
              </div>

              <div className="header-right-circles">
                <div className="header-circle level-circle">
                  <div className="circle-label">Level</div>
                  <input
                    type="number"
                    value={character.level || 1}
                    readOnly
                    title="Calculated from class progression."
                    aria-label="Total character level"
                  />
                </div>
                <div className="header-circle xp-circle">
                  <div className="circle-label">XP</div>
                  <input
                    type="number"
                    value={character.experience || character.xp || 0}
                    onChange={(e) => updateCharacter('experience', parseInt(e.target.value) || 0)}
                    min="0"
                  />
                </div>
              </div>

              {/* (moved) Portrait upload now lives under ability scores */}
            </div>

            {!isPresentationMode && (
              <CharacterClassProgressionEditor
                character={character}
                onChange={updateClassProgression}
              />
            )}

            {/* @date_of_change: 2025-10-10 - Restructured combat row: AC with shield, HP with hit dice and death saves */}
            {/* Top Combat Stats Row - AC (with Shield), HP (with Hit Dice and Death Saves) */}
            <div className="combat-top-row">
              {/* AC Box with Shield integrated */}
              <div className="combat-box ac-box">
                <div className="combat-box-label">Armor Class</div>
                <input
                  type="number"
                  min="1"
                  value={armorClassState.value}
                  onChange={(e) => updateCharacter('ac', parseInt(e.target.value) || 10)}
                  className="combat-box-value"
                />
                <div className="combat-box-source">{armorClassState.source}</div>
                <div className="shield-icon">🛡️</div>
                <input
                  type="checkbox"
                  checked={character.hasShield || false}
                  onChange={(e) => updateShieldState(e.target.checked)}
                  className="shield-checkbox"
                />
              </div>

              {/* HP Box with Hit Dice and Death Saves integrated */}
              <div className="combat-box hp-large-box">
                <div className="combat-box-label">Hit Points</div>

                {/* Unified 5-column grid: labels row then inputs row */}
                <div className="hp-hd-grid">
                  <div className="hp-hd-header">
                    <div>Cur</div>
                    <div>Max</div>
                    <div>Tmp</div>
                    <div>HD Max</div>
                    <div>HD Spent</div>
                  </div>
                  <div className="hp-hd-fields">
                    <div className="hp-hd-cell">
                      <input
                        type="number"
                        value={character.hp || character.maxHp || 1}
                        onChange={(e) => updateCharacter('hp', parseInt(e.target.value) || 1)}
                      />
                    </div>
                    <div className="hp-hd-cell">
                      <input
                        type="number"
                        value={character.maxHp || character.hp || 1}
                        onChange={(e) => updateCharacter('maxHp', parseInt(e.target.value) || 1)}
                      />
                    </div>
                    <div className="hp-hd-cell">
                      <input
                        type="number"
                        value={character.tempHp || 0}
                        onChange={(e) => updateCharacter('tempHp', parseInt(e.target.value) || 0)}
                      />
                    </div>
                    <div className="hp-hd-cell">
                      <input
                        type="text"
                        value={hitDiceState.formula}
                        readOnly
                        title="Calculated from class levels and hit dice."
                        aria-label="Maximum hit dice"
                        placeholder="1d8"
                      />
                    </div>
                    <div className="hp-hd-cell">
                      <input
                        type="number"
                        value={character.hitDiceSpent || 0}
                        onChange={(e) => updateCharacter('hitDiceSpent', parseInt(e.target.value) || 0)}
                        min="0"
                        max={hitDiceState.totalCount || undefined}
                      />
                    </div>
                  </div>
                </div>

                <div className="hit-dice-state-row">
                  <span><strong>{hitDiceState.remaining}</strong> of {hitDiceState.totalCount} Hit Dice available on a Short Rest</span>
                  {!isPresentationMode && hitDiceState.totalCount > 0 && (
                    <span className="hit-dice-state-actions">
                      <button
                        type="button"
                        onClick={() => updateCharacter('hitDiceSpent', Math.min(hitDiceState.totalCount, hitDiceState.spent + 1))}
                        disabled={hitDiceState.remaining < 1}
                      >Spend 1</button>
                      <button
                        type="button"
                        onClick={() => updateCharacter('hitDiceSpent', Math.max(0, hitDiceState.spent - 1))}
                        disabled={hitDiceState.spent < 1}
                      >Recover 1</button>
                    </span>
                  )}
                </div>

                {/* Death Saves below on a single row */}
                <div className="death-saves-single-row">
                  <span className="death-saves-inline-label">Death Saves:</span>
                  <span className="death-saves-inline-group">
                    <label>Successes</label>
                    {[0, 1, 2].map(i => (
                      <input
                        key={`success-${i}`}
                        type="checkbox"
                        checked={(character.deathSaves?.successes || 0) > i}
                        onChange={() => {
                          const current = character.deathSaves?.successes || 0;
                          updateCharacter('deathSaves', {
                            ...character.deathSaves,
                            successes: current > i ? i : i + 1
                          });
                        }}
                      />
                    ))}
                  </span>
                  <span className="death-saves-inline-group">
                    <label>Failures</label>
                    {[0, 1, 2].map(i => (
                      <input
                        key={`failure-${i}`}
                        type="checkbox"
                        checked={(character.deathSaves?.failures || 0) > i}
                        onChange={() => {
                          const current = character.deathSaves?.failures || 0;
                          updateCharacter('deathSaves', {
                            ...character.deathSaves,
                            failures: current > i ? i : i + 1
                          });
                        }}
                      />
                    ))}
                  </span>
                </div>

                {(defenseAndSenseState.defenses.length > 0 || defenseAndSenseState.senses.length > 0) && (
                  <div className="character-defense-sense-strip">
                    {defenseAndSenseState.defenses.length > 0 && (
                      <span><strong>Defenses:</strong> {defenseAndSenseState.defenses.join('; ')}</span>
                    )}
                    {defenseAndSenseState.senses.length > 0 && (
                      <span><strong>Senses:</strong> {defenseAndSenseState.senses.join('; ')}</span>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* date_of_change: 2026-02-26 — Redesigned: full-width flow, compact 3x2 abilities, full-width weapons */}
            <div className="dnd-2024-main-section">

              {/* Unified Stats Row: PB, Inspiration, Initiative, Speed, Size, Passive Perception */}
              <div className="secondary-stats-row">
                <div className="stat-box-secondary">
                  <div className="stat-label-secondary">Prof Bonus</div>
                  <input
                    type="number"
                    value={character.proficiencyBonus || Math.floor((Math.max(1, character.level || 1) - 1) / 4) + 2}
                    readOnly
                    title="Calculated from total character level."
                    aria-label="Proficiency bonus"
                    className="stat-value-secondary"
                  />
                </div>
                <div className="stat-box-secondary">
                  <div className="stat-label-secondary">Inspiration</div>
                  <input
                    type="checkbox"
                    checked={character.inspiration || false}
                    onChange={(e) => updateCharacter('inspiration', e.target.checked)}
                    className="inspiration-checkbox"
                    style={{ width: 18, height: 18 }}
                  />
                </div>
                <div className="stat-box-secondary">
                  <div className="stat-label-secondary">Initiative</div>
                  <input
                    type="number"
                    value={character.initiative || calculateModifier(character.abilities?.dexterity || 10).replace('+', '')}
                    onChange={(e) => updateCharacter('initiative', parseInt(e.target.value) || 0)}
                    className="stat-value-secondary"
                  />
                </div>
                <div className="stat-box-secondary">
                  <div className="stat-label-secondary">Speed</div>
                  <input
                    type="number"
                    min="0"
                    value={typeof character.speed === 'object' ? (character.speed?.walk ?? 30) : (character.speed || 30)}
                    onChange={(e) => updateCharacter(
                      'speed',
                      typeof character.speed === 'object'
                        ? { ...character.speed, walk: parseInt(e.target.value, 10) || 30 }
                        : parseInt(e.target.value, 10) || 30
                    )}
                    className="stat-value-secondary"
                  />
                </div>
                <div className="stat-box-secondary">
                  <div className="stat-label-secondary">Size</div>
                  <select
                    value={character.size || 'Medium'}
                    onChange={(e) => updateCharacter('size', e.target.value)}
                    className="stat-value-secondary"
                  >
                    <option>Tiny</option>
                    <option>Small</option>
                    <option>Medium</option>
                    <option>Large</option>
                    <option>Huge</option>
                    <option>Gargantuan</option>
                  </select>
                </div>
                <div className="stat-box-secondary">
                  <div className="stat-label-secondary">Passive Perception</div>
                  <input
                    type="number"
                    value={Number.isFinite(Number(character.passivePerception))
                      ? Number(character.passivePerception)
                      : getPassivePerceptionBase(character)}
                    onChange={(e) => updateCharacter('passivePerception', parseInt(e.target.value) || 10)}
                    className="stat-value-secondary"
                  />
                </div>
              </div>

              <div className="weapons-table-section character-sheet-full-width-section">
                <div className="section-header-dnd">Equipped Weapons</div>
                <div className="table-scroll">
                  <table className="weapons-table">
                    <thead>
                      <tr>
                        <th className="weapon-col-name">Name</th>
                        <th className="weapon-col-attack">Atk Bonus / DC</th>
                        <th className="weapon-col-damage">Damage & Type</th>
                        <th className="weapon-col-range">Range</th>
                        <th className="weapon-col-proficiency">Training</th>
                        <th className="weapon-col-mastery">Mastery</th>
                        <th className="weapon-col-notes">Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        // date_of_change: 2026-03-20
                        // rule_reference: This table is only for weapons carried ready for immediate use.
                        // Spellcasting, spells, cantrips, and action-economy entries surface in their dedicated sections.
                        const rows = [];
                        weaponRows.forEach((p) => {
                          const atkCell = (p.attackBonus != null)
                            ? formatSignedNumber(p.attackBonus)
                            : '';
                          rows.push(
                            <tr key={p.key}>
                              <td className="weapon-col-name"><input type="text" defaultValue={p.name} onChange={(event) => updateWeaponField(p, 'name', event.target.value)} /></td>
                              <td className="weapon-col-attack"><input type="text" defaultValue={atkCell} placeholder="+5" onChange={(event) => updateWeaponField(p, 'attackBonus', event.target.value)} /></td>
                              <td className="weapon-col-damage"><input type="text" defaultValue={p.damageText} placeholder="1d8 slashing" onChange={(event) => updateWeaponField(p, 'damageText', event.target.value)} /></td>
                              <td className="weapon-col-range"><input type="text" defaultValue={p.rangeText || ''} placeholder="100/400 ft." onChange={(event) => updateWeaponField(p, 'range', event.target.value)} /></td>
                              <td className="weapon-col-proficiency"><span title="Calculated from weapon and proficiency records.">{p.proficiencyText}</span></td>
                              <td className="weapon-col-mastery"><input type="text" defaultValue={p.masteryText || ''} placeholder="Vex" onChange={(event) => updateWeaponField(p, 'mastery', event.target.value)} /></td>
                              <td className="weapon-col-notes"><input type="text" defaultValue={p.notes || ''} placeholder="On hit..." onChange={(event) => updateWeaponField(p, 'notes', event.target.value)} /></td>
                            </tr>
                          );
                        });

                        const minimumWeaponRows = (!viewOnly && !previewMode)
                          ? Math.max(rows.length + 1, 4)
                          : rows.length;
                        for (let i = rows.length; i < minimumWeaponRows; i++) {
                          const draftRow = {
                            sourceCollection: 'equipment.weapons',
                            draftIndex: (Array.isArray(character?.equipment?.weapons) ? character.equipment.weapons.length : 0) + (i - rows.length)
                          };
                          rows.push(
                            <tr key={`empty-${i}`}>
                              <td className="weapon-col-name"><input type="text" placeholder="Equipped weapon" onChange={(event) => updateWeaponField(draftRow, 'name', event.target.value)} /></td>
                              <td className="weapon-col-attack"><input type="text" placeholder="+5" onChange={(event) => updateWeaponField(draftRow, 'attackBonus', event.target.value)} /></td>
                              <td className="weapon-col-damage"><input type="text" placeholder="1d8 slashing" onChange={(event) => updateWeaponField(draftRow, 'damageText', event.target.value)} /></td>
                              <td className="weapon-col-range"><input type="text" placeholder="100/400 ft." onChange={(event) => updateWeaponField(draftRow, 'range', event.target.value)} /></td>
                              <td className="weapon-col-proficiency"><span title="Calculated after the weapon is recorded.">-</span></td>
                              <td className="weapon-col-mastery"><input type="text" placeholder="Vex" onChange={(event) => updateWeaponField(draftRow, 'mastery', event.target.value)} /></td>
                              <td className="weapon-col-notes"><input type="text" placeholder="Notes" onChange={(event) => updateWeaponField(draftRow, 'notes', event.target.value)} /></td>
                            </tr>
                          );
                        }
                        return rows;
                      })()}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="character-sheet-summary-layout">
                <div className="character-sheet-summary-sidebar">
              {/* Compact 3x2 Ability Scores with Skills */}
              <div className="dnd-left-column">
                {/* Ability Scores with Skills */}
                <div className="abilities-with-skills">
                  {[
                    { name: 'strength', skills: ['Athletics'] },
                    { name: 'dexterity', skills: ['Acrobatics', 'Sleight of Hand', 'Stealth'] },
                    { name: 'constitution', skills: [] },
                    { name: 'intelligence', skills: ['Arcana', 'History', 'Investigation', 'Nature', 'Religion'] },
                    { name: 'wisdom', skills: ['Animal Handling', 'Insight', 'Medicine', 'Perception', 'Survival'] },
                    { name: 'charisma', skills: ['Deception', 'Intimidation', 'Performance', 'Persuasion'] }
                  ].map(({ name, skills }) => (
                    <div key={name} className="ability-with-skills-box">
                      <div className="ability-score-header">
                        <div className="ability-name-main">{name.toUpperCase().slice(0, 3)}</div>
                        <input
                          type="number"
                          min="1"
                          max="30"
                          value={character.abilities?.[name] || 10}
                          onChange={(e) => updateAbilityScore(name, parseInt(e.target.value, 10) || 10)}
                          className="ability-score-main"
                        />
                        <div className="ability-modifier-main" title="Calculated from the ability score.">
                          {calculateModifier(character.abilities?.[name] || 10)}
                        </div>
                      </div>

                      {/* Saving Throw */}
                      <div className="skill-row">
                        {(() => {
                          const rawSaveBonus = character?.savingThrows?.[name];
                          const numericSaveBonus = (
                            typeof rawSaveBonus === 'number' && Number.isFinite(rawSaveBonus)
                              ? rawSaveBonus
                              : (typeof rawSaveBonus === 'string' && rawSaveBonus.trim() !== '' && Number.isFinite(Number(rawSaveBonus))
                                ? Number(rawSaveBonus)
                                : null)
                          );
                          const abilityScore = Number(character?.abilities?.[name] ?? 10);
                          const baseModifier = abilityModNumber(abilityScore);
                          const levelValue = Number(character?.level);
                          const fallbackLevel = Number.isFinite(levelValue) && levelValue > 0 ? levelValue : 1;
                          const proficiencyBonus = (
                            typeof character?.proficiencyBonus === 'number' && Number.isFinite(character.proficiencyBonus)
                              ? character.proficiencyBonus
                              : (Math.floor((fallbackLevel - 1) / 4) + 2)
                          );
                          const hasSaveProficiency = typeof rawSaveBonus === 'boolean'
                            ? rawSaveBonus
                            : (numericSaveBonus !== null ? numericSaveBonus !== baseModifier : false);
                          const displayedSaveBonus = baseModifier + (hasSaveProficiency ? proficiencyBonus : 0);
                          const saveDisplay = formatSignedNumber(displayedSaveBonus);
                          return (
                            <>
                              <input
                                type="checkbox"
                                className="skill-proficiency"
                                checked={hasSaveProficiency}
                                onChange={(e) => {
                                  const existingSavingThrows = (
                                    character?.savingThrows && typeof character.savingThrows === 'object'
                                      ? character.savingThrows
                                      : {}
                                  );
                                  updateCharacter('savingThrows', {
                                    ...existingSavingThrows,
                                    [name]: e.target.checked
                                  });
                                }}
                              />
                              <span className="skill-modifier" title="Calculated from ability and save proficiency.">{saveDisplay}</span>
                            </>
                          );
                        })()}
                        <span className="skill-name">Saving Throw</span>
                      </div>

                      {/* Skills */}
                      {skills.map(skill => (
                        <div key={skill} className="skill-row">
                          {(() => {
                            const skillState = resolveSkillState(character, skill, name);
                            const display = formatSignedNumber(skillState.value);

                            return (
                              <>
                                <div className="skill-training-controls">
                                  <input
                                    type="checkbox"
                                    className="skill-proficiency"
                                    checked={skillState.proficient}
                                    aria-label={`${skill} proficiency`}
                                    title={`${skill} proficiency`}
                                    onChange={(event) => updateSkillTraining(skill, name, {
                                      proficient: event.target.checked,
                                      expertise: event.target.checked ? skillState.expertise : false
                                    })}
                                  />
                                  <input
                                    type="checkbox"
                                    className="skill-proficiency skill-proficiency--expertise"
                                    checked={skillState.expertise}
                                    disabled={!skillState.proficient}
                                    aria-label={`${skill} expertise`}
                                    title={skillState.proficient ? `Toggle ${skill} expertise.` : 'Choose proficiency first.'}
                                    onChange={(event) => updateSkillTraining(skill, name, {
                                      proficient: skillState.proficient || event.target.checked,
                                      expertise: event.target.checked
                                    })}
                                  />
                                </div>
                                <span className="skill-modifier" title="Calculated from ability, proficiency, and expertise.">{display}</span>
                              </>
                            );
                          })()}
                          <span className="skill-name">{skill}</span>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>

                {/* Artwork - compact inline */}
                {(hasCharacterArt || (!viewOnly && !previewMode)) && (
                  <div className="artwork-panel">
                    <div className="artwork-preview">
                      {/* eslint-disable-next-line jsx-a11y/alt-text */}
                      <img
                        src={characterArtUrl || resolvePlaceholderUrl('token-default')}
                        className="artwork-preview-image"
                      />
                    </div>
                    {(!viewOnly && !previewMode) && (
                      <div className="artwork-actions">
                        <button
                          type="button"
                          className="sheet-btn sheet-btn-secondary"
                          onClick={handleUploadButton}
                          disabled={atAssetCap}
                          title={atAssetCap ? `Free vault full. Upgrade to ${PREMIUM_PLAN_NAME}.` : 'Upload character image'}
                        >
                          {atAssetCap ? 'Vault Full' : 'Upload Image'}
                        </button>
                        <button type="button" className="sheet-btn sheet-btn-secondary" onClick={handleChooseExistingButton}>Choose Existing</button>
                        <input ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/jpg,image/svg+xml" onChange={onFileChosen} style={{ display: 'none' }} disabled={atAssetCap} />
                      </div>
                    )}
                    {(!viewOnly && !previewMode) && (
                      <div className="artwork-tier-note">
                        {isPremium
                          ? `${PREMIUM_PLAN_NAME}: keep stocking your art vault.`
                          : `Free art vault: ${assetUsage.totalCount}/${assetCap} uploads used. ${atAssetCap ? 'Clear a slot or ' : ''}`}
                        {!isPremium && atAssetCap && (
                          <button type="button" className="artwork-tier-link" onClick={() => navigate(UPGRADE_ROUTE)}>
                            upgrade
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
                </div>

                <div className="character-sheet-summary-main">
                {/* @date_of_change: 2026-03-17
                    rule_reference: Product print layout — keep core proficiencies on the play sheet paired with inventory/equipment reference content. */}
                <div className="content-section-static character-sheet-summary-support-section">
                  <div className="section-header-dnd">Equipment Training & Other Proficiencies</div>
                  <div className="proficiencies-grid-extended">
                    <div>
                      <strong>Armor Training</strong>
                      <div className="armor-checkboxes">
                        <label><input type="checkbox" checked={proficiencySummary.armor.light} onChange={(event) => updateProficiencyLabels('armor', proficiencySummary.armor.labels, (label) => label.includes('light'), 'Light Armor', event.target.checked)} /> Light</label>
                        <label><input type="checkbox" checked={proficiencySummary.armor.medium} onChange={(event) => updateProficiencyLabels('armor', proficiencySummary.armor.labels, (label) => label.includes('medium'), 'Medium Armor', event.target.checked)} /> Medium</label>
                        <label><input type="checkbox" checked={proficiencySummary.armor.heavy} onChange={(event) => updateProficiencyLabels('armor', proficiencySummary.armor.labels, (label) => label.includes('heavy'), 'Heavy Armor', event.target.checked)} /> Heavy</label>
                        <label><input type="checkbox" checked={proficiencySummary.armor.shields} onChange={(event) => updateProficiencyLabels('armor', proficiencySummary.armor.labels, (label) => label.includes('shield'), 'Shields', event.target.checked)} /> Shields</label>
                      </div>
                    </div>
                    <div>
                      <strong>Weapon Proficiencies</strong>
                      <div className="proficiency-list">
                        <label className="proficiency-item">
                          <input type="checkbox" checked={proficiencySummary.weapons.simple} onChange={(event) => updateProficiencyLabels('weapons', proficiencySummary.weapons.labels, (label) => label.includes('simple'), 'Simple Weapons', event.target.checked)} />
                          <span>Simple Weapons</span>
                        </label>
                        <label className="proficiency-item">
                          <input type="checkbox" checked={proficiencySummary.weapons.martial} onChange={(event) => updateProficiencyLabels('weapons', proficiencySummary.weapons.labels, (label) => label.includes('martial'), 'Martial Weapons', event.target.checked)} />
                          <span>Martial Weapons</span>
                        </label>
                        <label className="proficiency-specific-editor">
                          <span>Other weapon training</span>
                          <input
                            type="text"
                            value={proficiencySummary.weapons.specific.join(', ')}
                            placeholder="Rapier, hand crossbow"
                            onChange={(event) => {
                              const broadTraining = proficiencySummary.weapons.labels.filter((label) => /simple|martial/i.test(label));
                              const specificTraining = event.target.value.split(',').map((label) => label.trim()).filter(Boolean);
                              updateProficiencyLabels('weapons', [...broadTraining, ...specificTraining], () => false, '', true);
                            }}
                          />
                        </label>
                      </div>
                    </div>
                    <div>
                      <strong>Tool Proficiencies</strong>
                      <EditableTextList
                        label="Tools"
                        items={proficiencySummary.tools}
                        placeholder="Thieves' Tools"
                        onChange={(values) => updateStringList('toolProficiencies', values)}
                      />
                    </div>
                    <div>
                      <strong>Languages</strong>
                      <EditableTextList
                        label="Languages"
                        items={proficiencySummary.languages}
                        placeholder="Common"
                        onChange={(values) => updateStringList('languages', values)}
                      />
                    </div>
                  </div>
                </div>

                </div>
              </div>

              <CharacterInventorySection
                character={character}
                setCharacter={setCharacter}
                isPresentationMode={isPresentationMode}
              />

              <div className="wealth-section character-sheet-full-width-section">
                <div className="section-header-dnd">Wealth & Valuables</div>
                <div className="wealth-grid">
                  {[
                    ['cp', 'Copper'],
                    ['sp', 'Silver'],
                    ['ep', 'Electrum'],
                    ['gp', 'Gold'],
                    ['pp', 'Platinum']
                  ].map(([key, label]) => (
                    <label key={key} className="wealth-coin-card">
                      <span>{label}</span>
                      <input
                        type="number"
                        min="0"
                        value={wealthSummary.coins?.[key] ?? 0}
                        placeholder="0"
                        onChange={(event) => updateCurrencyValue(key, event.target.value)}
                      />
                    </label>
                  ))}
                </div>
                {hasWealthContent && wealthSummary.valuables.length > 0 && (
                  <div className="wealth-valuables-block">
                    <strong>Gems & Other Valuables</strong>
                    <ul className="wealth-valuables-list">
                      {wealthSummary.valuables.map((valuable) => (
                        <li key={valuable.key}>
                          {valuable.quantity > 1 ? `${valuable.quantity}x ` : ''}
                          {valuable.name}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
            </div>

                {/* date_of_change: 2026-03-12 — Preserve separate classFeatures and generic features buckets in the editor */}
                {shouldRenderReferencePage && (
                  <div className="character-sheet-page character-sheet-page--reference sheet-orientation-portrait">
                    <div className="character-sheet-page-kicker">
                      <div className="character-sheet-page-title">{characterDisplayName}</div>
                      <div className="character-sheet-page-meta">{characterSummaryLine || 'Reference & Traits'}</div>
                    </div>
                    <div className="character-sheet-reference-grid">
                {(characterChoiceState.selections.length > 0 || characterChoiceState.unresolved.length > 0) && (
                  <div className="content-section-static character-choice-summary">
                    <div className="section-header-dnd">Character Choices</div>
                    {characterChoiceState.selections.length > 0 && (
                      <p><strong>Recorded:</strong> {characterChoiceState.selections.join(' | ')}</p>
                    )}
                    {characterChoiceState.unresolved.length > 0 && (
                      <p><strong>Selection required:</strong> {characterChoiceState.unresolved.join('; ')}</p>
                    )}
                  </div>
                )}
                {(hasActionEconomyContent || isPresentationMode || canEditStructuredReference) && (
                  <div className="content-section-static character-action-economy-section">
                    {isPresentationMode && <div className="section-header-dnd">Action Economy</div>}
                    {isPresentationMode ? (
                      <CharacterActionEconomyPanel
                        viewModel={actionEconomyViewModel}
                        showTitle={false}
                        presentation
                      />
                    ) : (
                      <CharacterActionEconomyEditor
                        character={character}
                        setCharacter={setCharacter}
                        viewModel={actionEconomyViewModel}
                      />
                    )}
                  </div>
                )}

                {(hasStructuredClassFeatures || canEditStructuredReference || isPresentationMode) && (
                  <div className="content-section-static">
                    <div className="section-header-dnd-toolbar">
                      <div className="section-header-dnd">Class Features</div>
                      {canEditStructuredReference && (
                        <div className="section-header-dnd-actions">
                          {suggestedClassFeatures.length > 0 && (
                            <button
                              type="button"
                              className="section-inline-action"
                              onClick={applySuggestedClassFeatures}
                            >
                              Use Suggested Class Features
                            </button>
                          )}
                          <button
                            type="button"
                            className="section-inline-action"
                            onClick={() => addStructuredEntry('classFeatures', 'New Class Feature')}
                          >
                            Add Class Feature
                          </button>
                        </div>
                      )}
                    </div>
                    {hasStructuredClassFeatures ? (
                      <ul className="bullet-list">
                        {referencePresentation.classFeatures.map((f, idx) => (
                          <li key={`class-feature-arr-${idx}`} className="structured-entry-row">
                            <div className="structured-entry-row-main">
                              <strong>{f.name || 'Feature'}{(f.description || f.text) ? ':' : ''}</strong>{' '}
                              {(f.description || f.text)
                                ? (f.description || f.text)
                                : (canEditStructuredReference ? <em className="missing-desc">Add a description</em> : null)}
                            </div>
                            {canEditStructuredReference && (
                              <button
                                type="button"
                                className="structured-entry-edit-btn"
                                onClick={() => startStructuredEntryEdit('classFeatures', idx)}
                                title="Edit entry"
                                aria-label={`Edit ${f.name || 'class feature'}`}
                              >
                                ✎
                              </button>
                            )}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <div className="content-empty-state">
                        {character?.class
                          ? 'No class features have been added yet.'
                          : 'Set a class to unlock suggested class features, or add your own entries here.'}
                      </div>
                    )}
                  </div>
                )}

                {(hasStructuredFeatures || canEditStructuredReference || isPresentationMode) && (
                  <div className="content-section-static">
                    <div className="section-header-dnd-toolbar">
                      <div className="section-header-dnd">Features</div>
                      {canEditStructuredReference && (
                        <div className="section-header-dnd-actions">
                          <button
                            type="button"
                            className="section-inline-action"
                            onClick={() => addStructuredEntry('features', 'New Feature')}
                          >
                            Add Feature
                          </button>
                        </div>
                      )}
                    </div>
                    {hasStructuredFeatures ? (
                      <ul className="bullet-list">
                        {referencePresentation.features.map((f, idx) => (
                          <li key={`feature-arr-${idx}`} className="structured-entry-row">
                            <div className="structured-entry-row-main">
                              <strong>{f.name || 'Feature'}{(f.description || f.text) ? ':' : ''}</strong>{' '}
                              {(f.description || f.text)
                                ? (f.description || f.text)
                                : (canEditStructuredReference ? <em className="missing-desc">Add a description</em> : null)}
                            </div>
                            {canEditStructuredReference && (
                              <button
                                type="button"
                                className="structured-entry-edit-btn"
                                onClick={() => startStructuredEntryEdit('features', idx)}
                                title="Edit entry"
                                aria-label={`Edit ${f.name || 'feature'}`}
                              >
                                ✎
                              </button>
                            )}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <div className="content-empty-state">
                        Add any custom or campaign-specific features here.
                      </div>
                    )}
                  </div>
                )}

                {/* Species Traits (structured) */}
                {(hasStructuredSpeciesTraits || canEditStructuredReference || isPresentationMode) && (
                  <div className="content-section-static">
                    <div className="section-header-dnd-toolbar">
                      <div className="section-header-dnd">Species Traits</div>
                      {canEditStructuredReference && (
                        <div className="section-header-dnd-actions">
                          <button
                            type="button"
                            className="section-inline-action"
                            onClick={() => addStructuredEntry('speciesTraits', 'New Species Trait')}
                          >
                            Add Species Trait
                          </button>
                        </div>
                      )}
                    </div>
                    {hasStructuredSpeciesTraits ? (
                      <ul className="bullet-list">
                        {referencePresentation.speciesTraits.map((t, idx) => (
                          <li key={`species-trait-${idx}`} className="structured-entry-row">
                            <div className="structured-entry-row-main">
                              <strong>{t.name || 'Trait'}{(t.description || t.text) ? ':' : ''}</strong>{' '}
                              {(t.description || t.text)
                                ? (t.description || t.text)
                                : (canEditStructuredReference ? <em className="missing-desc">Add a description</em> : null)}
                            </div>
                            {canEditStructuredReference && (
                              <button
                                type="button"
                                className="structured-entry-edit-btn"
                                onClick={() => startStructuredEntryEdit('speciesTraits', idx)}
                                title="Edit entry"
                                aria-label={`Edit ${t.name || 'species trait'}`}
                              >
                                ✎
                              </button>
                            )}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <div className="content-empty-state">
                        No species traits have been added yet.
                      </div>
                    )}
                  </div>
                )}

                {/* Feats (structured list) */}
                {(hasStructuredFeats || canEditStructuredReference || isPresentationMode) && (
                  <div className="content-section-static">
                    <div className="section-header-dnd-toolbar">
                      <div className="section-header-dnd">Feats</div>
                      {canEditStructuredReference && (
                        <div className="section-header-dnd-actions">
                          <button
                            type="button"
                            className="section-inline-action"
                            onClick={() => addStructuredEntry('feats', 'New Feat')}
                          >
                            Add Feat
                          </button>
                        </div>
                      )}
                    </div>
                    {hasStructuredFeats ? (
                      <ul className="bullet-list">
                        {referencePresentation.feats.map((ft, idx) => (
                          <li key={`feat-list-${idx}`} className="structured-entry-row">
                            <div className="structured-entry-row-main">
                              <strong>{ft.name || 'Feat'}{(ft.description || ft.text) ? ':' : ''}</strong>{' '}
                              {(ft.description || ft.text)
                                ? (ft.description || ft.text)
                                : (canEditStructuredReference ? <em className="missing-desc">Add a description</em> : null)}
                            </div>
                            {canEditStructuredReference && (
                              <button
                                type="button"
                                className="structured-entry-edit-btn"
                                onClick={() => startStructuredEntryEdit('feats', idx)}
                                title="Edit entry"
                                aria-label={`Edit ${ft.name || 'feat'}`}
                              >
                                ✎
                              </button>
                            )}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <div className="content-empty-state">
                        Add feats that the player or DM has chosen for this character.
                      </div>
                    )}
                  </div>
                )}

                {/* date_of_change: 2026-03-06
                    rule_reference: Preserve structured data, but allow pleasant editing without mounting many textareas.
                    Extended to support actions, bonusActions, and reactions editing. */}
                {structuredEntryEditor && (() => {
                  const field = structuredEntryEditor.field;
                  const index = structuredEntryEditor.index;
                  // date_of_change: 2026-03-06 - Handle nested actions array (character.actions.actions vs character.actions)
                  let entry = null;
                  if (field === 'actions') {
                    const actionsList = Array.isArray(character?.actions?.actions)
                      ? character.actions.actions
                      : (Array.isArray(character?.actions) ? character.actions : []);
                    entry = actionsList[index];
                  } else if (field === 'bonusActions') {
                    const bonusList = Array.isArray(character?.bonusActions)
                      ? character.bonusActions
                      : (Array.isArray(character?.actions?.bonusActions) ? character.actions.bonusActions : []);
                    entry = bonusList[index];
                  } else if (field === 'reactions') {
                    const reactionsList = Array.isArray(character?.reactions)
                      ? character.reactions
                      : (Array.isArray(character?.actions?.reactions) ? character.actions.reactions : []);
                    entry = reactionsList[index];
                  } else {
                    entry = Array.isArray(character?.[field]) ? character[field][index] : null;
                  }
                  if (!entry) return null;
                  const titleMap = {
                    features: 'Feature',
                    classFeatures: 'Class Feature',
                    speciesTraits: 'Species Trait',
                    feats: 'Feat',
                    actions: 'Action',
                    bonusActions: 'Bonus Action',
                    reactions: 'Reaction'
                  };
                  const label = titleMap[field] || field;
                  const startingName = entry?.name || '';
                  const startingDescription = entry?.description || entry?.text || '';
                  const startingSource = typeof entry?.source === 'string'
                    ? entry.source
                    : (entry?.source?.name || entry?.source?.title || '');
                  const startingMaxUses = entry?.uses?.max ?? '';
                  const startingCurrentUses = entry?.uses?.current ?? '';
                  const startingResetOn = entry?.uses?.resetOn || '';

                  return (
                    <div className="structured-entry-editor-overlay" role="dialog" aria-modal="true" onClick={cancelStructuredEntryEdit}>
                      <div className="structured-entry-editor" onClick={(e) => e.stopPropagation()}>
                        <div className="structured-entry-editor-header">
                          <strong>Edit {label}</strong>
                        </div>
                        <div className="structured-entry-editor-body" key={`${field}-${index}-${structuredEntryEditor.openedAt}`}>
                          <label className="structured-entry-editor-label">
                            Name
                            <input ref={structuredEntryNameRef} type="text" defaultValue={startingName} />
                          </label>
                          <label className="structured-entry-editor-label">
                            Description
                            <textarea ref={structuredEntryDescriptionRef} defaultValue={startingDescription} rows={6} />
                          </label>
                          <div className="structured-entry-editor-grid">
                            <label className="structured-entry-editor-label structured-entry-editor-label--wide">
                              Source
                              <input
                                ref={structuredEntrySourceRef}
                                type="text"
                                defaultValue={startingSource}
                                placeholder="e.g. Rogue 5"
                              />
                            </label>
                            <label className="structured-entry-editor-label">
                              Max Uses
                              <input ref={structuredEntryMaxUsesRef} type="number" min="0" defaultValue={startingMaxUses} />
                            </label>
                            <label className="structured-entry-editor-label">
                              Uses Left
                              <input ref={structuredEntryCurrentUsesRef} type="number" min="0" defaultValue={startingCurrentUses} />
                            </label>
                            <label className="structured-entry-editor-label">
                              Recharge
                              <select ref={structuredEntryResetRef} defaultValue={startingResetOn}>
                                <option value="">None</option>
                                <option value="short rest">Short Rest</option>
                                <option value="long rest">Long Rest</option>
                              </select>
                            </label>
                          </div>
                        </div>
                        <div className="structured-entry-editor-actions">
                          <button type="button" className="sheet-btn sheet-btn-danger" onClick={removeStructuredEntry}>Remove</button>
                          <span className="structured-entry-editor-action-spacer" />
                          <button type="button" className="sheet-btn sheet-btn-secondary" onClick={cancelStructuredEntryEdit}>Cancel</button>
                          <button type="button" className="sheet-btn sheet-btn-primary" onClick={saveStructuredEntryEdit}>Save</button>
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {hasClassFeatureNotes && (
                  <div className="content-section-static">
                    <div className="section-header-dnd">Class Features (Notes)</div>
                    <div className="content-text character-sheet-note-display">
                      {character.classFeaturesText}
                    </div>
                  </div>
                )}

                {hasSpeciesTraitNotes && (
                  <div className="content-section-static">
                    <div className="section-header-dnd">Species Traits (Notes)</div>
                    <div className="content-text character-sheet-note-display">
                      {character.speciesTraitsText || character.racialTraits}
                    </div>
                  </div>
                )}

                {hasFeatNotes && (
                  <div className="content-section-static">
                    <div className="section-header-dnd">Feats (Notes)</div>
                    <div className="content-text character-sheet-note-display">
                      {character.featsText}
                    </div>
                  </div>
                )}
                    </div>
                  </div>
                )}

              {/* Spells Section */}
              {/* @date_of_change: 2025-10-10 - Compact combined spellcasting box */}
              {shouldRenderSpellbookPage && (
                <div className="character-sheet-page character-sheet-page--spellbook sheet-orientation-portrait">
                  <div className="character-sheet-page-kicker">
                    <div className="character-sheet-page-title">{characterDisplayName}</div>
                    <div className="character-sheet-page-meta">Spells & Casting Reference</div>
              </div>
              <div className="spells-section-wrapper">
                {isPresentationMode ? (
                  <CharacterSpellbookPanel subject={character} spellData={resolvedSpellData} />
                ) : (
                  <CharacterSpellbookEditor
                    subject={character}
                    spellData={editableSpellData}
                    onAbilityChange={updateSpellcastingAbility}
                    onAdjustmentChange={updateSpellcastingAdjustment}
                    onSlotChange={updateSpellSlot}
                    onSpellChange={updateSpellEntry}
                    onAddSpell={addSpellEntry}
                    onRemoveSpell={removeSpellEntry}
                  />
                )}
              </div>
            </div>
              )}

              <div className="character-sheet-page character-sheet-page--details sheet-orientation-portrait">
                <div className="character-sheet-digital-details">

            {(!viewOnly && !previewMode) && (
              <div className="content-section-static character-sheet-digital-editor-panel">
                <div className="section-header-dnd">Reference Page Notes</div>
                <div className="character-sheet-reference-editor-grid">
                  <div className="character-sheet-reference-editor-field">
                    <label>Class Features (Notes)</label>
                    <textarea
                      value={character.classFeaturesText || ''}
                      onChange={(e) => updateCharacter('classFeaturesText', e.target.value)}
                      rows={6}
                      className="long-text-input"
                      placeholder="Enter class features..."
                    />
                  </div>
                  <div className="character-sheet-reference-editor-field">
                    <label>Species Traits (Notes)</label>
                    <textarea
                      value={character.speciesTraitsText || character.racialTraits || ''}
                      onChange={(e) => updateCharacter('speciesTraitsText', e.target.value)}
                      rows={6}
                      className="long-text-input"
                      placeholder="Enter species traits..."
                    />
                  </div>
                  <div className="character-sheet-reference-editor-field">
                    <label>Feats (Notes)</label>
                    <textarea
                      value={character.featsText || ''}
                      onChange={(e) => updateCharacter('featsText', e.target.value)}
                      rows={6}
                      className="long-text-input"
                      placeholder="Enter feats..."
                    />
                  </div>
                </div>
              </div>
            )}

            {/* @date_of_change: 2025-10-10 - Comprehensive Character Details Sections */}
            {/* Character Background & Personality */}
            <div className="detail-section-collapsible">
              <div className="detail-section-header" onClick={() => toggleSection('background')}>
                <span className="detail-section-icon">{expandedSections.background ? '▼' : '▶'}</span>
                <span className="detail-section-title">CHARACTER BACKGROUND & PERSONALITY</span>
              </div>
              {expandedSections.background && (
                <div className="detail-section-content">
                  <div className="detail-grid-2col">
                    <div className="detail-field">
                      <label>PERSONALITY TRAITS</label>
                      <textarea
                        value={
                          character.personalityTraits 
                          || character.biography?.personalityTraits 
                          || character.compat?.originalData?.personalTraits?.personality 
                          || ''
                        }
                        onChange={(e) => updateCharacter('personalityTraits', e.target.value)}
                        placeholder="Describe your character's personality..."
                        rows={3}
                      />
                    </div>
                    <div className="detail-field">
                      <label>MOTIVATIONS</label>
                      <textarea
                        value={character.motivations || character.biography?.motivations || ''}
                        onChange={(e) => updateCharacter('motivations', e.target.value)}
                        placeholder="What drives your character..."
                        rows={3}
                      />
                    </div>
                    <div className="detail-field">
                      <label>IDEALS</label>
                      <textarea
                        value={
                          character.ideals 
                          || character.biography?.ideals 
                          || character.compat?.originalData?.personalTraits?.ideals 
                          || ''
                        }
                        onChange={(e) => updateCharacter('ideals', e.target.value)}
                        placeholder="Your character's core beliefs..."
                        rows={3}
                      />
                    </div>
                    <div className="detail-field">
                      <label>FLAWS</label>
                      <textarea
                        value={
                          character.flaws 
                          || character.biography?.flaws 
                          || character.compat?.originalData?.personalTraits?.flaws 
                          || ''
                        }
                        onChange={(e) => updateCharacter('flaws', e.target.value)}
                        placeholder="Your character's weaknesses..."
                        rows={3}
                      />
                    </div>
                    <div className="detail-field">
                      <label>BACKSTORY</label>
                      <textarea
                        value={character.backstory || character.biography?.backstory || ''}
                        onChange={(e) => updateCharacter('backstory', e.target.value)}
                        placeholder="Record your character's history, origins, and major life events..."
                        rows={4}
                      />
                    </div>
                    <div className="detail-field">
                      <label>BONDS</label>
                      <textarea
                        value={
                          character.bonds 
                          || character.biography?.bonds 
                          || character.compat?.originalData?.personalTraits?.bonds 
                          || ''
                        }
                        onChange={(e) => updateCharacter('bonds', e.target.value)}
                        placeholder="What ties your character to people, places, or ideals..."
                        rows={3}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Physical Description */}
            <div className="detail-section-collapsible">
              <div className="detail-section-header" onClick={() => toggleSection('physical')}>
                <span className="detail-section-icon">{expandedSections.physical ? '▼' : '▶'}</span>
                <span className="detail-section-title">PHYSICAL DESCRIPTION</span>
              </div>
              {expandedSections.physical && (
                <div className="detail-section-content">
                  <div className="detail-grid-2col">
                    <div className="detail-field">
                      <label>APPEARANCE</label>
                      <textarea
                        value={character.appearance || character.biography?.appearance || character.biography?.description || ''}
                        onChange={(e) => updateCharacter('appearance', e.target.value)}
                        placeholder="Describe your character's physical appearance..."
                        rows={4}
                      />
                    </div>
                    <div className="detail-field">
                      <label>IDENTIFYING FEATURES</label>
                      <textarea
                        value={character.identifyingFeatures || character.biography?.identifyingFeatures || ''}
                        onChange={(e) => updateCharacter('identifyingFeatures', e.target.value)}
                        placeholder="Scars, tattoos, unique markings (visible or hidden)..."
                        rows={4}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Storage & Additional Equipment */}
            <div className="detail-section-collapsible">
              <div className="detail-section-header" onClick={() => toggleSection('storage')}>
                <span className="detail-section-icon">{expandedSections.storage ? '▼' : '▶'}</span>
                <span className="detail-section-title">STORAGE & ADDITIONAL EQUIPMENT</span>
              </div>
              {expandedSections.storage && (
                <div className="detail-section-content">
                  <div className="detail-field">
                    <label>EQUIPMENT IN STORAGE (Not carried)</label>
                    <textarea
                      value={character.storageEquipment || character.storage || ''}
                      onChange={(e) => updateCharacter('storageEquipment', e.target.value)}
                      placeholder="Items owned but not currently carried (home, bank, storage facility, etc.)..."
                      rows={6}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Relationships */}
            <div className="detail-section-collapsible">
              <div className="detail-section-header" onClick={() => toggleSection('relationships')}>
                <span className="detail-section-icon">{expandedSections.relationships ? '▼' : '▶'}</span>
                <span className="detail-section-title">RELATIONSHIPS</span>
              </div>
              {expandedSections.relationships && (
                <div className="detail-section-content">
                  <div className="detail-grid-3col">
                    <div className="detail-field">
                      <label>ALLIES & FRIENDS</label>
                      <textarea
                        value={character.allies || (Array.isArray(character.relationships) ? character.relationships.join('\n') : '')}
                        onChange={(e) => updateCharacter('allies', e.target.value)}
                        placeholder="List allies, friends, contacts..."
                        rows={5}
                      />
                    </div>
                    <div className="detail-field">
                      <label>FOES & ENEMIES</label>
                      <textarea
                        value={character.foes || ''}
                        onChange={(e) => updateCharacter('foes', e.target.value)}
                        placeholder="List enemies, rivals, antagonists..."
                        rows={5}
                      />
                    </div>
                    <div className="detail-field">
                      <label>FAMILY</label>
                      <textarea
                        value={character.family || ''}
                        onChange={(e) => updateCharacter('family', e.target.value)}
                        placeholder="Family members, relations..."
                        rows={5}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Political & Social Standing */}
            <div className="detail-section-collapsible">
              <div className="detail-section-header" onClick={() => toggleSection('political')}>
                <span className="detail-section-icon">{expandedSections.political ? '▼' : '▶'}</span>
                <span className="detail-section-title">POLITICAL & SOCIAL STANDING</span>
              </div>
              {expandedSections.political && (
                <div className="detail-section-content">
                  <div className="detail-grid-2col">
                    <div className="detail-field">
                      <label>DEEDS & TITLES</label>
                      <textarea
                        value={character.deedsAndTitles || ''}
                        onChange={(e) => updateCharacter('deedsAndTitles', e.target.value)}
                        placeholder="Titles, honors, notable deeds..."
                        rows={3}
                      />
                    </div>
                    <div className="detail-field">
                      <label>POLITICAL INFLUENCE</label>
                      <textarea
                        value={character.politicalInfluence || character.political || ''}
                        onChange={(e) => updateCharacter('politicalInfluence', e.target.value)}
                        placeholder="Political power, connections, standing..."
                        rows={3}
                      />
                    </div>
                    <div className="detail-field">
                      <label>POLITICAL KNOWLEDGE</label>
                      <textarea
                        value={character.politicalKnowledge || character.political || ''}
                        onChange={(e) => updateCharacter('politicalKnowledge', e.target.value)}
                        placeholder="Knowledge of political situations, factions, intrigue..."
                        rows={3}
                      />
                    </div>
                    <div className="detail-field">
                      <label>POLITICAL PREFERENCES</label>
                      <textarea
                        value={character.politicalPreferences || character.political || ''}
                        onChange={(e) => updateCharacter('politicalPreferences', e.target.value)}
                        placeholder="Political leanings, supported factions..."
                        rows={3}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* date_of_change: 2026-02-12 */}
            {/* rule_reference: D&D 5e (2024) - Legendary/Mythic/Lair actions and Notes are first-class display fields */}
            {/* Legendary, Mythic, Lair Actions */}
            {(isPresentationMode || character.legendaryActions || character.mythicActions || character.lairActions || character.regionalEffects) && (
              <div className="detail-section-collapsible">
                <div className="detail-section-header" onClick={() => toggleSection('legendaryActions')}>
                  <span className="detail-section-icon">{expandedSections.legendaryActions ? '▼' : '▶'}</span>
                  <span className="detail-section-title">LEGENDARY / MYTHIC / LAIR ACTIONS</span>
                </div>
                {expandedSections.legendaryActions && (
                  <div className="detail-section-content">
                    {character.legendaryActions && (
                      <div className="detail-field" style={{ marginBottom: '1rem' }}>
                        <label>LEGENDARY ACTIONS ({character.legendaryActions.count || 3}/round)</label>
                        {character.legendaryActions.summary && (
                          <p style={{ fontStyle: 'italic', marginBottom: '0.5rem' }}>{character.legendaryActions.summary}</p>
                        )}
                        {Array.isArray(character.legendaryActions.options) && character.legendaryActions.options.length > 0 && (
                          <ul className="bullet-list">
                            {character.legendaryActions.options.map((opt, idx) => (
                              <li key={'la-' + idx}>
                                <strong>{opt.name}</strong> (Cost {opt.cost || 1}): {opt.description}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )}
                    {character.mythicActions && (
                      <div className="detail-field" style={{ marginBottom: '1rem' }}>
                        <label>MYTHIC ACTIONS</label>
                        {character.mythicActions.trigger && (
                          <p style={{ fontStyle: 'italic', marginBottom: '0.5rem' }}>Trigger: {character.mythicActions.trigger}</p>
                        )}
                        {character.mythicActions.summary && (
                          <p style={{ marginBottom: '0.5rem' }}>{character.mythicActions.summary}</p>
                        )}
                        {Array.isArray(character.mythicActions.options) && character.mythicActions.options.length > 0 && (
                          <ul className="bullet-list">
                            {character.mythicActions.options.map((opt, idx) => (
                              <li key={'ma-' + idx}>
                                <strong>{opt.name}</strong>: {opt.description}{opt.uses ? ` (${opt.uses})` : ''}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )}
                    {Array.isArray(character.lairActions) && character.lairActions.length > 0 && (
                      <div className="detail-field" style={{ marginBottom: '1rem' }}>
                        <label>LAIR ACTIONS (Initiative 20)</label>
                        <ul className="bullet-list">
                          {character.lairActions.map((la, idx) => (
                            <li key={'lair-' + idx}>
                              <strong>{la.action || la.name}</strong>{la.description ? ': ' + la.description : ''}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {Array.isArray(character.regionalEffects) && character.regionalEffects.length > 0 && (
                      <div className="detail-field">
                        <label>REGIONAL EFFECTS</label>
                        <ul className="bullet-list">
                          {character.regionalEffects.map((re, idx) => (
                            <li key={'re-' + idx}>
                              <strong>{re.effect || re.name}</strong>{re.radius ? ' (' + re.radius + ')' : ''}{re.description ? ': ' + re.description : ''}{re.duration ? ' [' + re.duration + ']' : ''}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {!character.legendaryActions
                      && !character.mythicActions
                      && (!Array.isArray(character.lairActions) || character.lairActions.length === 0)
                      && (!Array.isArray(character.regionalEffects) || character.regionalEffects.length === 0) && (
                        <div className="content-empty-state">
                          No legendary, mythic, lair, or regional actions recorded.
                        </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Minions */}
            {(isPresentationMode || (Array.isArray(character.minions) && character.minions.length > 0)) && (
              <div className="detail-section-collapsible">
                <div className="detail-section-header" onClick={() => toggleSection('minions')}>
                  <span className="detail-section-icon">{expandedSections.minions ? '▼' : '▶'}</span>
                  <span className="detail-section-title">MINIONS & FOLLOWERS</span>
                </div>
                {expandedSections.minions && (
                  <div className="detail-section-content">
                    {Array.isArray(character.minions) && character.minions.length > 0 ? (
                      <ul className="bullet-list">
                        {character.minions.map((m, idx) => (
                          <li key={'minion-' + idx}>
                            <strong>{m.name}</strong>{m.type ? ' (' + m.type + ')' : ''}{m.quantity > 1 ? ' x' + m.quantity : ''}{m.loyalty ? ' — Loyalty: ' + m.loyalty : ''}{m.notes ? ' — ' + m.notes : ''}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <div className="content-empty-state">No minions or followers recorded.</div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Comprehensive Notes Section */}
            <div className="detail-section-collapsible">
              <div className="detail-section-header" onClick={() => toggleSection('notes')}>
                <span className="detail-section-icon">{expandedSections.notes ? '▼' : '▶'}</span>
                <span className="detail-section-title">NOTES & MISCELLANEOUS DATA</span>
              </div>
              {expandedSections.notes && (
                <div className="detail-section-content">
                  <div className="detail-field">
                    <label>NOTES</label>
                    <textarea
                      value={character.notes || ''}
                      onChange={(e) => updateCharacter('notes', e.target.value)}
                      placeholder="Miscellaneous notes, GM data, parsed metadata, and any information that doesn't fit elsewhere..."
                      rows={12}
                      style={{ fontFamily: 'monospace', fontSize: '0.85rem', whiteSpace: 'pre-wrap' }}
                    />
                  </div>
                  {character.challengeRating && (
                    <div className="detail-field" style={{ marginTop: '0.5rem' }}>
                      <label>CHALLENGE RATING</label>
                      <span>{character.challengeRating}{character.xp ? ' (' + Number(character.xp).toLocaleString() + ' XP)' : ''}</span>
                    </div>
                  )}
                  {character.title && (
                    <div className="detail-field" style={{ marginTop: '0.5rem' }}>
                      <label>TITLE</label>
                      <span>{character.title}</span>
                    </div>
                  )}
                  {Array.isArray(character.aliases) && character.aliases.length > 0 && (
                    <div className="detail-field" style={{ marginTop: '0.5rem' }}>
                      <label>ALIASES</label>
                      <span>{character.aliases.join(', ')}</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* @date_of_change: 2025-10-09 - Removed dropdown sections, content now displayed statically below weapons table */}
                  </div>
                </div>
          </>
        </fieldset>

        {/* Artwork image browser modal */}
        {showImageBrowser && (
          <div className="modal-overlay">
            <div className="modal-content">
              <ImageBrowser
                filterType="token"
                onSelectImage={handleImageSelected}
                onClose={() => setShowImageBrowser(false)}
              />
            </div>
          </div>
        )}
      </div>
      <CharacterPrintPortal model={printSnapshot} />
    </div>
  );
};

export default CharacterSheetEditor;
